// Need to import the appropriate rendering profiles because of a breaking change in v18.0.0 (https://github.com/Kitware/vtk-js/releases/tag/v18.0.0)
import 'vtk.js/Sources/Rendering/Profiles/Volume';

import vtkGenericRenderWindow from 'vtk.js/Sources/Rendering/Misc/GenericRenderWindow';

// Force the loading of HttpDataAccessHelper to support gzip decompression
import 'vtk.js/Sources/IO/Core/DataAccessHelper/HttpDataAccessHelper';

import vtkImageMapper from 'vtk.js/Sources/Rendering/Core/ImageMapper';
import vtkImageReslice from "vtk.js/Sources/Imaging/Core/ImageReslice";
import vtkPlane from 'vtk.js/Sources/Common/DataModel/Plane';

//// DEBUG
//import vtkConeSource from 'vtk.js/Sources/Filters/Sources/ConeSource';
//import vtkMapper from 'vtk.js/Sources/Rendering/Core/Mapper';
//import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';

import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';
import vtkMapper from 'vtk.js/Sources/Rendering/Core/Mapper';

import React, { useEffect, useRef } from 'react';
import Konva from 'konva';

import { logDebug, debugCallback, isNullOrUndefined, getDUName, CprViewMode, CprControlPointMode, clamp } from './Common';
import { CenterlineDistanceBranchSelection, CenterlineBranchSelection, convertUnifiedBranchIndexToMasterIndex, tryConvertMasterToDistanceBranchIndex } from '../CenterlineMeasurement/CenterlinePlaneShared';
import { buildCenterlinesPolyData } from './CenterlineUtils';

// ============================================================================
// Visual Style Constants
// ============================================================================

// Default centerline colors
const STRETCHED_CENTERLINE_COLOR    = [0.1, 1.0, 0.1];
const STRAIGHTENED_CENTERLINE_COLOR = [1.0, 1.0, 1.0];

// Stretched-view spline widget handle styling.
const STRETCHED_HANDLE_SCALAR              = 0.5; // neutral
const STRETCHED_HANDLE_SCALAR_TOP_VIEW     = 0.9; // orthogonal highlight (top view, currently blue)
const STRETCHED_HANDLE_SCALAR_BOTTOM_VIEW  = 0.0; // orthogonal highlight (bottom view, currently red)
const STRETCHED_HANDLE_SIZE         = 10;  // pixels
const STRETCHED_HANDLE_SIZE_ACTIVE  = 16;  // pixels

// Straightened-view centerline overlay
const STRAIGHTENED_OVERLAY_LINE_WIDTH = 1;
const STRAIGHTENED_OVERLAY_POINT_SIZE = 7;

// Measurement guide lines
const MEASUREMENT_LINE_WIDTH = 1;
const MEASUREMENT_LINE_VIEWPORT_RATIO = 0.9; // covers 90% of viewport width
const MEASUREMENT_LINE_DRAG_TOLERANCE_CSS_PX = 8; // Vertical grab distance (CSS px) from a horizontal guide line
const CONTROL_POINT_PICK_TOLERANCE_WORLD_SQ = 25.0; // World-space squared hit tolerance (~5 mm²) for spline handles
const MEASUREMENT_LINE_COLORS = {
    start: [0.0, 0.0, 1.0], // proximal / first  landmark
    end:   [1.0, 0.0, 0.0], // distal  / second landmark
};

// Measurement length arrow
const MEASUREMENT_ARROW_COLOR           = [1.0, 0.9, 0.0]; // yellow
const MEASUREMENT_ARROW_LINE_WIDTH      = 2;
const MEASUREMENT_ARROW_VIEWPORT_RATIO  = 0.75; // positioned at 75% across viewport
const MEASUREMENT_ARROW_HEAD_LENGTH_PX  = 18;   // display pixels, converted to world space at render time
const MEASUREMENT_ARROW_HEAD_WIDTH_PX   = 10;   // display pixels, converted to world space at render time
const MEASUREMENT_ARROW_HEAD_MIN_LENGTH = 0.25; // guards against tiny/zero values
const MEASUREMENT_ARROW_HEAD_MIN_WIDTH  = 0.25;

import customImageInteractor from "./CustomImageInteractor";
import vtkMatrixBuilder from "vtk.js/Sources/Common/Core/MatrixBuilder";
import vtkCoordinate from 'vtk.js/Sources/Rendering/Core/Coordinate';

import { useScreenshot } from '../Screenshot/Screenshot';
import { useInteraction } from './Interaction';
import { InterpolationType } from "vtk.js/Sources/Rendering/Core/ImageProperty/Constants";

import { Union } from "fable-compiler/bin/fable-library/Types";

import { ProjectionMode } from 'vtk.js/Sources/Rendering/Core/ImageCPRMapper/Constants';
import { radiansFromDegrees } from 'vtk.js/Sources/Common/Core/Math';
import { updateState } from 'vtk.js/Sources/Widgets/Widgets3D/ResliceCursorWidget/helpers';
import { vec3, mat3, mat4 } from 'gl-matrix';
import { ViewTypes } from 'vtk.js/Sources/Widgets/Core/WidgetManager/Constants';
import vtkCPRManipulator from 'vtk.js/Sources/Widgets/Manipulators/CPRManipulator';
import vtkDataArray from 'vtk.js/Sources/Common/Core/DataArray';
import vtkImageCPRMapper from 'vtk.js/Sources/Rendering/Core/ImageCPRMapper';
import vtkImageSlice from 'vtk.js/Sources/Rendering/Core/ImageSlice';
import vtkPlaneManipulator from 'vtk.js/Sources/Widgets/Manipulators/PlaneManipulator';
import vtkPolyData from 'vtk.js/Sources/Common/DataModel/PolyData';
// Importing vtkPolyLine even if not used directly so we can reference it via JSdoc.
import vtkPolyLine from 'vtk.js/Sources/Common/DataModel/PolyLine';
import vtkPointPicker from 'vtk.js/Sources/Rendering/Core/PointPicker';
import vtkResliceCursorWidget from 'vtk.js/Sources/Widgets/Widgets3D/ResliceCursorWidget';
import { viewTypeToPlaneName } from 'vtk.js/Sources/Widgets/Widgets3D/ResliceCursorWidget/Constants';
import vtkWidgetManager from 'vtk.js/Sources/Widgets/Core/WidgetManager';
import widgetBehavior from 'vtk.js/Sources/Widgets/Widgets3D/ResliceCursorWidget/cprBehavior';

// Spline-related imports
import vtkSplineWidget from 'vtk.js/Sources/Widgets/Widgets3D/SplineWidget';
import { splineKind } from 'vtk.js/Sources/Common/DataModel/Spline3D/Constants';
import { BoundaryCondition } from 'vtk.js/Sources/Common/DataModel/Spline1D/Constants';

// For exporting edited centerline
import vtkPoints from 'vtk.js/Sources/Common/Core/Points';
import vtkXMLPolyDataWriter from 'vtk.js/Sources/IO/XML/XMLPolyDataWriter';
import vtkXMLWriter from 'vtk.js/Sources/IO/XML/XMLWriter';
import vtkXMLPolyDataReader from 'vtk.js/Sources/IO/XML/XMLPolyDataReader';

const isDebugging = false;

function log(msg, ...params) {
    logDebug("CprVisualization", isDebugging, msg, ...params);
}

function debug(callback) {
    debugCallback(isDebugging, callback);
}

function highlightScalarForColor(segmentationColor) {
    switch (segmentationColor) {
        case 'Blue': return STRETCHED_HANDLE_SCALAR_TOP_VIEW;
        case 'Red':  return STRETCHED_HANDLE_SCALAR_BOTTOM_VIEW;
        default:     return STRETCHED_HANDLE_SCALAR;
    }
}

// Per-orthogonal-view control point highlight state. The most recently touched
// view wins if both views happen to highlight the same centerline control point.
const activeOrthogonalHighlights = new Map();

function findActiveHighlightForPointIndex(pointIndex) {
    if (pointIndex == null) return null;
    let match = null;
    for (const highlight of activeOrthogonalHighlights.values()) {
        if (highlight.pointIndex === pointIndex) {
            match = highlight;
        }
    }
    return match;
}

function applyHandleHighlight(handle, pointIndex, handlesVisible = true) {
    if (!handlesVisible) {
        handle.setScale1(0);
        return;
    }
    const active = findActiveHighlightForPointIndex(pointIndex);
    handle.setScale1(active ? STRETCHED_HANDLE_SIZE_ACTIVE : STRETCHED_HANDLE_SIZE);
    handle.setColor(active ? active.color : STRETCHED_HANDLE_SCALAR);
}

function setOrthogonalHighlight(viewportId, pointIndex, segmentationColor) {
    if (!viewportId) return;
    // Re-insert so the entry moves to the back of the iteration order.
    activeOrthogonalHighlights.delete(viewportId);
    if (pointIndex == null) return;
    activeOrthogonalHighlights.set(viewportId, {
        pointIndex,
        color: highlightScalarForColor(segmentationColor),
    });
}

/**
 * Create a non-interactive centerline overlay for the straightened CPR view.
 * The overlay consists of two VTK actors:
 *   - A line actor: the straightened centerline (vertical line at the horizontal center).
 *   - A points actor: square GL point primitives at each control point position.
 * @param {[number, number, number]} color - RGB color [0..1] applied to both actors.
 * @returns {{ lineActor: vtkActor, lineMapper: vtkMapper, linePolyData: vtkPolyData,
 *             pointsActor: vtkActor, pointsMapper: vtkMapper, pointsPolyData: vtkPolyData }}
 */
function createStraightenedCenterlineOverlay(color) {
    const [r, g, b] = color;

    // --- Centerline line actor ---
    const linePolyData = vtkPolyData.newInstance();
    const linePoints = vtkPoints.newInstance();
    linePoints.setNumberOfPoints(2);
    linePolyData.setPoints(linePoints);
    linePolyData.getLines().setData(new Uint16Array([2, 0, 1]));

    const lineMapper = vtkMapper.newInstance();
    lineMapper.setInputData(linePolyData);

    const lineActor = vtkActor.newInstance();
    lineActor.setMapper(lineMapper);
    lineActor.getProperty().setColor(r, g, b);
    lineActor.getProperty().setLineWidth(STRAIGHTENED_OVERLAY_LINE_WIDTH);
    lineActor.getProperty().setLighting(false);

    // --- Control points actor (GL point primitives) ---
    const pointsPolyData = vtkPolyData.newInstance();

    const pointsMapper = vtkMapper.newInstance();
    pointsMapper.setInputData(pointsPolyData);

    const pointsActor = vtkActor.newInstance();
    pointsActor.setMapper(pointsMapper);
    pointsActor.getProperty().setColor(r, g, b);
    pointsActor.getProperty().setPointSize(STRAIGHTENED_OVERLAY_POINT_SIZE);
    pointsActor.getProperty().setLighting(false);

    return { lineActor, lineMapper, linePolyData, pointsActor, pointsMapper, pointsPolyData };
}

/**
 * Update the geometry of the straightened CPR centerline overlay.
 * Recomputes the centerline line endpoints and control point positions from the
 * current CPR frame and mapper state, then writes them into the overlay polydata.
 * @param {{ linePolyData: vtkPolyData, pointsPolyData: vtkPolyData }} overlay
 * @param {vtkImageSlice} actor - The CPR image actor (provides the world transform via getUserMatrix).
 * @param {vtkImageCPRMapper} mapper - The CPR image mapper.
 * @param {BranchControlData|null|undefined} branchControlData - Control data for the active branch.
 * @param {Object} currentCPRFrame - CPR frame snapshot (width, height).
 */
function updateStraightenedCenterlineOverlay(overlay, actor, mapper, branchControlData, currentCPRFrame) {
    if (!overlay || !actor || !mapper || !currentCPRFrame) return;

    const { width, height } = currentCPRFrame;
    if (width == null || height == null) return;

    const actorMatrix = actor.getUserMatrix();
    if (!actorMatrix) return;

    // ---- Centerline line: vertical line at the horizontal center of the CPR strip ----
    const lineStart = [width / 2, 0, 0];
    const lineEnd   = [width / 2, height, 0];
    const worldStart = [0, 0, 0];
    const worldEnd   = [0, 0, 0];
    vec3.transformMat4(worldStart, lineStart, actorMatrix);
    vec3.transformMat4(worldEnd,   lineEnd,   actorMatrix);

    const linePts = overlay.linePolyData.getPoints();
    linePts.setPoint(0, worldStart[0], worldStart[1], worldStart[2]);
    linePts.setPoint(1, worldEnd[0],   worldEnd[1],   worldEnd[2]);
    linePts.modified();
    overlay.linePolyData.modified();

    // ---- Control points: one GL point primitive per control point ----
    const worldControlPointIndices = branchControlData?.splineWorldControlPointData?.worldControlPointIndices;
    if (!worldControlPointIndices?.length) {
        overlay.pointsPolyData.setPoints(vtkPoints.newInstance());
        overlay.pointsPolyData.getVerts().setData(new Uint16Array([]));
        overlay.pointsPolyData.modified();
        return;
    }

    const orientedCenterline = mapper.getOrientedCenterline();
    if (!orientedCenterline) return;

    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const nPts = orientedCenterline.getPoints().getNumberOfPoints();
    if (!distancesToFirstPoint || !nPts) return;

    const params = buildCPRParams(mapper, orientedCenterline, currentCPRFrame);

    const worldPositions = [];
    for (let i = 0; i < worldControlPointIndices.length; i++) {
        const clIndex = worldControlPointIndices[i];
        if (clIndex == null || clIndex < 0 || clIndex >= nPts) continue;

        const cprCoords = computeCPRCoordsForCenterlineIndex(clIndex, orientedCenterline, params);
        if (!cprCoords) continue;

        const cprPos = [width / 2, cprCoords[1], 0];
        const worldPos = [0, 0, 0];
        vec3.transformMat4(worldPos, cprPos, actorMatrix);
        worldPositions.push(worldPos[0], worldPos[1], worldPos[2]);
    }

    const nValid = worldPositions.length / 3;
    if (nValid > 0) {
        const ptArray = vtkPoints.newInstance();
        ptArray.setData(new Float32Array(worldPositions), 3);

        const vertsData = new Uint16Array(nValid * 2);
        for (let i = 0; i < nValid; i++) {
            vertsData[2 * i]     = 1;
            vertsData[2 * i + 1] = i;
        }

        overlay.pointsPolyData.setPoints(ptArray);
        overlay.pointsPolyData.getVerts().setData(vertsData);
    } else {
        overlay.pointsPolyData.setPoints(vtkPoints.newInstance());
        overlay.pointsPolyData.getVerts().setData(new Uint16Array([]));
    }
    overlay.pointsPolyData.modified();
}

/**
 * Remove the straightened centerline overlay actors from the renderer and free VTK resources.
 * @param {vtkRenderer|null|undefined} renderer
 * @param {{ lineActor, lineMapper, linePolyData, pointsActor, pointsMapper, pointsPolyData }|null|undefined} overlay
 */
function disposeStraightenedCenterlineOverlay(renderer, overlay) {
    if (!overlay) return;
    if (renderer) {
        if (overlay.lineActor)   renderer.removeActor(overlay.lineActor);
        if (overlay.pointsActor) renderer.removeActor(overlay.pointsActor);
    }
    overlay.lineActor?.delete?.();
    overlay.lineMapper?.delete?.();
    overlay.linePolyData?.delete?.();
    overlay.pointsActor?.delete?.();
    overlay.pointsMapper?.delete?.();
    overlay.pointsPolyData?.delete?.();
}

// Measurement guide line helpers for the straightened CPR view.
/**
 * @typedef {Object} StraightenedMeasurementLineConfig
 * @property {string} key - Stable identifier so actors can be replaced atomically.
 * @property {number} offset - Measurement index from the proximal end of the centerline.
 * @property {[number, number, number]} color - RGB color used for the guide line.
 */
const diameterMeasurementCaseNames = ['Diameter', 'OffsetDiameter', 'MidpointDiameter'];

/**
 * Validate the provided centerline index.
 * @param {number} value - Centerline index to check.
 * @returns {boolean} True when the index is usable.
 */
function isValidCenterlineIndex(value) {
    return Number.isFinite(value) && value >= 0;
}

/**
 * Convert the optional active measurement into line configs for the straightened CPR view.
 * @param {Union|null|undefined} maybeMeasurement - Active stent measurement (Fable union).
 * @returns {StraightenedMeasurementLineConfig[]} Measurement-aligned line definitions.
 */
function getStraightenedMeasurementLineConfigs(maybeMeasurement) {
    if (isNullOrUndefined(maybeMeasurement)) {
        return [];
    }

    const measurementName = maybeMeasurement.name;

    if (diameterMeasurementCaseNames.includes(measurementName)) {
        const selection = maybeMeasurement?.fields?.[1]?.[0];
        const index = Number(selection?.index);

        if (!isValidCenterlineIndex(index)) {
            return [];
        }

        return [
            {
                key: `${measurementName}-${index}`,
                offset: index,
                color: MEASUREMENT_LINE_COLORS.start,
            },
        ];
    }

    if (measurementName === 'Length') {
        const range = maybeMeasurement?.fields?.[1]?.[0];
        const firstIndex = Number(range?.firstIndex);
        const secondIndex = Number(range?.secondIndex);
        const configs = [];

        if (isValidCenterlineIndex(firstIndex)) {
            configs.push({
                key: `${measurementName}-start-${firstIndex}`,
                offset: firstIndex,
                color: MEASUREMENT_LINE_COLORS.start,
            });
        }

        if (isValidCenterlineIndex(secondIndex)) {
            configs.push({
                key: `${measurementName}-end-${secondIndex}`,
                offset: secondIndex,
                color: MEASUREMENT_LINE_COLORS.end,
            });
        }

        return configs;
    }

    return [];
}

/**
 * Remove the supplied measurement lines from the renderer and free their resources.
 * @param {vtkRenderer|null|undefined} renderer - Renderer hosting the straightened lines.
 * @param {Array<{actor: vtkActor, mapper: vtkMapper, polyData: vtkPolyData}>} [lineDescriptors=[]] - Actors to dispose.
 * @returns {void}
 */
function disposeStraightenedIndexLines(renderer, lineDescriptors = []) {
    if (!Array.isArray(lineDescriptors) || !lineDescriptors.length) {
        return;
    }

    lineDescriptors.forEach((descriptor) => {
        if (!descriptor) {
            return;
        }

        if (renderer && descriptor.actor) {
            renderer.removeActor(descriptor.actor);
        }

        descriptor.actor?.delete?.();
        descriptor.mapper?.delete?.();
    });
}

/**
 * Update the measurement lines for the straightened view to reflect the active measurement.
 * @param {{
 *   cprObjects: {
 *     resliceFilter: vtkImageReslice,
 *     horizontalIndexLines?: Array<{actor: vtkActor, mapper: vtkMapper, polyData: vtkPolyData}>,
 *   },
 *   renderer: vtkRenderer,
 *   renderWindow: vtkRenderWindow,
 * }|null} ctx - Visualization context created during initialization.
 * @param {Union|null|undefined} maybeMeasurement - Active stent measurement (Fable union).
 * @returns {void}
 */
function refreshStraightenedMeasurementLines(ctx, maybeMeasurement) {
    if (!ctx || !ctx.cprObjects?.resliceFilter || !ctx.renderer || !ctx.renderWindow) {
        return;
    }

    const existingLines = Array.isArray(ctx.cprObjects.horizontalIndexLines)
        ? ctx.cprObjects.horizontalIndexLines
        : [];
    const hadLines = existingLines.length > 0;

    disposeStraightenedIndexLines(ctx.renderer, existingLines);
    ctx.cprObjects.horizontalIndexLines = [];

    const lineConfigs = getStraightenedMeasurementLineConfigs(maybeMeasurement);

    if (!lineConfigs.length) {
        if (hadLines) {
            ctx.renderWindow?.render();
        }
        return;
    }

    const descriptors = lineConfigs
        .map((config) =>
            createStraightenedIndexLine(
                ctx.cprObjects.resliceFilter,
                config,
                ctx.renderer,
                ctx.renderWindow
            )
        )
        .filter(Boolean);

    if (!descriptors.length) {
        if (hadLines) {
            ctx.renderWindow?.render();
        }
        return;
    }

    descriptors.forEach((descriptor) => ctx.renderer.addActor(descriptor.actor));
    ctx.cprObjects.horizontalIndexLines = descriptors;

    ctx.renderWindow?.render();
}

/**
 * Compute the I-axis index for a straightened CPR guide line from the proximal end.
 * The I axis corresponds to the distance along the straightened centerline, so we clamp the
 * requested offset against the reslice extent to ensure the line always lies within the image.
 *
 * @param {number[]} extent - vtkImageData extent in the form [iMin, iMax, jMin, jMax, kMin, kMax].
 * @param {number} offset - Measurement index from the proximal end of the centerline.
 * @returns {number|null} - Reslice-space I index or null if the extent is invalid.
 */
function computeStraightenedLineImageIndex(extent, offset) {
    if (!extent || extent.length !== 6) {
        return null;
    }

    const iMin = extent[0];
    const iMax = extent[1];

    if (!Number.isFinite(iMin) || !Number.isFinite(iMax)) {
        return null;
    }

    const span = Math.max(0, iMax - iMin);
    const safeOffset = clamp(Number(offset) || 0, 0, span);

    return iMax - safeOffset;
}

/**
 * Update a single straightened CPR guide line by sampling the current reslice output.
 * Converts the fixed I index into world coordinates for the (I, Jmin) and (I, Jmax) points and
 * writes them into the line's shared vtkPolyData.
 *
 * @param {vtkImageReslice} resliceFilter - Source filter that generates the straightened CPR image.
 * @param {{ polyData: vtkPolyData, offset: number }} lineDescriptor - Mutable descriptor for the line.
 * @param {vtkRenderer} renderer - VTK renderer for viewport calculations.
 * @param {vtkRenderWindow} renderWindow - VTK render window for viewport calculations.
 * @returns {void}
 */
function updateStraightenedIndexLine(resliceFilter, lineDescriptor, renderer, renderWindow) {
    if (!resliceFilter || !lineDescriptor?.polyData) {
        return;
    }

    const reslicedImage = resliceFilter.getOutputData();
    if (!reslicedImage) {
        return;
    }

    const extent = reslicedImage.getExtent();
    if (!extent || extent.length !== 6) {
        log('Horizontal index line: missing extent information.');
        return;
    }

    const [_iMin, _iMax, jMin, jMax, kMin, kMax] = extent;

    if (kMin !== kMax) {
        log('Horizontal index line: expected single K slice, got', kMin, kMax);
        return;
    }

    const iIndex = computeStraightenedLineImageIndex(
        extent,
        lineDescriptor.offset,
    );

    if (!Number.isFinite(iIndex)) {
        return;
    }

    // Get the full width endpoints in world space.
    const leftWorld = reslicedImage.indexToWorld([iIndex, jMin, kMin]);
    const rightWorld = reslicedImage.indexToWorld([iIndex, jMax, kMin]);

    let start = leftWorld;
    let end = rightWorld;

    // If renderer and renderWindow are available, calculate 90% based on actual display dimensions.
    if (renderer && renderWindow && leftWorld && rightWorld) {
        const view = renderWindow.getViews()[0];
        if (view) {
            // Get the canvas element.
            const canvas = view.getCanvas();
            if (canvas) {
                const canvasWidth = canvas.width;
                const canvasHeight = canvas.height;

                if (canvasWidth > 0 && canvasHeight > 0) {
                    // In parallel projection, calculate the world-space width visible in the viewport.
                    const camera = renderer.getActiveCamera();
                    const parallelScale = camera.getParallelScale();
                    const aspect = canvasWidth / canvasHeight;

                    // The visible world height is 2 * parallelScale.
                    // The visible world width is 2 * parallelScale * aspect.
                    const worldWidth = 2 * parallelScale * aspect;

                    // Cover MEASUREMENT_LINE_VIEWPORT_RATIO of the world width.
                    const targetWorldWidth = worldWidth * MEASUREMENT_LINE_VIEWPORT_RATIO;
                    const halfTargetWidth = targetWorldWidth / 2;

                    // The line should be horizontal at the I index position, so we need to find
                    // the Y coordinate at that position and extend +/- halfTargetWidth in Y direction.
                    const centerWorld = reslicedImage.indexToWorld([iIndex, (jMin + jMax) / 2, kMin]);

                    // Create start and end points by offsetting in Y direction (horizontal in the view).
                    start = [centerWorld[0], centerWorld[1] - halfTargetWidth, centerWorld[2]];
                    end = [centerWorld[0], centerWorld[1] + halfTargetWidth, centerWorld[2]];
                }
            }
        }
    }

    if (!start || !end) {
        log('Horizontal index line: unable to convert IJK to world coordinates.');
        return;
    }

    const points = lineDescriptor.polyData.getPoints();
    if (!points) {
        return;
    }

    if (points.getNumberOfPoints() < 2) {
        points.setNumberOfPoints(2);
    }

    points.setPoint(0, start[0], start[1], start[2]);
    points.setPoint(1, end[0], end[1], end[2]);
    points.modified();

    lineDescriptor.polyData.modified();
}

/**
 * Update a collection of straightened CPR guide lines.
 *
 * @param {vtkImageReslice} resliceFilter - Straightened CPR reslice filter.
 * @param {Array<{ polyData: vtkPolyData, offset: number }>} lineDescriptors - Line descriptors to refresh.
 * @param {vtkRenderer} renderer - VTK renderer for viewport calculations.
 * @param {vtkRenderWindow} renderWindow - VTK render window for viewport calculations.
 * @returns {void}
 */
function updateStraightenedIndexLines(resliceFilter, lineDescriptors = [], renderer, renderWindow) {
    lineDescriptors.forEach((descriptor) => {
        updateStraightenedIndexLine(resliceFilter, descriptor, renderer, renderWindow);
    });
}

/**
 * Create a straightened CPR guide line actor/mapper pair and initialize it against the current reslice output.
 *
 * @param {vtkImageReslice} resliceFilter - Straightened CPR reslice filter.
 * @param {{ key: string, offset: number, color: [number, number, number] }} config - Line configuration.
 * @param {vtkRenderer} renderer - VTK renderer for viewport calculations.
 * @param {vtkRenderWindow} renderWindow - VTK render window for viewport calculations.
 * @returns {{ actor: vtkActor, mapper: vtkMapper, polyData: vtkPolyData, offset: number, key: string }|null}
 */
function createStraightenedIndexLine(resliceFilter, config, renderer, renderWindow) {
    if (!resliceFilter || !config) {
        return null;
    }

    const polyData = vtkPolyData.newInstance();
    const points = vtkPoints.newInstance();
    points.setNumberOfPoints(2);
    polyData.setPoints(points);

    const lines = new Uint16Array([2, 0, 1]);
    polyData.getLines().setData(lines);

    const mapper = vtkMapper.newInstance();
    mapper.setInputData(polyData);

    const actor = vtkActor.newInstance();
    actor.setMapper(mapper);
    const [r, g, b] = config.color || [1, 1, 1];
    actor.getProperty().setColor(r, g, b);
    actor.getProperty().setLineWidth(MEASUREMENT_LINE_WIDTH);
    actor.getProperty().setLighting(false);

    const descriptor = {
        actor,
        mapper,
        polyData,
        offset: config.offset,
        key: config.key,
    };

    updateStraightenedIndexLine(resliceFilter, descriptor, renderer, renderWindow);

    return descriptor;
}

/**
 * @typedef {Object} StraightenedLengthArrowDescriptor
 * @property {vtkActor} actor - Actor responsible for rendering the arrow.
 * @property {vtkMapper} mapper - Mapper connected to the arrow polydata.
 * @property {vtkPolyData} polyData - Polydata containing line segments for the arrow and heads.
 * @property {number|null} startOffset - Measurement index from the proximal end.
 * @property {number|null} endOffset - Measurement index from the proximal end.
 */

/**
 * Convert a desired display-space length into world-space magnitude along the supplied direction.
 * @param {vtkRenderer|null|undefined} renderer - Renderer used for world/display transforms.
 * @param {number[]|Float32Array} originWorld - Anchor point expressed in world coordinates.
 * @param {import('gl-matrix').vec3} direction - Direction vector defining the measurement axis.
 * @param {number} pixels - Target size in pixels.
 * @returns {number} Corresponding world-space magnitude or 0 when unavailable.
 */
function getWorldLengthForPixelDelta(renderer, originWorld, direction, pixels) {
    if (!renderer || !originWorld || !direction || !Number.isFinite(pixels) || pixels <= 0) {
        return 0;
    }

    const normalizedDir = vec3.clone(direction);
    if (vec3.len(normalizedDir) < 1e-5) {
        return 0;
    }
    vec3.normalize(normalizedDir, normalizedDir);

    const coord = vtkCoordinate.newInstance();
    coord.setCoordinateSystemToWorld();

    coord.setValue(originWorld[0], originWorld[1], originWorld[2]);
    const startDisplay = coord.getComputedDoubleDisplayValue(renderer);
    const start = [startDisplay[0], startDisplay[1]];

    const samplePoint = vec3.scaleAndAdd(vec3.create(), originWorld, normalizedDir, 1);
    coord.setValue(samplePoint[0], samplePoint[1], samplePoint[2]);
    const endDisplay = coord.getComputedDoubleDisplayValue(renderer);
    const end = [endDisplay[0], endDisplay[1]];

    const pixelsPerWorld = Math.hypot(end[0] - start[0], end[1] - start[1]);

    if (!Number.isFinite(pixelsPerWorld) || pixelsPerWorld < 1e-5) {
        return 0;
    }

    return pixels / pixelsPerWorld;
}

/**
 * Extract offsets for the straightened length measurement arrow.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement union from Elmish state.
 * @returns {{start: number, end: number}|null}
 */
function getStraightenedLengthMeasurementOffsets(maybeMeasurement) {
    if (isNullOrUndefined(maybeMeasurement) || maybeMeasurement.name !== 'Length') {
        return null;
    }

    const range = maybeMeasurement?.fields?.[1]?.[0];
    const firstIndex = Number(range?.firstIndex);
    const secondIndex = Number(range?.secondIndex);

    if (!isValidCenterlineIndex(firstIndex) || !isValidCenterlineIndex(secondIndex)) {
        return null;
    }

    return {
        start: firstIndex,
        end: secondIndex,
    };
}

/**
 * Create the reusable arrow descriptor for straightened length measurements.
 * @returns {StraightenedLengthArrowDescriptor}
 */
function createStraightenedMeasurementArrow() {
    const polyData = vtkPolyData.newInstance();
    const points = vtkPoints.newInstance();
    points.setNumberOfPoints(6);
    polyData.setPoints(points);

    // Five independent line segments: shaft plus four arrowhead edges.
    const lines = new Uint16Array([
        2, 0, 1,
        2, 1, 2,
        2, 1, 3,
        2, 0, 4,
        2, 0, 5,
    ]);
    polyData.getLines().setData(lines);

    const mapper = vtkMapper.newInstance();
    mapper.setInputData(polyData);

    const actor = vtkActor.newInstance();
    actor.setMapper(mapper);
    actor.getProperty().setColor(...MEASUREMENT_ARROW_COLOR);
    actor.getProperty().setLineWidth(MEASUREMENT_ARROW_LINE_WIDTH);
    actor.getProperty().setLighting(false);

    return {
        actor,
        mapper,
        polyData,
        startOffset: null,
        endOffset: null,
    };
}

/**
 * Dispose of the arrow actor and associated resources.
 * @param {vtkRenderer|null|undefined} renderer - Renderer the arrow was added to.
 * @param {StraightenedLengthArrowDescriptor|null|undefined} arrowDescriptor - Descriptor to dispose.
 * @returns {void}
 */
function disposeStraightenedMeasurementArrow(renderer, arrowDescriptor) {
    if (!arrowDescriptor) {
        return;
    }

    if (renderer && arrowDescriptor.actor) {
        renderer.removeActor(arrowDescriptor.actor);
    }

    arrowDescriptor.actor?.delete?.();
    arrowDescriptor.mapper?.delete?.();
    arrowDescriptor.polyData?.delete?.();
}

/**
 * Update the arrow geometry so it stays aligned with the measurement bounds.
 * @param {vtkImageReslice} resliceFilter - Reslice filter powering the straightened view.
 * @param {StraightenedLengthArrowDescriptor} arrowDescriptor - Descriptor containing offsets and VTK data.
 * @param {vtkRenderer|null} renderer - Renderer used to evaluate pixel-to-world scaling.
 * @param {vtkRenderWindow|null} renderWindow - Render window providing canvas dimensions.
 * @returns {boolean} True when the arrow geometry was updated.
 */
function updateStraightenedMeasurementArrow(resliceFilter, arrowDescriptor, renderer, renderWindow) {
    if (!resliceFilter || !arrowDescriptor || !Number.isFinite(arrowDescriptor.startOffset) || !Number.isFinite(arrowDescriptor.endOffset)) {
        return false;
    }

    const reslicedImage = resliceFilter.getOutputData();
    if (!reslicedImage) {
        return false;
    }

    const extent = reslicedImage.getExtent();
    if (!extent || extent.length !== 6) {
        log('Length arrow: missing extent information.');
        return false;
    }

    const [iMin, iMax, jMin, jMax, kMin, kMax] = extent;

    if (kMin !== kMax) {
        log('Length arrow: expected a single K slice.');
        return false;
    }

    // Compute I indices for start and end points from the proximal end.
    const startIndex = computeStraightenedLineImageIndex(extent, arrowDescriptor.startOffset);
    const endIndex = computeStraightenedLineImageIndex(extent, arrowDescriptor.endOffset);

    if (!Number.isFinite(startIndex) || !Number.isFinite(endIndex)) {
        return false;
    }

    const lowIndex = Math.min(startIndex, endIndex);
    const highIndex = Math.max(startIndex, endIndex);

    // Position arrow laterally using viewport width calculation.
    const centerJ = jMin + (jMax - jMin) * 0.5;
    let startWorld = reslicedImage.indexToWorld([lowIndex, centerJ, kMin]);
    let endWorld = reslicedImage.indexToWorld([highIndex, centerJ, kMin]);

    if (!startWorld || !endWorld) {
        log('Length arrow: unable to map IJK to world coordinates.');
        return false;
    }

    // Compute lateral direction from camera view.
    let lateralDir = null;
    if (renderer) {
        const camera = renderer.getActiveCamera();
        const viewPlaneNormal = camera?.getViewPlaneNormal?.();
        const viewUp = camera?.getViewUp?.();
        if (viewPlaneNormal && viewUp) {
            lateralDir = vec3.cross(vec3.create(), viewUp, viewPlaneNormal);
            if (vec3.len(lateralDir) > 1e-5) {
                vec3.normalize(lateralDir, lateralDir);
                // Ensure lateral direction points right in display space.
                const coord = vtkCoordinate.newInstance();
                coord.setCoordinateSystemToWorld();
                coord.setValue(startWorld[0], startWorld[1], startWorld[2]);
                const referenceDisplay = coord.getComputedDoubleDisplayValue(renderer);
                const sampleWorld = vec3.add(vec3.create(), startWorld, lateralDir);
                coord.setValue(sampleWorld[0], sampleWorld[1], sampleWorld[2]);
                const sampleDisplay = coord.getComputedDoubleDisplayValue(renderer);
                if (sampleDisplay?.[0] < referenceDisplay?.[0]) {
                    vec3.scale(lateralDir, lateralDir, -1);
                }
            } else {
                lateralDir = null;
            }
        }
    }

    // Position arrow at 75% of viewport width.
    if (lateralDir && renderer && renderWindow) {
        const view = renderWindow.getViews()?.[0];
        const canvas = view?.getCanvas?.();
        if (canvas?.width > 0 && canvas?.height > 0) {
            const camera = renderer.getActiveCamera();
            const parallelScale = camera.getParallelScale();
            const aspect = canvas.width / canvas.height;
            const worldWidth = 2 * parallelScale * aspect;
            const offsetFromCenter = (MEASUREMENT_ARROW_VIEWPORT_RATIO - 0.5) * worldWidth;
            const lateralShift = vec3.scale(vec3.create(), lateralDir, offsetFromCenter);
            startWorld = vec3.add(vec3.create(), startWorld, lateralShift);
            endWorld = vec3.add(vec3.create(), endWorld, lateralShift);
        }
    }

    // Compute arrow direction and length.
    const arrowDir = vec3.sub(vec3.create(), endWorld, startWorld);
    const arrowLength = vec3.len(arrowDir);

    if (!Number.isFinite(arrowLength) || arrowLength < 1e-5) {
        return false;
    }

    vec3.normalize(arrowDir, arrowDir);

    // Compute perpendicular direction for arrowheads.
    const perpDir = lateralDir && vec3.len(lateralDir) > 1e-5
        ? vec3.normalize(vec3.create(), lateralDir)
        : vec3.normalize(vec3.create(), vec3.cross(vec3.create(), arrowDir, [0, 0, 1]));

    // Compute arrowhead dimensions in world space from pixel sizes.
    let headLength = renderer
        ? getWorldLengthForPixelDelta(renderer, startWorld, arrowDir, MEASUREMENT_ARROW_HEAD_LENGTH_PX)
        : MEASUREMENT_ARROW_HEAD_MIN_LENGTH;
    headLength = clamp(headLength || MEASUREMENT_ARROW_HEAD_MIN_LENGTH, MEASUREMENT_ARROW_HEAD_MIN_LENGTH, arrowLength * 0.4);

    let headWidth = renderer
        ? getWorldLengthForPixelDelta(renderer, startWorld, perpDir, MEASUREMENT_ARROW_HEAD_WIDTH_PX)
        : MEASUREMENT_ARROW_HEAD_MIN_WIDTH;
    headWidth = clamp(headWidth || MEASUREMENT_ARROW_HEAD_MIN_WIDTH, MEASUREMENT_ARROW_HEAD_MIN_WIDTH, headLength * 2);

    // Build arrow geometry: shaft from startWorld to endWorld, with arrowheads at each end.
    const startInset = vec3.scaleAndAdd(vec3.create(), startWorld, arrowDir, headLength);
    const startLeft = vec3.scaleAndAdd(vec3.create(), startInset, perpDir, headWidth);
    const startRight = vec3.scaleAndAdd(vec3.create(), startInset, perpDir, -headWidth);

    const endInset = vec3.scaleAndAdd(vec3.create(), endWorld, arrowDir, -headLength);
    const endLeft = vec3.scaleAndAdd(vec3.create(), endInset, perpDir, headWidth);
    const endRight = vec3.scaleAndAdd(vec3.create(), endInset, perpDir, -headWidth);

    // Update polydata points.
    const points = arrowDescriptor.polyData.getPoints();
    if (!points) {
        return false;
    }

    if (points.getNumberOfPoints() < 6) {
        points.setNumberOfPoints(6);
    }

    points.setPoint(0, startWorld[0], startWorld[1], startWorld[2]);
    points.setPoint(1, endWorld[0], endWorld[1], endWorld[2]);
    points.setPoint(2, endLeft[0], endLeft[1], endLeft[2]);
    points.setPoint(3, endRight[0], endRight[1], endRight[2]);
    points.setPoint(4, startLeft[0], startLeft[1], startLeft[2]);
    points.setPoint(5, startRight[0], startRight[1], startRight[2]);
    points.modified();

    arrowDescriptor.polyData.modified();

    return true;
}

/**
 * Synchronize the arrow descriptor with the supplied measurement state.
 * @param {{ cprObjects: { resliceFilter: vtkImageReslice, lengthMeasurementArrow?: StraightenedLengthArrowDescriptor|null }, renderer: vtkRenderer, renderWindow: vtkRenderWindow }|null} ctx - Visualization context.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement union from Elmish state.
 * @returns {void}
 */
function refreshStraightenedLengthMeasurementArrow(ctx, maybeMeasurement) {
    if (!ctx || !ctx.cprObjects?.resliceFilter || !ctx.renderer) {
        return;
    }

    const offsets = getStraightenedLengthMeasurementOffsets(maybeMeasurement);

    if (!offsets) {
        if (ctx.cprObjects.lengthMeasurementArrow) {
            disposeStraightenedMeasurementArrow(ctx.renderer, ctx.cprObjects.lengthMeasurementArrow);
            ctx.cprObjects.lengthMeasurementArrow = null;
            ctx.renderWindow?.render();
        }
        return;
    }

    let arrowDescriptor = ctx.cprObjects.lengthMeasurementArrow;
    let addedArrow = false;

    if (!arrowDescriptor) {
        arrowDescriptor = createStraightenedMeasurementArrow();
        ctx.renderer.addActor(arrowDescriptor.actor);
        ctx.cprObjects.lengthMeasurementArrow = arrowDescriptor;
        addedArrow = true;
    }

    arrowDescriptor.startOffset = offsets.start;
    arrowDescriptor.endOffset = offsets.end;

    const updated = updateStraightenedMeasurementArrow(
        ctx.cprObjects.resliceFilter,
        arrowDescriptor,
        ctx.renderer,
        ctx.renderWindow,
    );

    if ((addedArrow || updated) && ctx.renderWindow) {
        ctx.renderWindow.render();
    }
}

/**
 * Initialize the Konva stage for the straightened CPR measurement label.
 * @param {HTMLElement} container - Container element for the Konva stage.
 * @param {string} viewportId - Unique identifier for the viewport.
 * @returns {{stage: Konva.Stage, layer: Konva.Layer, label: Konva.Label, konvaContainer: HTMLElement}|null}
 */
function initializeStraightenedMeasurementLabel(container, viewportId) {
    if (!container) {
        return null;
    }

    // Create a div container for Konva that overlays the VTK canvas.
    const konvaContainer = document.createElement('div');
    konvaContainer.id = `cprMeasurementLabelContainer-${viewportId}`;
    konvaContainer.style.position = 'absolute';
    konvaContainer.style.top = '0';
    konvaContainer.style.left = '0';
    konvaContainer.style.width = '100%';
    konvaContainer.style.height = '100%';
    konvaContainer.style.pointerEvents = 'none'; // Allow clicks to pass through to VTK.
    konvaContainer.style.zIndex = '1';
    container.appendChild(konvaContainer);

    const stage = new Konva.Stage({
        container: konvaContainer,
        width: container.offsetWidth,
        height: container.offsetHeight,
    });

    const layer = new Konva.Layer();
    stage.add(layer);

    const label = new Konva.Label({
        visible: false,
    });

    const tag = new Konva.Tag({
        fill: 'yellow',
        cornerRadius: 3,
        pointerDirection: 'none',
    });

    const text = new Konva.Text({
        text: '',
        fontSize: 24,
        fontFamily: 'Open Sans',
        fonstStyle: 'Bold',
        fill: 'black',
        padding: 5,
    });

    label.add(tag);
    label.add(text);
    layer.add(label);

    return { stage, layer, label, konvaContainer };
}

/**
 * Update the Konva measurement label for the straightened CPR view.
 * @param {{ cprObjects: { resliceFilter: vtkImageReslice, lengthMeasurementArrow?: StraightenedLengthArrowDescriptor|null }, renderer: vtkRenderer, renderWindow: vtkRenderWindow, konvaElements?: { stage: Konva.Stage, layer: Konva.Layer, label: Konva.Label } }|null} ctx - Visualization context.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement union from Elmish state.
 * @returns {void}
 */
function updateStraightenedMeasurementLabel(ctx, maybeMeasurement) {
    if (!ctx || !ctx.konvaElements || !ctx.cprObjects?.resliceFilter || !ctx.renderer || !ctx.renderWindow) {
        return;
    }

    const { stage, layer, label } = ctx.konvaElements;

    if (!stage || !layer || !label) {
        return;
    }

    function hideLabel() {
        label.visible(false);
        layer.batchDraw();
    }

    // Hide the label if there's no measurement.
    if (isNullOrUndefined(maybeMeasurement) || maybeMeasurement.name !== 'Length') {
        hideLabel();
        return;
    }

    const arrowDescriptor = ctx.cprObjects.lengthMeasurementArrow;
    if (!arrowDescriptor) {
        hideLabel();
        return;
    }

    // Get the measurement value from fields[1][1].
    const measurementValue = maybeMeasurement?.fields?.[1]?.[1];
    if (!Number.isFinite(measurementValue)) {
        hideLabel();
        return;
    }

    // Format the value.
    const formattedValue = measurementValue.toFixed(1);
    label.getText().text(formattedValue);

    // Get the arrow's start and end points in world coordinates.
    const points = arrowDescriptor.polyData.getPoints();
    if (!points || points.getNumberOfPoints() < 2) {
        hideLabel();
        return;
    }

    const startPoint = points.getPoint(0);
    const endPoint = points.getPoint(1);
    if (!startPoint || startPoint.length < 3 || !endPoint || endPoint.length < 3) {
        hideLabel();
        return;
    }

    // Calculate the midpoint of the arrow.
    const midPoint = [
        (startPoint[0] + endPoint[0]) / 2,
        (startPoint[1] + endPoint[1]) / 2,
        (startPoint[2] + endPoint[2]) / 2
    ];

    // Convert the arrow midpoint to display coordinates.
    const coord = vtkCoordinate.newInstance();
    coord.setCoordinateSystemToWorld();
    coord.setValue(midPoint[0], midPoint[1], midPoint[2]);
    const displayCoords = coord.getComputedDoubleDisplayValue(ctx.renderer);

    if (!displayCoords || displayCoords.length < 2) {
        hideLabel();
        return;
    }

    // Convert display coordinates to Konva canvas coordinates.
    // VTK display coordinates have origin at bottom-left, Konva has origin at top-left.
    const canvas = ctx.renderWindow.getViews()?.[0]?.getCanvas?.();
    if (!canvas || !canvas.height) {
        hideLabel();
        return;
    }

    // displayCoords is expressed in device pixels (canvas.width/height).
    // We need to flip Y in device pixel space, then convert to CSS pixels.
    const dpr = window.devicePixelRatio || 1;
    const konvaX = displayCoords[0] / dpr;
    const konvaY = (canvas.height - displayCoords[1]) / dpr;

    // Validate coordinates before positioning (can be NaN if container is hidden).
    if (!Number.isFinite(konvaX) || !Number.isFinite(konvaY) || !Number.isFinite(label.height())) {
        hideLabel();
        return;
    }

    // Position the label: left edge 20 pixels to the right, vertically centered.
    label.x(konvaX + 20);
    label.y(konvaY - (label.height() / 2));
    label.visible(true);

    layer.batchDraw();
}

// ============================================================================
// Stretched CPR Measurement Indicators
// ============================================================================

/**
 * @typedef {Object} StretchedMeasurementLineConfig
 * @property {string} key - Stable identifier for the line.
 * @property {number} unifiedIndex - Unified branch index from the measurement.
 * @property {[number, number, number]} color - RGB color for the line.
 * @property {'start'|'end'} role - Role of this line (start or end).
 */

/**
 * @typedef {Object} StretchedMeasurementLineDescriptor
 * @property {vtkActor} actor - VTK actor for the line.
 * @property {vtkMapper} mapper - VTK mapper for the line.
 * @property {vtkPolyData} polyData - VTK polydata for the line geometry.
 * @property {number} unifiedIndex - Unified branch index.
 * @property {string} key - Stable identifier.
 * @property {'start'|'end'} role - Role of this line.
 */

/**
 * @typedef {Object} StretchedMeasurementArrowDescriptor
 * @property {vtkActor} actor - VTK actor for the arrow.
 * @property {vtkMapper} mapper - VTK mapper for the arrow.
 * @property {vtkPolyData} polyData - VTK polydata for the arrow geometry.
 * @property {number|null} startUnifiedIndex - Unified index for the start point.
 * @property {number|null} endUnifiedIndex - Unified index for the end point.
 */

/**
 * @typedef {Object} StretchedMeasurementKonvaElements
 * @property {Konva.Stage} stage - Konva stage.
 * @property {Konva.Layer} layer - Konva layer.
 * @property {Konva.Label} label - Measurement value label.
 * @property {Konva.Line} topDashedLine - Top dashed horizontal line.
 * @property {Konva.Line} bottomDashedLine - Bottom dashed horizontal line.
 * @property {HTMLElement} konvaContainer - Container element for the Konva stage.
 */

/**
 * Get the branch selection type for converting unified index to master index.
 * @param {Union|null|undefined} selectedBranch - Selected branch (UnifiedDistanceBranch).
 * @returns {Object|null} - CenterlineBranchSelection value or null.
 */
function getBranchSelectionForConversion(selectedBranch) {
    if (isNullOrUndefined(selectedBranch)) {
        return null;
    }

    const branchName = getDUName(selectedBranch);

    switch (branchName) {
        case CenterlineDistanceBranchSelection.LEFT:
            return CenterlineBranchSelection.DISTANCE_LEFT;
        case CenterlineDistanceBranchSelection.RIGHT:
            return CenterlineBranchSelection.DISTANCE_RIGHT;
        case CenterlineDistanceBranchSelection.CUSP:
            return CenterlineBranchSelection.DISTANCE_CUSP;
        default:
            return null;
    }
}

/**
 * Extract measurement line configurations for stretched CPR view.
 * @param {Union|null|undefined} maybeMeasurement - Active stent measurement (Fable union).
 * @returns {StretchedMeasurementLineConfig[]} - Line configurations.
 */
function getStretchedMeasurementLineConfigs(maybeMeasurement) {
    if (isNullOrUndefined(maybeMeasurement)) {
        return [];
    }

    const measurementName = maybeMeasurement.name;

    if (diameterMeasurementCaseNames.includes(measurementName)) {
        const selection = maybeMeasurement?.fields?.[1]?.[0];
        const index = Number(selection?.index);

        if (!isValidCenterlineIndex(index)) {
            return [];
        }

        return [
            {
                key: `stretched-${measurementName}-${index}`,
                unifiedIndex: index,
                color: MEASUREMENT_LINE_COLORS.start,
                role: 'start',
            },
        ];
    }

    if (measurementName === 'Length') {
        const range = maybeMeasurement?.fields?.[1]?.[0];
        const firstIndex = Number(range?.firstIndex);
        const secondIndex = Number(range?.secondIndex);
        const configs = [];

        if (isValidCenterlineIndex(firstIndex)) {
            configs.push({
                key: `stretched-${measurementName}-start-${firstIndex}`,
                unifiedIndex: firstIndex,
                color: MEASUREMENT_LINE_COLORS.start,
                role: 'start',
            });
        }

        if (isValidCenterlineIndex(secondIndex)) {
            configs.push({
                key: `stretched-${measurementName}-end-${secondIndex}`,
                unifiedIndex: secondIndex,
                color: MEASUREMENT_LINE_COLORS.end,
                role: 'end',
            });
        }

        return configs;
    }

    return [];
}

/**
 * Extract length measurement offsets for stretched CPR view.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement union from Elmish state.
 * @returns {{startUnifiedIndex: number, endUnifiedIndex: number, value: number}|null}
 */
function getStretchedLengthMeasurementOffsets(maybeMeasurement) {
    if (isNullOrUndefined(maybeMeasurement) || maybeMeasurement.name !== 'Length') {
        return null;
    }

    const range = maybeMeasurement?.fields?.[1]?.[0];
    const firstIndex = Number(range?.firstIndex);
    const secondIndex = Number(range?.secondIndex);
    const measurementValue = maybeMeasurement?.fields?.[1]?.[1];

    if (!isValidCenterlineIndex(firstIndex) || !isValidCenterlineIndex(secondIndex)) {
        return null;
    }

    return {
        startUnifiedIndex: firstIndex,
        endUnifiedIndex: secondIndex,
        value: Number.isFinite(measurementValue) ? measurementValue : null,
    };
}

/**
 * Create a stretched CPR measurement indicator line actor.
 * @returns {StretchedMeasurementLineDescriptor}
 */
function createStretchedMeasurementLine() {
    const polyData = vtkPolyData.newInstance();
    const points = vtkPoints.newInstance();
    points.setNumberOfPoints(2);
    polyData.setPoints(points);

    const lines = new Uint16Array([2, 0, 1]);
    polyData.getLines().setData(lines);

    const mapper = vtkMapper.newInstance();
    mapper.setInputData(polyData);

    const actor = vtkActor.newInstance();
    actor.setMapper(mapper);
    actor.getProperty().setLineWidth(MEASUREMENT_LINE_WIDTH);
    actor.getProperty().setLighting(false);

    return {
        actor,
        mapper,
        polyData,
        unifiedIndex: null,
        key: null,
        role: null,
    };
}

/**
 * Compute world position and normal for a centerline index on the stretched CPR.
 * @param {number} centerlineIndex - Index into the oriented centerline.
 * @param {vtkPolyLine} orientedCenterline - Oriented centerline from the mapper.
 * @param {CPRParams} params - CPR parameters.
 * @returns {{worldPosition: number[], normal: number[]}|null}
 */
function getCenterlineWorldPositionAndNormal(centerlineIndex, orientedCenterline, params) {
    const ptsArray = orientedCenterline.getPoints().getData();
    const nPts = orientedCenterline.getPoints().getNumberOfPoints();

    if (
        centerlineIndex < 0 ||
        centerlineIndex >= nPts ||
        !ptsArray
    ) {
        return null;
    }

    // World position of this centerline point.
    const worldPosition = [
        ptsArray[3 * centerlineIndex],
        ptsArray[3 * centerlineIndex + 1],
        ptsArray[3 * centerlineIndex + 2],
    ];

    // Get tangent direction from orientations if available.
    const orientations = params.orientations;
    let tangent = [0, 1, 0]; // Default tangent.

    if (orientations && orientations.length >= (centerlineIndex + 1) * 16) {
        // The orientation is stored as a mat4; columns are [T, B, N, P].
        // T (tangent) is column 0.
        const offset = centerlineIndex * 16;
        tangent = [
            orientations[offset],
            orientations[offset + 1],
            orientations[offset + 2],
        ];
        vec3.normalize(tangent, tangent);
    } else {
        // Fallback: compute tangent from neighboring points.
        if (centerlineIndex > 0 && centerlineIndex < nPts - 1) {
            const prev = [
                ptsArray[3 * (centerlineIndex - 1)],
                ptsArray[3 * (centerlineIndex - 1) + 1],
                ptsArray[3 * (centerlineIndex - 1) + 2],
            ];
            const next = [
                ptsArray[3 * (centerlineIndex + 1)],
                ptsArray[3 * (centerlineIndex + 1) + 1],
                ptsArray[3 * (centerlineIndex + 1) + 2],
            ];
            vec3.subtract(tangent, next, prev);
            vec3.normalize(tangent, tangent);
        }
    }

    // Compute a normal perpendicular to the tangent in the CPR plane.
    // Use the tangent direction from params to define the cross-section orientation.
    const cprTangent = params.tangentDirection || [1, 0, 0];
    let normal = vec3.cross([], tangent, cprTangent);

    if (vec3.len(normal) < 1e-5) {
        // Fallback if tangent is parallel to cprTangent.
        normal = vec3.cross([], tangent, [0, 0, 1]);
        if (vec3.len(normal) < 1e-5) {
            normal = vec3.cross([], tangent, [0, 1, 0]);
        }
    }
    vec3.normalize(normal, normal);

    return { worldPosition, normal };
}

/**
 * Update a stretched measurement indicator line geometry.
 * @param {StretchedMeasurementLineDescriptor} lineDescriptor - Line descriptor to update.
 * @param {number} centerlineIndex - Index into the oriented centerline.
 * @param {vtkPolyLine} orientedCenterline - Oriented centerline from the mapper.
 * @param {CPRParams} params - CPR parameters.
 * @param {mat4} actorMatrix - Actor user matrix for world-to-CPR transform.
 * @param {CPRFrame} currentCPRFrame - Current CPR frame data.
 * @returns {boolean} - True if the geometry was updated.
 */
function updateStretchedMeasurementLine(
    lineDescriptor,
    centerlineIndex,
    orientedCenterline,
    params,
    actorMatrix,
    currentCPRFrame,
) {
    if (!lineDescriptor || !orientedCenterline || !params || !actorMatrix) {
        return false;
    }

    // Compute CPR coordinates for this centerline index.
    const baseCPR = computeCPRCoordsForCenterlineIndex(
        centerlineIndex,
        orientedCenterline,
        params
    );

    if (!baseCPR) {
        return false;
    }

    // The indicator line extends 90% of the CPR width, centered horizontally.
    // In CPR space, X is across the width, Y is along the height (distance along centerline).
    const lineWidth = params.width * MEASUREMENT_LINE_VIEWPORT_RATIO;
    const halfLineWidth = lineWidth / 2;
    const centerX = params.width / 2;

    // Create start and end points in CPR space (horizontal line centered in view).
    const cprStart = [centerX - halfLineWidth, baseCPR[1], 0];
    const cprEnd = [centerX + halfLineWidth, baseCPR[1], 0];

    // Transform from CPR space to world space.
    const worldStart = vec3.transformMat4([], cprStart, actorMatrix);
    const worldEnd = vec3.transformMat4([], cprEnd, actorMatrix);

    // Update polydata points.
    const points = lineDescriptor.polyData.getPoints();
    if (!points) {
        return false;
    }

    if (points.getNumberOfPoints() < 2) {
        points.setNumberOfPoints(2);
    }

    points.setPoint(0, worldStart[0], worldStart[1], worldStart[2]);
    points.setPoint(1, worldEnd[0], worldEnd[1], worldEnd[2]);
    points.modified();

    lineDescriptor.polyData.modified();

    return true;
}

/**
 * Dispose of stretched measurement line descriptors.
 * @param {vtkRenderer|null|undefined} renderer - Renderer hosting the lines.
 * @param {StretchedMeasurementLineDescriptor[]} [lineDescriptors=[]] - Line descriptors to dispose.
 */
function disposeStretchedMeasurementLines(renderer, lineDescriptors = []) {
    if (!Array.isArray(lineDescriptors) || !lineDescriptors.length) {
        return;
    }

    lineDescriptors.forEach((descriptor) => {
        if (!descriptor) {
            return;
        }

        if (renderer && descriptor.actor) {
            renderer.removeActor(descriptor.actor);
        }

        descriptor.actor?.delete?.();
        descriptor.mapper?.delete?.();
        descriptor.polyData?.delete?.();
    });
}

/**
 * Refresh stretched CPR measurement indicator lines.
 * @param {Object} ctx - Visualization context.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement.
 * @param {Union|null|undefined} selectedBranch - Selected branch.
 * @param {CPRFrame} currentCPRFrame - Current CPR frame data.
 */
function refreshStretchedMeasurementLines(ctx, maybeMeasurement, selectedBranch, currentCPRFrame) {
    if (!ctx || !ctx.cprObjects?.mapper || !ctx.renderer || !ctx.renderWindow) {
        return;
    }

    const { cprObjects, renderer, renderWindow } = ctx;
    const { mapper, actor, wallCenterline } = cprObjects;

    // Dispose existing lines.
    const existingLines = Array.isArray(cprObjects.stretchedMeasurementLines)
        ? cprObjects.stretchedMeasurementLines
        : [];
    const hadLines = existingLines.length > 0;

    disposeStretchedMeasurementLines(renderer, existingLines);
    cprObjects.stretchedMeasurementLines = [];

    // Get line configurations.
    const lineConfigs = getStretchedMeasurementLineConfigs(maybeMeasurement);

    if (!lineConfigs.length) {
        if (hadLines) {
            renderWindow.render();
        }
        return;
    }

    // Get branch selection for index conversion.
    const branchSelection = getBranchSelectionForConversion(selectedBranch);
    if (!branchSelection || !wallCenterline) {
        if (hadLines) {
            renderWindow.render();
        }
        return;
    }

    const centerlineFieldData = wallCenterline.getFieldData();

    // Get oriented centerline and CPR params.
    const orientedCenterline = mapper.getOrientedCenterline();
    if (!orientedCenterline) {
        return;
    }

    const actorMatrix = actor.getUserMatrix();
    if (!actorMatrix) {
        return;
    }

    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const orientations = orientedCenterline.getOrientations() || null;

    const params = {
        width: currentCPRFrame.width,
        height: currentCPRFrame.height,
        tangentDirection: mapper.getTangentDirection(),
        centerPoint: mapper.getCenterPoint(),
        distancesToFirstPoint,
        orientations,
    };

    // Create and update lines.
    const descriptors = lineConfigs.map((config) => {
        // Convert unified index to master index, then to centerline index.
        const masterIndex = convertUnifiedBranchIndexToMasterIndex(
            config.unifiedIndex,
            branchSelection,
            centerlineFieldData
        );

        // Find the centerline index that corresponds to this master index.
        // The branchOriginalIndices maps branch polydata index -> master index.
        const branchName = getDUName(selectedBranch);
        const branchIndices = cprObjects.branchOriginalIndices?.[branchName];

        let centerlineIndex = -1;
        if (branchIndices) {
            centerlineIndex = branchIndices.indexOf(masterIndex);
        }

        if (centerlineIndex < 0) {
            return null;
        }

        const lineDescriptor = createStretchedMeasurementLine();
        lineDescriptor.unifiedIndex = config.unifiedIndex;
        lineDescriptor.key = config.key;
        lineDescriptor.role = config.role;

        const [r, g, b] = config.color;
        lineDescriptor.actor.getProperty().setColor(r, g, b);

        const updated = updateStretchedMeasurementLine(
            lineDescriptor,
            centerlineIndex,
            orientedCenterline,
            params,
            actorMatrix,
            currentCPRFrame
        );

        if (!updated) {
            lineDescriptor.actor?.delete?.();
            lineDescriptor.mapper?.delete?.();
            lineDescriptor.polyData?.delete?.();
            return null;
        }

        return lineDescriptor;
    }).filter(Boolean);

    if (!descriptors.length) {
        if (hadLines) {
            renderWindow.render();
        }
        return;
    }

    descriptors.forEach((descriptor) => renderer.addActor(descriptor.actor));
    cprObjects.stretchedMeasurementLines = descriptors;

    renderWindow.render();
}

/**
 * Create the arrow descriptor for stretched length measurements.
 * @returns {StretchedMeasurementArrowDescriptor}
 */
function createStretchedMeasurementArrow() {
    const polyData = vtkPolyData.newInstance();
    const points = vtkPoints.newInstance();
    points.setNumberOfPoints(6);
    polyData.setPoints(points);

    // Five independent line segments: shaft plus four arrowhead edges.
    const lines = new Uint16Array([
        2, 0, 1,
        2, 1, 2,
        2, 1, 3,
        2, 0, 4,
        2, 0, 5,
    ]);
    polyData.getLines().setData(lines);

    const mapper = vtkMapper.newInstance();
    mapper.setInputData(polyData);

    const actor = vtkActor.newInstance();
    actor.setMapper(mapper);
    actor.getProperty().setColor(...MEASUREMENT_ARROW_COLOR); // Yellow.
    actor.getProperty().setLineWidth(MEASUREMENT_ARROW_LINE_WIDTH);
    actor.getProperty().setLighting(false);

    return {
        actor,
        mapper,
        polyData,
        startUnifiedIndex: null,
        endUnifiedIndex: null,
    };
}

/**
 * Dispose of stretched measurement arrow.
 * @param {vtkRenderer|null|undefined} renderer - Renderer hosting the arrow.
 * @param {StretchedMeasurementArrowDescriptor|null|undefined} arrowDescriptor - Arrow descriptor to dispose.
 */
function disposeStretchedMeasurementArrow(renderer, arrowDescriptor) {
    if (!arrowDescriptor) {
        return;
    }

    if (renderer && arrowDescriptor.actor) {
        renderer.removeActor(arrowDescriptor.actor);
    }

    arrowDescriptor.actor?.delete?.();
    arrowDescriptor.mapper?.delete?.();
    arrowDescriptor.polyData?.delete?.();
}

/**
 * Update stretched measurement arrow geometry.
 * @param {StretchedMeasurementArrowDescriptor} arrowDescriptor - Arrow descriptor.
 * @param {number} startCenterlineIndex - Start centerline index.
 * @param {number} endCenterlineIndex - End centerline index.
 * @param {vtkPolyLine} orientedCenterline - Oriented centerline.
 * @param {CPRParams} params - CPR parameters.
 * @param {mat4} actorMatrix - Actor user matrix.
 * @param {CPRFrame} currentCPRFrame - Current CPR frame.
 * @param {vtkRenderer} renderer - Renderer for pixel-to-world calculations.
 * @param {vtkRenderWindow} renderWindow - Render window for viewport calculations.
 * @returns {boolean} - True if arrow was updated.
 */
function updateStretchedMeasurementArrow(
    arrowDescriptor,
    startCenterlineIndex,
    endCenterlineIndex,
    orientedCenterline,
    params,
    actorMatrix,
    currentCPRFrame,
    renderer,
    renderWindow,
) {
    if (!arrowDescriptor || !orientedCenterline || !params || !actorMatrix) {
        return false;
    }

    // Get CPR coordinates for both endpoints.
    const startCPR = computeCPRCoordsForCenterlineIndex(
        startCenterlineIndex,
        orientedCenterline,
        params
    );

    const endCPR = computeCPRCoordsForCenterlineIndex(
        endCenterlineIndex,
        orientedCenterline,
        params
    );

    if (!startCPR || !endCPR) {
        return false;
    }

    // Calculate arrow X position based on visible viewport width, not CPR image width.
    // Use the camera's parallel scale and aspect ratio to determine the visible world width.
    let arrowX = params.width * MEASUREMENT_ARROW_VIEWPORT_RATIO; // Fallback to CPR width.

    if (renderer && renderWindow) {
        const view = renderWindow.getViews()?.[0];
        const canvas = view?.getCanvas?.();
        if (canvas?.width > 0 && canvas?.height > 0) {
            const camera = renderer.getActiveCamera();
            const parallelScale = camera.getParallelScale();
            const aspect = canvas.width / canvas.height;
            const visibleWorldWidth = 2 * parallelScale * aspect;

            // The CPR image is centered in the viewport.
            // Calculate the arrow position as a percentage of the visible viewport width.
            const centerX = params.width / 2;
            const offsetFromCenter = (MEASUREMENT_ARROW_VIEWPORT_RATIO - 0.5) * visibleWorldWidth;
            arrowX = centerX + offsetFromCenter;
        }
    }

    // Determine which point is higher (lower Y in CPR space = higher on screen).
    const lowY = Math.min(startCPR[1], endCPR[1]);
    const highY = Math.max(startCPR[1], endCPR[1]);

    // Arrow goes from lowY to highY at arrowX.
    const startCPRArrow = [arrowX, lowY, 0];
    const endCPRArrow = [arrowX, highY, 0];

    // Transform to world space.
    const worldStart = vec3.transformMat4([], startCPRArrow, actorMatrix);
    const worldEnd = vec3.transformMat4([], endCPRArrow, actorMatrix);

    // Compute arrow direction and length.
    const arrowDir = vec3.sub([], worldEnd, worldStart);
    const arrowLength = vec3.len(arrowDir);

    if (!Number.isFinite(arrowLength) || arrowLength < 1e-5) {
        return false;
    }

    vec3.normalize(arrowDir, arrowDir);

    // Compute perpendicular direction for arrowheads.
    // In CPR space, the perpendicular is along X.
    const perpCPR = [1, 0, 0];
    const worldZero = vec3.transformMat4([], [0, 0, 0], actorMatrix);
    const worldPerpUnit = vec3.transformMat4([], perpCPR, actorMatrix);
    vec3.sub(worldPerpUnit, worldPerpUnit, worldZero);
    vec3.normalize(worldPerpUnit, worldPerpUnit);

    // Compute arrowhead dimensions.
    let headLength = renderer
        ? getWorldLengthForPixelDelta(renderer, worldStart, arrowDir, MEASUREMENT_ARROW_HEAD_LENGTH_PX)
        : MEASUREMENT_ARROW_HEAD_MIN_LENGTH;
    headLength = clamp(headLength || MEASUREMENT_ARROW_HEAD_MIN_LENGTH, MEASUREMENT_ARROW_HEAD_MIN_LENGTH, arrowLength * 0.4);

    let headWidth = renderer
        ? getWorldLengthForPixelDelta(renderer, worldStart, worldPerpUnit, MEASUREMENT_ARROW_HEAD_WIDTH_PX)
        : MEASUREMENT_ARROW_HEAD_MIN_WIDTH;
    headWidth = clamp(headWidth || MEASUREMENT_ARROW_HEAD_MIN_WIDTH, MEASUREMENT_ARROW_HEAD_MIN_WIDTH, headLength * 2);

    // Build arrow geometry.
    const startInset = vec3.scaleAndAdd([], worldStart, arrowDir, headLength);
    const startLeft = vec3.scaleAndAdd([], startInset, worldPerpUnit, headWidth);
    const startRight = vec3.scaleAndAdd([], startInset, worldPerpUnit, -headWidth);

    const endInset = vec3.scaleAndAdd([], worldEnd, arrowDir, -headLength);
    const endLeft = vec3.scaleAndAdd([], endInset, worldPerpUnit, headWidth);
    const endRight = vec3.scaleAndAdd([], endInset, worldPerpUnit, -headWidth);

    // Update polydata points.
    const points = arrowDescriptor.polyData.getPoints();
    if (!points) {
        return false;
    }

    if (points.getNumberOfPoints() < 6) {
        points.setNumberOfPoints(6);
    }

    points.setPoint(0, worldStart[0], worldStart[1], worldStart[2]);
    points.setPoint(1, worldEnd[0], worldEnd[1], worldEnd[2]);
    points.setPoint(2, endLeft[0], endLeft[1], endLeft[2]);
    points.setPoint(3, endRight[0], endRight[1], endRight[2]);
    points.setPoint(4, startLeft[0], startLeft[1], startLeft[2]);
    points.setPoint(5, startRight[0], startRight[1], startRight[2]);
    points.modified();

    arrowDescriptor.polyData.modified();

    return true;
}

/**
 * Refresh stretched CPR length measurement arrow.
 * @param {Object} ctx - Visualization context.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement.
 * @param {Union|null|undefined} selectedBranch - Selected branch.
 * @param {CPRFrame} currentCPRFrame - Current CPR frame data.
 */
function refreshStretchedLengthMeasurementArrow(ctx, maybeMeasurement, selectedBranch, currentCPRFrame) {
    if (!ctx || !ctx.cprObjects?.mapper || !ctx.renderer) {
        return;
    }

    const { cprObjects, renderer, renderWindow } = ctx;
    const { mapper, actor, wallCenterline } = cprObjects;

    const offsets = getStretchedLengthMeasurementOffsets(maybeMeasurement);

    if (!offsets) {
        if (cprObjects.stretchedMeasurementArrow) {
            disposeStretchedMeasurementArrow(renderer, cprObjects.stretchedMeasurementArrow);
            cprObjects.stretchedMeasurementArrow = null;
            renderWindow?.render();
        }
        return;
    }

    // Get branch selection for index conversion.
    const branchSelection = getBranchSelectionForConversion(selectedBranch);
    if (!branchSelection || !wallCenterline) {
        if (cprObjects.stretchedMeasurementArrow) {
            disposeStretchedMeasurementArrow(renderer, cprObjects.stretchedMeasurementArrow);
            cprObjects.stretchedMeasurementArrow = null;
            renderWindow?.render();
        }
        return;
    }

    const centerlineFieldData = wallCenterline.getFieldData();

    // Convert unified indices to centerline indices.
    const startMasterIndex = convertUnifiedBranchIndexToMasterIndex(
        offsets.startUnifiedIndex,
        branchSelection,
        centerlineFieldData
    );
    const endMasterIndex = convertUnifiedBranchIndexToMasterIndex(
        offsets.endUnifiedIndex,
        branchSelection,
        centerlineFieldData
    );

    const branchName = getDUName(selectedBranch);
    const branchIndices = cprObjects.branchOriginalIndices?.[branchName];

    let startCenterlineIndex = -1;
    let endCenterlineIndex = -1;

    if (branchIndices) {
        startCenterlineIndex = branchIndices.indexOf(startMasterIndex);
        endCenterlineIndex = branchIndices.indexOf(endMasterIndex);
    }

    if (startCenterlineIndex < 0 || endCenterlineIndex < 0) {
        if (cprObjects.stretchedMeasurementArrow) {
            disposeStretchedMeasurementArrow(renderer, cprObjects.stretchedMeasurementArrow);
            cprObjects.stretchedMeasurementArrow = null;
            renderWindow?.render();
        }
        return;
    }

    // Get oriented centerline and CPR params.
    const orientedCenterline = mapper.getOrientedCenterline();
    if (!orientedCenterline) {
        return;
    }

    const actorMatrix = actor.getUserMatrix();
    if (!actorMatrix) {
        return;
    }

    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const orientations = orientedCenterline.getOrientations() || null;

    const params = {
        width: currentCPRFrame.width,
        height: currentCPRFrame.height,
        tangentDirection: mapper.getTangentDirection(),
        centerPoint: mapper.getCenterPoint(),
        distancesToFirstPoint,
        orientations,
    };

    // Create arrow if it doesn't exist.
    let arrowDescriptor = cprObjects.stretchedMeasurementArrow;
    let addedArrow = false;

    if (!arrowDescriptor) {
        arrowDescriptor = createStretchedMeasurementArrow();
        renderer.addActor(arrowDescriptor.actor);
        cprObjects.stretchedMeasurementArrow = arrowDescriptor;
        addedArrow = true;
    }

    arrowDescriptor.startUnifiedIndex = offsets.startUnifiedIndex;
    arrowDescriptor.endUnifiedIndex = offsets.endUnifiedIndex;

    const updated = updateStretchedMeasurementArrow(
        arrowDescriptor,
        startCenterlineIndex,
        endCenterlineIndex,
        orientedCenterline,
        params,
        actorMatrix,
        currentCPRFrame,
        renderer,
        renderWindow,
    );

    if ((addedArrow || updated) && renderWindow) {
        renderWindow.render();
    }
}

/**
 * Initialize Konva elements for stretched CPR measurement display.
 * @param {HTMLElement} container - Container element for the Konva stage.
 * @param {string} viewportId - Unique identifier for the viewport.
 * @returns {StretchedMeasurementKonvaElements|null}
 */
function initializeStretchedMeasurementKonva(container, viewportId) {
    if (!container) {
        return null;
    }

    // Create a div container for Konva that overlays the VTK canvas.
    const konvaContainer = document.createElement('div');
    konvaContainer.id = `stretchedMeasurementKonvaContainer-${viewportId}`;
    konvaContainer.style.position = 'absolute';
    konvaContainer.style.top = '0';
    konvaContainer.style.left = '0';
    konvaContainer.style.width = '100%';
    konvaContainer.style.height = '100%';
    konvaContainer.style.pointerEvents = 'none';
    konvaContainer.style.zIndex = '1';
    container.appendChild(konvaContainer);

    const stage = new Konva.Stage({
        container: konvaContainer,
        width: container.offsetWidth,
        height: container.offsetHeight,
    });

    const layer = new Konva.Layer();
    stage.add(layer);

    // Create the measurement value label.
    const label = new Konva.Label({
        visible: false,
    });

    const tag = new Konva.Tag({
        fill: 'yellow',
        cornerRadius: 3,
        pointerDirection: 'none',
    });

    const text = new Konva.Text({
        text: '',
        fontSize: 24,
        fontFamily: 'Open Sans',
        fonstStyle: 'Bold',
        fill: 'black',
        padding: 5,
    });

    label.add(tag);
    label.add(text);
    layer.add(label);

    return { stage, layer, label, konvaContainer };
}

/**
 * Update Konva elements for stretched CPR measurement display.
 * @param {Object} ctx - Visualization context.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement.
 * @param {Union|null|undefined} selectedBranch - Selected branch.
 * @param {CPRFrame} currentCPRFrame - Current CPR frame data.
 */
function updateStretchedMeasurementKonva(ctx, maybeMeasurement, selectedBranch, currentCPRFrame) {
    if (!ctx || !ctx.stretchedKonvaElements || !ctx.cprObjects?.mapper || !ctx.renderer || !ctx.renderWindow) {
        return;
    }

    const { stage, layer, label } = ctx.stretchedKonvaElements;
    const { cprObjects, renderer, renderWindow } = ctx;
    const { mapper, actor, wallCenterline } = cprObjects;

    if (!stage || !layer || !label) {
        return;
    }

    function hideElements() {
        label.visible(false);
        layer.batchDraw();
    }

    // Only show for length measurements.
    if (isNullOrUndefined(maybeMeasurement) || maybeMeasurement.name !== 'Length') {
        hideElements();
        return;
    }

    const offsets = getStretchedLengthMeasurementOffsets(maybeMeasurement);
    if (!offsets || !Number.isFinite(offsets.value)) {
        hideElements();
        return;
    }

    // Get branch selection for index conversion.
    const branchSelection = getBranchSelectionForConversion(selectedBranch);
    if (!branchSelection || !wallCenterline) {
        hideElements();
        return;
    }

    const centerlineFieldData = wallCenterline.getFieldData();

    // Convert unified indices to centerline indices.
    const startMasterIndex = convertUnifiedBranchIndexToMasterIndex(
        offsets.startUnifiedIndex,
        branchSelection,
        centerlineFieldData
    );
    const endMasterIndex = convertUnifiedBranchIndexToMasterIndex(
        offsets.endUnifiedIndex,
        branchSelection,
        centerlineFieldData
    );

    const branchName = getDUName(selectedBranch);
    const branchIndices = cprObjects.branchOriginalIndices?.[branchName];

    let startCenterlineIndex = -1;
    let endCenterlineIndex = -1;

    if (branchIndices) {
        startCenterlineIndex = branchIndices.indexOf(startMasterIndex);
        endCenterlineIndex = branchIndices.indexOf(endMasterIndex);
    }

    if (startCenterlineIndex < 0 || endCenterlineIndex < 0) {
        hideElements();
        return;
    }

    // Get oriented centerline and CPR params.
    const orientedCenterline = mapper.getOrientedCenterline();
    if (!orientedCenterline) {
        hideElements();
        return;
    }

    const actorMatrix = actor.getUserMatrix();
    if (!actorMatrix) {
        hideElements();
        return;
    }

    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const orientations = orientedCenterline.getOrientations() || null;

    const params = {
        width: currentCPRFrame.width,
        height: currentCPRFrame.height,
        tangentDirection: mapper.getTangentDirection(),
        centerPoint: mapper.getCenterPoint(),
        distancesToFirstPoint,
        orientations,
    };

    // Get CPR coordinates for both endpoints.
    const startCPR = computeCPRCoordsForCenterlineIndex(
        startCenterlineIndex,
        orientedCenterline,
        params
    );

    const endCPR = computeCPRCoordsForCenterlineIndex(
        endCenterlineIndex,
        orientedCenterline,
        params
    );

    if (!startCPR || !endCPR) {
        hideElements();
        return;
    }

    // Calculate arrow X position based on visible viewport width, not CPR image width.
    // Use the camera's parallel scale and aspect ratio to determine the visible world width.
    let arrowX = params.width * MEASUREMENT_ARROW_VIEWPORT_RATIO; // Fallback to CPR width.

    const view = renderWindow.getViews()?.[0];
    const canvas = view?.getCanvas?.();
    if (canvas?.width > 0 && canvas?.height > 0) {
        const camera = renderer.getActiveCamera();
        const parallelScale = camera.getParallelScale();
        const aspect = canvas.width / canvas.height;
        const visibleWorldWidth = 2 * parallelScale * aspect;

        // The CPR image is centered in the viewport.
        // Calculate the arrow position as a percentage of the visible viewport width.
        const centerX = params.width / 2;
        const offsetFromCenter = (MEASUREMENT_ARROW_VIEWPORT_RATIO - 0.5) * visibleWorldWidth;
        arrowX = centerX + offsetFromCenter;
    }

    const arrowCPRTop = [arrowX, Math.min(startCPR[1], endCPR[1]), 0];
    const arrowCPRBottom = [arrowX, Math.max(startCPR[1], endCPR[1]), 0];

    // Transform to world space.
    const worldArrowTop = vec3.transformMat4([], arrowCPRTop, actorMatrix);
    const worldArrowBottom = vec3.transformMat4([], arrowCPRBottom, actorMatrix);

    // Convert world coordinates to display coordinates.
    if (!canvas || !canvas.height || !canvas.width) {
        hideElements();
        return;
    }

    const coord = vtkCoordinate.newInstance();
    coord.setCoordinateSystemToWorld();

    const dpr = window.devicePixelRatio || 1;

    function worldToKonva(worldPoint) {
        coord.setValue(worldPoint[0], worldPoint[1], worldPoint[2]);
        const displayCoords = coord.getComputedDoubleDisplayValue(renderer);
        if (!displayCoords || displayCoords.length < 2) {
            return null;
        }
        return {
            x: displayCoords[0] / dpr,
            y: (canvas.height - displayCoords[1]) / dpr,
        };
    }

    const konvaArrowTop = worldToKonva(worldArrowTop);
    const konvaArrowBottom = worldToKonva(worldArrowBottom);

    if (!konvaArrowTop || !konvaArrowBottom) {
        hideElements();
        return;
    }

    // Update label with measurement value.
    const formattedValue = offsets.value.toFixed(1);
    label.getText().text(formattedValue);

    // Position label at midpoint of arrow, offset to the right.
    const labelX = (konvaArrowTop.x + konvaArrowBottom.x) / 2 + 20;
    const labelY = (konvaArrowTop.y + konvaArrowBottom.y) / 2 - label.height() / 2;

    if (!Number.isFinite(labelX) || !Number.isFinite(labelY)) {
        hideElements();
        return;
    }

    label.x(labelX);
    label.y(labelY);
    label.visible(true);

    layer.batchDraw();
}

/**
 * Resize the stretched measurement Konva stage.
 * @param {StretchedMeasurementKonvaElements|null|undefined} konvaElements - Konva elements.
 * @param {number} width - New width.
 * @param {number} height - New height.
 */
function resizeStretchedMeasurementKonva(konvaElements, width, height) {
    if (!konvaElements || !konvaElements.stage) {
        return;
    }

    konvaElements.stage.width(width);
    konvaElements.stage.height(height);
    konvaElements.layer?.batchDraw();
}

/**
 * Dispose of stretched measurement Konva elements.
 * @param {StretchedMeasurementKonvaElements|null|undefined} konvaElements - Konva elements to dispose.
 */
function disposeStretchedMeasurementKonva(konvaElements) {
    if (!konvaElements) {
        return;
    }

    konvaElements.stage?.destroy();
    konvaElements.konvaContainer?.remove();
}

// ============================================================================
// End of Stretched CPR Measurement Indicators
// ============================================================================

/**
 * Spline world control point storage.
 * @typedef {Object} SplineWorldControlPointData
 * @property {Array.<[number, number, number]>} worldControlPoints
 *   Array of sampled control points in world coordinates. Each entry is a 3-tuple
 *   [x, y, z].
 * @property {number[]} worldControlPointIndices
 *   Array of master indices on the centerline polydata corresponding to each control point.
 * @property {number} [lastTrunkMasterIndex]
 *   Master index of the last point on the trunk portion of the centerline (unrelated to control points).
 */

/**
 * Branch control data storage for CPR visualization.
 * @typedef {Object} BranchControlData
 * @property {SplineWorldControlPointData} splineWorldControlPointData
 *   Sampled control points and their indices from the centerline polydata.
 * @property {number[]} controlRadialOffsetR
 *   Array of radial offsets for each control point (CPR-space X offset at edit-time angle).
 * @property {number[]} controlOffsetYCPR
 *   Array of Y offsets for each control point in CPR space.
 * @property {(number[]|null)[]} controlWorldOffsets
 *   Array of world-space offset vectors [dx, dy, dz] per control point, or null if unedited.
 *   These are fixed in world-space and independent of the viewing angle.
 */

/**
 * @typedef {Object} CommittedChanges
 * @property {BranchControlData} branchControlData - The committed control point data for this branch.
 */

/**
 * Build the sampled original control points from a vtkPolyData centerline.
 * We sample approx every 20th point, always keeping the first and last.
 * @param {vtkPolyData} polyData - Centerline polydata.
 * @returns {BranchControlData} Branch control data containing sampled control points and initialized offset arrays.
 */
function buildOriginalControlPointsFromPolyData(
    polyData,
) {
    if (!polyData) {
        throw new Error('buildOriginalControlPointsFromPolyData - polyData is null or undefined');
    }

    const pts = polyData.getPoints().getData();
    const n = polyData.getPoints().getNumberOfPoints();
    const lastTrunkMasterIndex = polyData.getFieldData().getArrayByName('LastTrunkMasterIndex').getData()[0];

    if (n <= 0) {
        throw new Error('buildOriginalControlPointsFromPolyData - polyData has no points');
    }

    const indices = [];
    const step = 20; // every 20th point

    for (let i = 0; i < n; i += step) {
        indices.push(i);
    }

    if (indices.length === 0 || indices[indices.length - 1] !== n - 1) {
        indices.push(n - 1);
    }

    const worldControlPoints = indices.map((idx) => [
        pts[3 * idx],
        pts[3 * idx + 1],
        pts[3 * idx + 2],
    ]);

    const worldControlPointIndices = indices.slice();

    return {
        splineWorldControlPointData: {
            worldControlPoints,
            worldControlPointIndices,
            lastTrunkMasterIndex: lastTrunkMasterIndex
        },
        controlRadialOffsetR: new Array(worldControlPointIndices.length).fill(null),
        controlOffsetYCPR: new Array(worldControlPointIndices.length).fill(null),
        controlWorldOffsets: new Array(worldControlPointIndices.length).fill(null),
    };
}

/**
 * If the offset arrays do not exist or are not the correct length, initialize them to zeroes.
 * @param {BranchControlData} branchControlData - Branch control data containing offset arrays.
 * @param {number} targetLength - Target length for the arrays.
 */
function ensureOffsetArraysExist(
    branchControlData,
    targetLength) {
    if (!branchControlData.controlRadialOffsetR || branchControlData.controlRadialOffsetR.length !== targetLength) {
        branchControlData.controlRadialOffsetR = new Array(targetLength).fill(0.0);
    }
    if (!branchControlData.controlOffsetYCPR || branchControlData.controlOffsetYCPR.length !== targetLength) {
        branchControlData.controlOffsetYCPR = new Array(targetLength).fill(0.0);
    }
    if (!branchControlData.controlWorldOffsets || branchControlData.controlWorldOffsets.length !== targetLength) {
        branchControlData.controlWorldOffsets = new Array(targetLength).fill(null);
    }
}

/**
 * @typedef {Object} CPRParams
 * @property {number} width - CPR frame width.
 * @property {number} height - CPR frame height.
 * @property {import('gl-matrix').vec3} tangentDirection - Tangent direction vector.
 * @property {import('gl-matrix').vec3} centerPoint - Center point of the CPR frame.
 * @property {number[]} distancesToFirstPoint - Distances along centerline to first point.
 * @property {import('gl-matrix').quat|null} orientations - Optional packed mat4 orientations from the centerline.
 */

/** @typedef {Object} CPRFrame
 * @property {import('gl-matrix').vec3} worldTangent
 * @property {import('gl-matrix').vec3} worldNormal
 * @property {import('gl-matrix').vec3} worldBitangent
 * @property {import('gl-matrix').vec3} worldWidgetCenter
 * @property {number} distance
 * @property {number} height
 * @property {number} width
 * @property {import('gl-matrix').vec3} worldActorTranslation
 */

/**
 * Build CPR parameters object from the mapper and current CPR frame.
 * @param {vtkImageCPRMapper} mapper - CPR image mapper.
 * @param {vtkPolyLine} orientedCenterline - Oriented centerline from the mapper.
 * @param {CPRFrame} currentCPRFrame - Current CPR frame data.
 * @returns {CPRParams}
 */
function buildCPRParams(mapper, orientedCenterline, currentCPRFrame) {
    const { width, height } = currentCPRFrame;
    const tangentDirection = mapper.getTangentDirection();
    const centerPoint = mapper.getCenterPoint();
    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const orientations = orientedCenterline.getOrientations() || null;

    return {
        width,
        height,
        tangentDirection,
        centerPoint,
        distancesToFirstPoint,
        orientations,
    };
}

/**
 * @typedef {Object} CPRUpdateContext
 * @property {vtkRenderWindow} renderWindow
 * @property {vtkRenderer} renderer
 * @property {vtkRenderWindowInteractor} interactor
 * @property {vtkImageCPRMapper} mapper
 * @property {vtkImageSlice} actor
 * @property {vtkResliceCursorWidget} resliceCursorWidget
 * @property {Object} resliceCursorWidgetState
 * @property {vtkCPRManipulator} cprManipulator
 * @property {vtkPlaneManipulator} planeManipulator
 * @property {vtkSplineWidget} splineWidget
 * @property {Object} cprObjects - Reference to cprObjects for state tracking.
 */

/**
 * Create a CPR update context object from the main context.
 * @param {React.RefObject<Object>} context
 * @return {CPRUpdateContext}
 */
function createCPRUpdateContext(context) {
    return {
        renderWindow: context.renderWindow,
        renderer: context.renderer,
        interactor: context.interactor,
        mapper: context.cprObjects.mapper,
        actor: context.cprObjects.actor,
        resliceCursorWidget: context.cprObjects.resliceCursorWidget,
        resliceCursorWidgetState: context.cprObjects.resliceCursorWidgetState,
        cprManipulator: context.cprObjects.cprManipulator,
        planeManipulator: context.cprObjects.planeManipulator,
        splineWidget: context.cprObjects.splineWidget,
        cprObjects: context.cprObjects
    };
}

/**
 * Updates the control point offsets based on the current positions of the spline widget handles.
 * @param {BranchControlData} branchControlData - Branch control data containing spline world control points and offset arrays.
 * @param {React.RefObject<CPRFrame>} currentCPRFrameRef - Ref to current CPR frame data.
 * @param {vtkImageCPRMapper} mapper - CPR image mapper.
 * @param {vtkImageSlice} actor - Image slice actor.
 * @param {vtkSplineWidget} splineWidget - Spline widget.
 * @param {Object|null} [movedHandle=null] - The specific handle that was moved by the user.
 *   When provided, only the world offset for the matching handle is recomputed.
 */
function updateOffsetsFromHandles(
    branchControlData,
    currentCPRFrameRef,
    mapper,
    actor,
    splineWidget,
    movedHandle = null,
) {
    const { worldControlPointIndices } = branchControlData.splineWorldControlPointData;

    if (
        !worldControlPointIndices ||
        !worldControlPointIndices.length
    ) {
        return;
    }

    if (!currentCPRFrameRef.current) {
        return;
    }

    const orientedCenterline = mapper.getOrientedCenterline();

    if (!orientedCenterline) {
        return;
    }

    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const pts = orientedCenterline.getPoints();
    const nPts = pts.getNumberOfPoints();
    if (!distancesToFirstPoint || !pts || !nPts) {
        return;
    }

    const widgetState = splineWidget.getWidgetState();
    if (!widgetState) {
        return;
    }

    const handles = widgetState.getHandleList();
    if (!handles || !handles.length) {
        return;
    }

    // Ensure arrays exist / aligned
    ensureOffsetArraysExist(
        branchControlData,
        worldControlPointIndices.length,
    );

    const actorMatrix = actor.getUserMatrix();
    if (!actorMatrix) {
        return;
    }

    const invActorMatrix = mat4.create();
    if (!mat4.invert(invActorMatrix, actorMatrix)) {
        return;
    }

    const params = buildCPRParams(mapper, orientedCenterline, currentCPRFrameRef.current);
    log('updateOffsetsFromHandles - params:', params);

    const worldPos = [0, 0, 0];
    const cprPos = [0, 0, 0];

    const handleList = handles;
    for (let k = 0; k < handleList.length; ++k) {
        const handle = handleList[k];
        const clIndex = worldControlPointIndices[k];

        if (
            clIndex == null ||
            clIndex < 0 ||
            clIndex >= nPts ||
            distancesToFirstPoint[clIndex] === undefined
        ) {
            continue;
        }

        const baseCPR = computeCPRCoordsForCenterlineIndex(
            clIndex,
            orientedCenterline,
            params
        );
        if (!baseCPR) {
            continue;
        }
        log(`updateOffsetsFromHandles - baseCPR[${k}]:`, baseCPR);

        const origin = handle.getOrigin();
        log(`updateOffsetsFromHandles - handle origin[${k}]:`, origin);
        worldPos[0] = origin[0];
        worldPos[1] = origin[1];
        worldPos[2] = origin[2];

        vec3.transformMat4(cprPos, worldPos, invActorMatrix);
        log(`updateOffsetsFromHandles - cprPos[${k}]:`, cprPos);

        let dx = cprPos[0] - baseCPR[0];
        let dy = cprPos[1] - baseCPR[1];

        // Round to 0 if below threshold.
        if (Math.abs(dx) < 0.00001) {
            dx = 0;
        }
        if (Math.abs(dy) < 0.00001) {
            dy = 0;
        }

        branchControlData.controlRadialOffsetR[k] = Number.isFinite(dx) ? dx : 0.0;
        branchControlData.controlOffsetYCPR[k] = Number.isFinite(dy) ? dy : 0.0;

        // Only recompute the world-space offset for the handle that was actually moved.
        // For non-moved handles, the existing world offset must be preserved because
        // converting CPR-space dx back to a world offset at a different viewing angle
        // loses the displacement component perpendicular to the current CPR X direction.
        if (movedHandle && handle !== movedHandle) {
            continue;
        }

        // Compute and store the world-space offset vector for this control point.
        const orientations = orientedCenterline?.getOrientations?.();
        const tangentDir = mapper.getTangentDirection();
        const q = orientations?.[clIndex];
        const safeDx = Number.isFinite(dx) ? dx : 0.0;

        let newOffset = [0, 0, 0];
        if (q && tangentDir) {
            const worldDir = vec3.create();
            vec3.transformQuat(worldDir, tangentDir, q);
            if (vec3.length(worldDir) > 0) {
                vec3.normalize(worldDir, worldDir);

                const existingWO = branchControlData.controlWorldOffsets[k];
                const hasExisting = Array.isArray(existingWO) && existingWO.length === 3 &&
                    (existingWO[0] !== 0 || existingWO[1] !== 0 || existingWO[2] !== 0);

                if (hasExisting) {
                    // Project the existing world offset onto the current CPR X direction.
                    // Replace only this parallel component with the new dx value, keeping
                    // the perpendicular component intact so that displacements made at
                    // other viewing angles are not lost.
                    const oldProjection = vec3.dot(existingWO, worldDir);
                    newOffset = [
                        existingWO[0] + (safeDx - oldProjection) * worldDir[0],
                        existingWO[1] + (safeDx - oldProjection) * worldDir[1],
                        existingWO[2] + (safeDx - oldProjection) * worldDir[2],
                    ];
                } else if (safeDx !== 0.0) {
                    newOffset = [
                        worldDir[0] * safeDx,
                        worldDir[1] * safeDx,
                        worldDir[2] * safeDx,
                    ];
                }
            }
        }
        branchControlData.controlWorldOffsets[k] = newOffset;
    }
}



/**
 * Project original control points onto the current CPR plane and update the spline widget handles accordingly.
 * @param {BranchControlData} branchControlData - Branch control data containing spline world control points and offset arrays.
 * @param {Object} currentCPRFrameRef - Current CPR frame data.
 * @param {vtkImageCPRMapper} mapper - CPR image mapper.
 * @param {vtkSplineWidget} splineWidget - Spline widget to update.
 * @param {vtkRenderWindowInteractor} interactor - Render window interactor.
 * @param {vtkRenderWindow} renderWindow - Render window.
 * @param {vtkPlaneManipulator} planeManipulator - Plane manipulator for the spline handles.
 * @param {vtkImageSlice} actor - Image slice actor.
 * @param {boolean} [editsAlreadyApplied=false] - When true, the oriented centerline already contains the edits.
 */
function updateSplineProjection(
    branchControlData,
    currentCPRFrameRef,
    mapper,
    splineWidget,
    interactor,
    renderWindow,
    planeManipulator,
    actor,
    handleIndexList,
    editsAlreadyApplied = false,
    handlesVisible = true,
) {
    // Guard: skip if no spline widget (e.g. straightened CPR view which doesn't support editing).
    if (!splineWidget) {
        return;
    }

    const { worldControlPoints, worldControlPointIndices } = branchControlData.splineWorldControlPointData;

    if (!Array.isArray(worldControlPoints) || worldControlPoints.length === 0) {
        log('updateSplineProjection: No world control points to project.');
        return;
    }

    if (!worldControlPointIndices || worldControlPointIndices.length === 0 || currentCPRFrameRef.current.height === null) {
        log('updateSplineProjection: Missing control point indices or invalid CPR frame height.');
        return;
    }

    const { width, height } = currentCPRFrameRef.current;

    const orientedCenterline = mapper.getOrientedCenterline();

    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const pts = orientedCenterline.getPoints();
    const nPts = pts.getNumberOfPoints();
    const ptsArray = pts.getData();
    if (!distancesToFirstPoint || !ptsArray || !nPts) {
        return;
    }

    const widgetState = splineWidget.getWidgetState();
    widgetState.clearHandleList();
    handleIndexList.length = 0; // reset in sync with the handle list, mutating the caller's array

    // Keep offset arrays aligned with indices
    if (
        !branchControlData.controlRadialOffsetR ||
        branchControlData.controlRadialOffsetR.length !== worldControlPointIndices.length
    ) {
        const old = branchControlData.controlRadialOffsetR || [];
        const arr = new Array(worldControlPointIndices.length).fill(0.0);
        for (let i = 0; i < Math.min(old.length, arr.length); i++) {
            arr[i] = old[i];
        }
        branchControlData.controlRadialOffsetR = arr;
    }

    if (
        !branchControlData.controlOffsetYCPR ||
        branchControlData.controlOffsetYCPR.length !== worldControlPointIndices.length
    ) {
        const old = branchControlData.controlOffsetYCPR || [];
        const arr = new Array(worldControlPointIndices.length).fill(0.0);
        for (let i = 0; i < Math.min(old.length, arr.length); i++) {
            arr[i] = old[i];
        }
        branchControlData.controlOffsetYCPR = arr;
    }

    if (
        !branchControlData.controlWorldOffsets ||
        branchControlData.controlWorldOffsets.length !== worldControlPointIndices.length
    ) {
        const old = branchControlData.controlWorldOffsets || [];
        const arr = new Array(worldControlPointIndices.length).fill(null);
        for (let i = 0; i < Math.min(old.length, arr.length); i++) {
            arr[i] = old[i];
        }
        branchControlData.controlWorldOffsets = arr;
    }

    const actorMatrix = actor.getUserMatrix();

    const params = buildCPRParams(mapper, orientedCenterline, currentCPRFrameRef.current);

    const cprPoint = [0, 0, 0];
    const worldCoord = [0, 0, 0];

    for (let i = 0; i < worldControlPointIndices.length; ++i) {
        const pointIndex = worldControlPointIndices[i];

        if (
            pointIndex < 0 ||
            pointIndex >= nPts ||
            distancesToFirstPoint[pointIndex] === undefined
        ) {
            continue;
        }

        const baseCPR = computeCPRCoordsForCenterlineIndex(
            pointIndex,
            orientedCenterline,
            params
        );
        if (!baseCPR) {
            continue;
        }
        log('updateSplineProjection - baseCPR:', baseCPR);

        let x = baseCPR[0];
        let y = baseCPR[1];
        let z = baseCPR[2] ?? 0.0;

        // When the oriented centerline already has edits applied (editsAlreadyApplied=true),
        // baseCPR already includes the displacement, so skip offset application to avoid
        // double-counting.
        if (!editsAlreadyApplied) {
            // Derive the CPR-space X offset (dx) from the stored world-space offset if available.
            // The world offset is angle-independent; projecting it onto the current CPR X direction
            // gives the correct visual offset for this viewing angle.
            const worldOffset = branchControlData.controlWorldOffsets && branchControlData.controlWorldOffsets[i];
            const hasWorldOffset = Array.isArray(worldOffset) && worldOffset.length === 3 &&
                (worldOffset[0] !== 0 || worldOffset[1] !== 0 || worldOffset[2] !== 0);

            let dx = branchControlData.controlRadialOffsetR[i];
            if (hasWorldOffset) {
                // Compute the world-space CPR X direction at this centerline point.
                const tangentDir = mapper.getTangentDirection();
                const projOrientations = params.orientations;
                const q = projOrientations?.[pointIndex];
                const worldCPRX = vec3.create();
                if (q && tangentDir) {
                    vec3.transformQuat(worldCPRX, tangentDir, q);
                    const dirLen = vec3.length(worldCPRX);
                    if (dirLen > 0) {
                        vec3.normalize(worldCPRX, worldCPRX);
                        // Project world offset onto the CPR X direction.
                        dx = vec3.dot(worldOffset, worldCPRX);
                    }
                }
            }

            const dy = branchControlData.controlOffsetYCPR[i];

            if (Number.isFinite(dx)) {
                x += dx;
            }
            if (Number.isFinite(dy)) {
                y += dy;
            }
        }

        // Clamp into CPR frame
        x = clamp(x, 0, width);
        y = clamp(y, 0, height);

        cprPoint[0] = x;
        cprPoint[1] = y;
        cprPoint[2] = z;

        vec3.transformMat4(worldCoord, cprPoint, actorMatrix);

        const handle = widgetState.addHandle();
        handle.setOrigin(worldCoord[0], worldCoord[1], worldCoord[2]);
        handle.setManipulator(planeManipulator);
        handle.setVisible(true);

        handleIndexList.push(pointIndex);
        applyHandleHighlight(handle, pointIndex, handlesVisible);
    }

    interactor.render();
    renderWindow.render();
}

/**
 * Reapply highlight state to existing spline-widget handles without rebuilding
 * them.  Reads from `activeOrthogonalHighlights`, so callers must update that
 * map first.  Uses `handleIndexList` (populated by `updateSplineProjection`)
 * for the positional lookup: handle i corresponds to centerline point index
 * handleIndexList[i].
 * @param {Object} splineWidget - The spline widget instance.
 * @param {Object} interactor - The render window interactor.
 * @param {Object} renderWindow - The render window.
 */
function updateHandleHighlighting(splineWidget, interactor, renderWindow, handleIndexList, handlesVisible = true) {
    if (!splineWidget) return;

    const widgetState = splineWidget.getWidgetState();
    if (!widgetState || typeof widgetState.getHandleList !== 'function') return;

    const handles = widgetState.getHandleList() || [];
    for (let i = 0; i < handles.length; i++) {
        applyHandleHighlight(handles[i], handleIndexList[i], handlesVisible);
    }

    interactor.render();
    renderWindow.render();
}

const stretchViewType = ViewTypes.XZ_PLANE;

/**
 * Save the current camera state for a specific branch.
 * @param {vtkCamera} camera - The camera to save state from.
 * @param {Map<string, Object>} branchCameraStates - Map to store camera states per branch.
 * @param {string} branchName - The branch name to save state for.
 * @param {number[]} [defaultPosition] - The default (computed) camera position for reference.
 * @param {number[]} [defaultFocalPoint] - The default (computed) focal point for reference.
 */
function saveCameraStateForBranch(camera, branchCameraStates, branchName, defaultPosition, defaultFocalPoint) {
    if (!camera || !branchCameraStates || !branchName) {
        return;
    }

    const position = camera.getPosition();
    const focalPoint = camera.getFocalPoint();
    const viewUp = camera.getViewUp();
    const parallelScale = camera.getParallelScale();

    const existingState = branchCameraStates.get(branchName);

    let panOffset = [0, 0, 0];
    let lastDefaultPosition;
    let lastDefaultFocalPoint;

    if (defaultPosition && defaultFocalPoint) {
        // We're setting a new default (from updateDistanceAndDirection)
        // Calculate pan offset relative to this new default
        panOffset = [
            position[0] - defaultPosition[0],
            position[1] - defaultPosition[1],
            position[2] - defaultPosition[2]
        ];
        lastDefaultPosition = [...defaultPosition];
        lastDefaultFocalPoint = [...defaultFocalPoint];
    } else if (existingState && existingState.lastDefaultPosition && existingState.lastDefaultFocalPoint) {
        // User interaction - calculate offset from last known default
        panOffset = [
            position[0] - existingState.lastDefaultPosition[0],
            position[1] - existingState.lastDefaultPosition[1],
            position[2] - existingState.lastDefaultPosition[2]
        ];
        lastDefaultPosition = existingState.lastDefaultPosition;
        lastDefaultFocalPoint = existingState.lastDefaultFocalPoint;
    } else {
        // No default available, preserve existing offset
        panOffset = existingState?.panOffset || [0, 0, 0];
        lastDefaultPosition = existingState?.lastDefaultPosition;
        lastDefaultFocalPoint = existingState?.lastDefaultFocalPoint;
    }

    const state = {
        position: [...position],
        focalPoint: [...focalPoint],
        viewUp: [...viewUp],
        parallelScale,
        panOffset,
        lastDefaultPosition,
        lastDefaultFocalPoint
    };

    branchCameraStates.set(branchName, state);
}

/**
 * Dispatch a LiveCenterlineEdited event to notify the straightened CPR view
 * that data has changed.
 * @param {Object} eventNames - Event name map from component props.
 * @param {string} branchName - The branch that was edited.
 * @param {BranchControlData} branchControlData - The updated control data.
 */
function notifyStraightenedView(eventNames, branchName, branchControlData) {
    if (eventNames?.LiveCenterlineEdited && branchName && branchControlData) {
        window.dispatchEvent(new CustomEvent(eventNames.LiveCenterlineEdited, {
            detail: {
                branchName,
                branchControlData: structuredClone(branchControlData),
            }
        }));
    }
}

/**
 * Dispatch a LiveCenterlineDataChanged event to notify the orthogonal cross-section
 * views that the live centerline polydata has been updated.
 * @param {Object} eventNames - Event name map from component props.
 * @param {string} branchName - The branch that was updated.
 * @param {vtkPolyData} liveCenterline - The current live centerline polydata.
 */
function notifyOrthogonalViews(eventNames, branchName, liveCenterline) {
    if (eventNames?.LiveCenterlineDataChanged && branchName && liveCenterline) {
        window.dispatchEvent(new CustomEvent(eventNames.LiveCenterlineDataChanged, {
            detail: { branchName, liveCenterline }
        }));
    }
}

/** Sync the CPR mapper's direction matrix from the current reslice cursor widget state.
 * This updates the mapper's tangent direction without touching the camera, actor transform,
 * or handle positions. It must be called after rotateView() so the widget planes reflect
 * the new angle, and before computeEditedCenterlinePositions() which may need the correct tangent direction.
 * @param {vtkImageCPRMapper} mapper - The CPR image mapper.
 * @param {Object} resliceCursorWidgetState - The reslice cursor widget state.
 * @param {vtkCPRManipulator} cprManipulator - The CPR manipulator.
 */
function syncMapperDirectionMatrix(mapper, resliceCursorWidgetState, cprManipulator) {
    const resliceCursorWidgetPlanes = resliceCursorWidgetState.getPlanes();
    let worldBitangent = [...resliceCursorWidgetPlanes[stretchViewType].normal];
    let worldNormal   = [...resliceCursorWidgetPlanes[stretchViewType].viewUp];

    vec3.normalize(worldNormal, worldNormal);
    const proj = vec3.dot(worldBitangent, worldNormal);
    vec3.scaleAndAdd(worldBitangent, worldBitangent, worldNormal, -proj);
    vec3.normalize(worldBitangent, worldBitangent);

    const worldTangent = vec3.cross([], worldBitangent, worldNormal);
    vec3.normalize(worldTangent, worldTangent);

    const distance = cprManipulator.getCurrentDistance();
    const { orientation } = mapper.getCenterlinePositionAndOrientation(distance);
    const modelDirections = mat3.fromQuat([], orientation);
    const inverseModelDirections = mat3.invert([], modelDirections);
    const worldDirections = mat3.fromValues(
        ...worldTangent,
        ...worldBitangent,
        ...worldNormal
    );
    const baseDirections = mat3.mul([], inverseModelDirections, worldDirections);
    mapper.setDirectionMatrix(baseDirections);
}

/** Update CPR distance, direction, actor and camera based on the reslice cursor widget state.
 * @param {Object} context - Context object containing VTK components.
 * @param {vtkRenderWindow} context.renderWindow - The render window.
 * @param {vtkRenderer} context.renderer - The renderer.
 * @param {vtkRenderWindowInteractor} context.interactor - The render window interactor.
 * @param {vtkImageCPRMapper} context.mapper - The CPR image mapper.
 * @param {vtkImageSlice} context.actor - The image slice actor.
 * @param {vtkResliceCursorWidget} context.resliceCursorWidget - The reslice cursor widget.
 * @param {Object} context.resliceCursorWidgetState - The reslice cursor widget state.
 * @param {vtkCPRManipulator} context.cprManipulator - The CPR manipulator.
 * @param {vtkPlaneManipulator} context.planeManipulator - The plane manipulator for the spline handles.
 * @param {vtkSplineWidget} context.splineWidget - The spline widget.
 * @param {Object} context.cprObjects - Reference to cprObjects for state tracking.
 * @param {function(number): void} angleUpdatedCallback - Callback function for angle updates.
 * @param {BranchControlData} branchControlData - Branch control data containing spline world control points and offset arrays.
 * @param {React.RefObject.<Object>} currentCPRFrameRef - Ref to current CPR frame data.
 * @param {string} selectedBranchName - The currently selected branch name for camera state restoration.
 * @param {boolean} [editsAlreadyApplied=false] - When true, the oriented centerline already contains the edits.
 */
function updateDistanceAndDirection(
    {
        renderWindow,
        renderer,
        interactor,
        mapper,
        actor,
        resliceCursorWidget,
        resliceCursorWidgetState,
        cprManipulator,
        planeManipulator,
        splineWidget,
        cprObjects,
    },
    angleUpdatedCallback,
    branchControlData,
    currentCPRFrameRef,
    selectedBranchName,
    editsAlreadyApplied = false,
) {

    log("updateDistanceAndDirection");
    // Directions and position in world space from the widget.
    const resliceCursorWidgetPlanes = resliceCursorWidgetState.getPlanes();

    /**
     * A unit vector normal to the CPR plane (out-of-plane). It is the plane normal / camera offset direction and is used
     * together with worldTangent and worldNormal to build the full orientation basis.
     */
    let worldBitangent = [...resliceCursorWidgetPlanes[stretchViewType].normal];

    /**
     * A unit vector in world space that represents the mapper's model Y axis (the in-plane "along-height /
     * along-distance" direction, often treated as the plane's "up"). It lies in the CPR plane and is used to move points
     *  toward/away along the CPR height.
     */
    let worldNormal = [...resliceCursorWidgetPlanes[stretchViewType].viewUp];

    // Gram-Schmidt: https://en.wikipedia.org/wiki/Gram-Schmidt_process
    // Make sure that the Bitangent and Normal are orthonormal to each other.
    vec3.normalize(worldNormal, worldNormal);
    const proj = vec3.dot(worldBitangent, worldNormal);
    vec3.scaleAndAdd(worldBitangent, worldBitangent, worldNormal, -proj);
    vec3.normalize(worldBitangent, worldBitangent);

    /**
     * A unit vector in world space that represents the mapper's model X axis (the in-plane "across-width" direction).
     * It lies in the CPR plane and is used to move points left-right across the CPR strip.
     */
    const worldTangent = vec3.cross([], worldBitangent, worldNormal);
    vec3.normalize(worldTangent, worldTangent);

    const worldWidgetCenter = resliceCursorWidgetState.getCenter();
    const distance = cprManipulator.getCurrentDistance();

    // CPR mapper tangent and bitangent directions update
    const { orientation } = mapper.getCenterlinePositionAndOrientation(distance);
    const modelDirections = mat3.fromQuat([], orientation);
    const inverseModelDirections = mat3.invert([], modelDirections);
    const worldDirections = mat3.fromValues(
        ...worldTangent,
        ...worldBitangent,
        ...worldNormal
    );
    const baseDirections = mat3.mul([], inverseModelDirections, worldDirections);
    log('updateDistanceAndDirection - baseDirections:', baseDirections);

    // The direction matrix is the matrix composed of tangent, bitangent and normal directions.
    // It is used to orient the camera or the actor.
    mapper.setDirectionMatrix(baseDirections);

    planeManipulator.setUserOrigin(worldWidgetCenter);
    planeManipulator.setUserNormal(worldNormal);

    // Ensure spline widget manipulator uses the same plane so handles move on the CPR plane.
    splineWidget?.setManipulator(planeManipulator);

    updateState(
        resliceCursorWidgetState,
        resliceCursorWidget.getScaleInPixels(),
        resliceCursorWidget.getRotationHandlePosition()
    );

    const width = mapper.getWidth();
    const height = mapper.getHeight();

    const worldActorTranslation = vec3.scaleAndAdd(
        [],
        worldWidgetCenter,
        worldTangent,
        -0.5 * width
    );
    vec3.scaleAndAdd(
        worldActorTranslation,
        worldActorTranslation,
        worldNormal,
        distance - height
    );
    const worldActorTransform = mat4.fromValues(
        ...worldTangent, 0,
        ...worldNormal, 0,
        ...vec3.scale([], worldBitangent, -1), 0,
        ...worldActorTranslation, 1
    );
    actor.setUserMatrix(worldActorTransform);

    // CPR frame info for spline update
    currentCPRFrameRef.current = {
        worldTangent: [...worldTangent],
        worldNormal: [...worldNormal],
        worldBitangent: [...worldBitangent],
        worldWidgetCenter: [...worldWidgetCenter],
        distance,
        height,
        width,
        worldActorTranslation: [...worldActorTranslation],
    };

    const camera = renderer.getActiveCamera();

    let targetParallelScale;

    // Check if there's a saved state for this branch
    const savedState = (cprObjects?.branchCameraStates && selectedBranchName)
        ? cprObjects.branchCameraStates.get(selectedBranchName)
        : null;

    if (savedState) {
        targetParallelScale = savedState.parallelScale;
    } else {
        // No saved state - use default scale based on CPR height
        targetParallelScale = 0.5 * height;
    }

    // Ensure camera is in parallel projection mode
    if (cprObjects && !cprObjects.cameraScaleInitialized) {
        camera.setParallelProjection(true);
        cprObjects.cameraScaleInitialized = true;
    }

    const cameraDistance =
        targetParallelScale /
        Math.tan(radiansFromDegrees(0.5 * camera.getViewAngle()));

    const cameraFocalPoint = vec3.scaleAndAdd(
        [],
        worldWidgetCenter,
        worldNormal,
        distance - 0.5 * height
    );

    const cameraPosition = vec3.scaleAndAdd(
        [],
        cameraFocalPoint,
        worldBitangent,
        -cameraDistance
    );

    // Apply pan offset if saved state exists
    let finalPosition = [...cameraPosition];
    let finalFocalPoint = [...cameraFocalPoint];

    if (savedState && savedState.panOffset) {
        finalPosition[0] += savedState.panOffset[0];
        finalPosition[1] += savedState.panOffset[1];
        finalPosition[2] += savedState.panOffset[2];

        finalFocalPoint[0] += savedState.panOffset[0];
        finalFocalPoint[1] += savedState.panOffset[1];
        finalFocalPoint[2] += savedState.panOffset[2];
    }

    // Set flag to prevent saving during programmatic camera updates.
    if (cprObjects) {
        cprObjects.preventCameraSave = true;
    }

    // Apply the computed camera settings with pan offset.
    camera.setPosition(...finalPosition);
    camera.setFocalPoint(...finalFocalPoint);
    camera.setViewUp(...worldNormal);
    camera.setParallelScale(targetParallelScale);

    // Save the current state with the computed default position as reference.
    // This allows us to track future pan offsets correctly.
    if (cprObjects?.branchCameraStates && selectedBranchName) {
        saveCameraStateForBranch(
            camera,
            cprObjects.branchCameraStates,
            selectedBranchName,
            cameraPosition,  // default position without pan
            cameraFocalPoint  // default focal point without pan
        );
    }

    // Clear the flag to allow user interactions to save.
    if (cprObjects) {
        cprObjects.preventCameraSave = false;
    }

    renderer.resetCameraClippingRange();
    interactor.render();
    renderWindow.render();

    updateSplineProjection(
        branchControlData,
        currentCPRFrameRef,
        mapper,
        splineWidget,
        interactor,
        renderWindow,
        planeManipulator,
        actor,
        cprObjects.handleIndexList,
        editsAlreadyApplied,
        cprObjects?.isCenterlineEditable !== false,
    );
}

/** Set the centerline polydata for the CPR mapper and update the reslice cursor widget center.
 * @param {vtkImageCPRMapper} mapper
 * @param {vtkPolyData} liveCenterline
 * @param {vtkPolyData} polyData
 * @param {import('vtk.js/Sources/Widgets/Widgets3D/ResliceCursorWidget/ResliceCursorWidgetState').ResliceCursorWidgetState} resliceCursorWidgetState
 * @param {vtkCPRManipulator} cprManipulator
 */
function setCenterlinePolyData(
    mapper,
    liveCenterline,
    polyData,
    resliceCursorWidgetState,
    cprManipulator
) {
    if (!polyData) {
        log("setCenterlinePolyData: No polydata provided for centerline.");
        return;
    }

    // Positions.
    liveCenterline.getPoints().setData(polyData.getPoints().getData(), 3);

    // Polyline (single line from 0..N-1).
    const nPoints = polyData.getPoints().getNumberOfPoints();
    const lines = new Uint32Array(1 + nPoints);
    lines[0] = nPoints;

    for (let i = 0; i < nPoints; ++i) {
        lines[i + 1] = i;
    }

    liveCenterline.getLines().setData(lines);

    // Orientation (mat4 per point).
    const tensors = polyData.getPointData().getTensors();
    liveCenterline.getPointData().setTensors(
        vtkDataArray.newInstance({
            name: 'Orientation',
            numberOfComponents: 16,
            values: tensors.getData().slice(), // Copy
        })
    );

    // Sign-consistent quaternion array (4-component per point) for the CPR mapper.
    const quatArray = polyData.getPointData().getArrayByName('OrientationQuat');
    if (quatArray) {
        liveCenterline.getPointData().removeArray('OrientationQuat');
        liveCenterline.getPointData().addArray(
            vtkDataArray.newInstance({
                name: 'OrientationQuat',
                numberOfComponents: 4,
                values: quatArray.getData().slice(),
            })
        );
    }

    liveCenterline.modified();

    const midPointDistance = mapper.getHeight() / 2;
    const { worldCoords } = cprManipulator.distanceEvent(midPointDistance);
    resliceCursorWidgetState.setCenter(worldCoords);
}

/** Rotate the CPR view by the specified angle in degrees.
 * @param {number} angleInDegrees
 * @param {vtkImageCPRMapper} mapper
 * @param {vtkCPRManipulator} cprManipulator
 * @param {import('vtk.js/Sources/Widgets/Widgets3D/ResliceCursorWidget/ResliceCursorWidgetState').ResliceCursorWidgetState} resliceCursorWidgetState
 */
function rotateView(angleInDegrees, mapper, cprManipulator, resliceCursorWidgetState) {
    const radAngle = radiansFromDegrees(angleInDegrees);
    const origin = [0, 0, 0];

    // The per-point orientation matrices store [T, B, N] in columns 0, 1, 2 where:
    //   T = centerline tangent (along the vessel path)
    //   B = bitangent (perpendicular to vessel, in cross-section plane)
    //   N = normal (perpendicular to vessel, completes the right-hand frame)
    //
    // In stretched mode (useUniformOrientation=true), the per-point orientations are
    // NOT used in the shader. The oblique angle rotates the sampling direction in the
    // T-B plane (model X-Y), which is correct because the uniform orientation applies
    // the same direction to all points.
    //
    // In straightened mode (useUniformOrientation=false), the per-point quaternion q_i
    // transforms tangentDirection into the world-space sampling direction:
    //   samplingDir = q_i * tangentDirection
    // Since q_i * [1,0,0] = T_i, the tangentDirection must NOT have an X-component
    // (which would add an along-centerline sampling offset). Instead, the oblique angle
    // must rotate in the B-N plane (model Y-Z) so that tangentDirection = [0, cos θ, sin θ],
    // giving: samplingDir = cos(θ)*B_i + sin(θ)*N_i (always perpendicular to centerline).
    let normalDir, bitangentDir;

    if (mapper.getUseUniformOrientation()) {
        // Stretched mode: rotate in model X-Y plane (around Z).
        normalDir = [0, 0, 1];
        bitangentDir = [0, 1, 0];
        vec3.rotateZ(bitangentDir, bitangentDir, origin, radAngle);
    } else {
        // Straightened mode: rotate in model Y-Z plane (around X / centerline tangent axis).
        // normalDir points along the centerline tangent T so that worldNormal = T_i,
        // which becomes the camera up direction (strip height = along-centerline distance).
        normalDir = [1, 0, 0];
        bitangentDir = [0, 0, 1];
        vec3.rotateX(bitangentDir, bitangentDir, origin, radAngle);
    }

    const distance = cprManipulator.getCurrentDistance();
    const { orientation } = mapper.getCenterlinePositionAndOrientation(distance);
    const modelDirections = mat3.fromQuat([], orientation);

    const worldBitangent = vec3.transformMat3([], bitangentDir, modelDirections);
    const worldNormal = vec3.transformMat3([], normalDir, modelDirections);

    // TODO: It would be nice to replace the reslice cursor widget with our own computed plane.
    const widgetPlanes = resliceCursorWidgetState.getPlanes();
    widgetPlanes[stretchViewType].normal = worldBitangent;
    widgetPlanes[stretchViewType].viewUp = worldNormal;
    resliceCursorWidgetState.setPlanes(widgetPlanes);
}

/** Configure the spline widget and its representation with desired settings.
 * @param {vtkSplineWidget} splineWidget
 * @param {import('vtk.js/Sources/Widgets/Core/WidgetManager/WidgetInstance')} splineWidgetRepresentation
 * @param {[number, number, number]|null} [lineColor=null] - Optional RGB [0..1] color for the spline line actor.
 */
function configureSplineWidget(splineWidget, splineWidgetRepresentation, lineColor = null) {
    splineWidgetRepresentation.setOutputBorder(true);
    splineWidgetRepresentation.setFill(false);
    const widgetState = splineWidget.getWidgetState();

    // Spline boundary conditions setup
    widgetState.setSplineBoundaryCondition(BoundaryCondition.DEFAULT);

    // TODO: If the boundary condition is not DEFAULT, we need to set the boundary condition values:
    // widgetState.setSplineBoundaryConditionValues([0, 0, 0]);

    widgetState.setSplineClosed(false);
    widgetState.setSplineKind(splineKind.KOCHANEK_SPLINE);

    // TODO: If using Kochanek spline, we can set tension, bias, continuity here:
    // widgetState.setSplineTension(0);
    // widgetState.setSplineBias(0);
    // widgetState.setSplineContinuity(0);

    splineWidgetRepresentation.setResolution(20);
    splineWidgetRepresentation.setHandleSizeInPixels(20);

    // Freehand control
    splineWidgetRepresentation.setAllowFreehand(false);
    splineWidgetRepresentation.setFreehandMinDistance(0.2);

    // SplineContextRepresentation.requestData() overwrites the border actor's color
    // from model.borderColor on every render, so we must store it there.
    if (lineColor) {
        splineWidgetRepresentation.setBorderColor(...lineColor);
    }
}



/**
 * Find an existing control-point index that is "near" the given centerline id.
 * Returns the existing index value (e.g. 180) or null if none is within maxDelta.
 * @param {number} pointIndex - Centerline point index to search for.
 * @param {number} maxDelta - Maximum delta to consider "near".
 * @param {number[]} worldControlPointIndices - Array of existing control point indices.
 * @returns {number|null} - The found nearby control point index, or null if none found.
 */
function findNearbyControlPointIndex(pointIndex, maxDelta = 3, worldControlPointIndices) {
    if (
        !worldControlPointIndices ||
        !worldControlPointIndices.length
    ) {
        return null;
    }

    let bestIdx = null;
    let bestDelta = Infinity;

    for (const idx of worldControlPointIndices) {
        const delta = Math.abs(idx - pointIndex);
        if (delta <= maxDelta && delta < bestDelta) {
            bestDelta = delta;
            bestIdx = idx;
        }
    }

    return bestIdx;
}

/**
 * Add a new original control point index to the spline world control point data,
 * maintaining sorted order. If the index already exists, optionally update its
 * initial radial and Y offsets.
 * @param {number} pointIndex - Centerline point index to add.
 * @param {number} initialRadialOffset - Initial radial offset R to set for this control point.
 * @param {BranchControlData} branchControlData - Branch control data containing spline world control points and offset arrays.
 * @param {number[]|null} [initialWorldOffset=null] - Initial world-space offset [dx, dy, dz], or null.
 * @returns {number} - The index at which the control point was inserted or found.
 */
function addOriginalControlPointIndex(
    pointIndex,
    initialRadialOffset,
    branchControlData,
    initialWorldOffset = null,
) {
    const r0 = Number.isFinite(initialRadialOffset) ? initialRadialOffset : 0.0;
    // BUG? ViTAA PoC code never passes in a value for initialYOffset, so y0 is always 0.0.
    // const y0 = Number.isFinite(initialYOffset) ? initialYOffset : 0.0;
    const y0 = 0.0;
    const w0 = Array.isArray(initialWorldOffset) && initialWorldOffset.length === 3
        ? initialWorldOffset
        : null;

    const oldIndices = branchControlData.splineWorldControlPointData.worldControlPointIndices;

    // Ensure offset arrays exist and are aligned
    ensureOffsetArraysExist(
        branchControlData,
        oldIndices.length,
    );

    const oldRadial = branchControlData.controlRadialOffsetR;
    const oldY = branchControlData.controlOffsetYCPR;
    const oldWorld = branchControlData.controlWorldOffsets || new Array(oldIndices.length).fill(null);

    // If it already exists, optionally update its offsets and return its index
    for (let i = 0; i < oldIndices.length; i++) {
        if (oldIndices[i] === pointIndex) {
            if (Number.isFinite(initialRadialOffset)) {
                oldRadial[i] = r0;
                branchControlData.controlRadialOffsetR = oldRadial;
            }
            if (initialWorldOffset) {
                oldWorld[i] = w0;
            }
            // BUG? ViTAA PoC code never passes in a value for initialYOffset, so this would never fire.
            // if (Number.isFinite(initialYOffset)) {
            //     oldY[i] = y0;
            // }
            branchControlData.controlRadialOffsetR = oldRadial;
            branchControlData.controlOffsetYCPR = oldY;
            branchControlData.controlWorldOffsets = oldWorld;
            branchControlData.controlOffsetYCPR = oldY;
            return i;
        }
    }

    // Insert the new index in sorted order, carrying offsets with it
    const newIndices = [];
    const newRadial = [];
    const newY = [];
    const newWorld = [];
    let insertedIndex = -1;

    for (let i = 0; i < oldIndices.length; i++) {
        const idx = oldIndices[i];

        if (insertedIndex === -1 && pointIndex < idx) {
            insertedIndex = newIndices.length;
            newIndices.push(pointIndex);
            newRadial.push(r0);
            newY.push(y0);
            newWorld.push(w0);
        }

        newIndices.push(idx);
        newRadial.push(oldRadial[i]);
        newY.push(oldY[i]);
        newWorld.push(oldWorld[i]);
    }

    if (insertedIndex === -1) {
        // New index goes at the end
        insertedIndex = newIndices.length;
        newIndices.push(pointIndex);
        newRadial.push(r0);
        newY.push(y0);
        newWorld.push(w0);
    }

    branchControlData.splineWorldControlPointData.worldControlPointIndices = newIndices;
    branchControlData.controlRadialOffsetR = newRadial;
    branchControlData.controlOffsetYCPR = newY;
    branchControlData.controlWorldOffsets = newWorld;

    return insertedIndex;
}

/**
 * Compute CPR-strip coordinates [x, y, 0] for a centerline point i,
 * using the same logic as updateSplineProjection.
 *
 * Assumes:
 *   - orientedCenterline is mapper.getOrientedCenterline()
 *   - distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint()
 *   - orientations = orientedCenterline.getOrientations() or null
 *   - width/height come from currentCPRFrame
 *
 * @param {number} i - Centerline point index.
 * @param {vtkPolyLine} orientedCenterline - Oriented centerline polydata.
 * @param {Object} params - Parameters object.
 * @param {number} params.width - CPR width.
 * @param {number} params.height - CPR height.
 * @param {number[]} params.tangentDirection - World-space tangent direction vector.
 * @param {number[]} params.centerPoint - World-space center point [x, y, z].
 * @param {number[]} params.distancesToFirstPoint - Array of distances along centerline to first point.
 * @param {Array.<quat>|null} params.orientations - Array of orientation quaternions per centerline point, or null.
 * @returns {number[]|null} - CPR coordinates [x, y, 0] or null if invalid index.
 */
function computeCPRCoordsForCenterlineIndex(
    i,
    orientedCenterline,
    {
        width,
        height,
        tangentDirection,
        centerPoint,
        distancesToFirstPoint,
        orientations,
    }
) {
    const ptsArray = orientedCenterline.getPoints().getData();
    const nPts = orientedCenterline.getPoints().getNumberOfPoints();

    if (
        i < 0 ||
        i >= nPts ||
        !ptsArray ||
        !distancesToFirstPoint ||
        distancesToFirstPoint[i] === undefined
    ) {
        return null;
    }

    // ---- Y: distance along centerline -> CPR height ----
    const distance = distancesToFirstPoint[i];
    let newY = height - distance;
    newY = clamp(newY, 0, height);

    // ---- World position of this centerline point ----
    const point = [
        ptsArray[3 * i],
        ptsArray[3 * i + 1],
        ptsArray[3 * i + 2],
    ];

    // ---- X: based on centerPoint + local tangent/orientation ----
    let newX = width / 2;

    if (centerPoint) {
        const localXDirection = [0, 0, 0];
        const tmp = [0, 0, 0];

        // Orientation quaternion for this point (if available)
        const q = orientations?.[i];

        if (q) {
            vec3.transformQuat(localXDirection, tangentDirection, q);
        } else {
            vec3.copy(localXDirection, tangentDirection);
        }

        // Offset along local X so that centerPoint is centered
        vec3.subtract(tmp, centerPoint, point);
        newX += vec3.dot(tmp, localXDirection);
    }

    // Clamp to strip
    newX = clamp(newX, 0, width);

    // Mirror across the midline (same as updateSplineProjection)
    newX = width - newX;

    return [newX, newY, 0];
}

/**
 * Cubic Hermite interpolation at t ∈ [0,1] over a segment of length dt.
 * m0, m1 are the dv/di tangent values at the segment endpoints.
 * With Catmull-Rom finite-difference tangents this matches Kochanek-Bartels T=B=C=0,
 * the same setting used by vtkSplineWidget in the stretched CPR view.
 */
function catmullRomHermite(t, v0, v1, m0, m1, dt) {
    const t2 = t * t, t3 = t2 * t;
    return (2*t3 - 3*t2 + 1) * v0
         + (t3 - 2*t2 + t)   * dt * m0
         + (-2*t3 + 3*t2)    * v1
         + (t3 - t2)          * dt * m1;
}

/**
 * Get the radial CPR-space offset r for an arbitrary centerline point index i
 * by interpolating between the nearest control points along the centerline.
 *
 * This turns controlRadialOffsetR (which is defined only at
 * worldControlPointIndices[]) into a continuous function r(i) over the
 * whole oriented centerline.
 *
 * @param {number} i - Centerline point index.
 * @param {number[]} worldControlPointIndices - Array of original control point indices.
 * @param {number[]} controlRadialOffsetR - Array of radial offsets at the control points.
 * @returns {number} - Interpolated radial offset r(i).
 */
function getRadialOffsetForCenterlineIndex(i, worldControlPointIndices, controlRadialOffsetR) {
    if (
        !worldControlPointIndices ||
        !worldControlPointIndices.length ||
        !controlRadialOffsetR ||
        !controlRadialOffsetR.length
    ) {
        return 0.0;
    }

    const nCtrl = Math.min(worldControlPointIndices.length, controlRadialOffsetR.length);

    if (nCtrl === 0) {
        return 0.0;
    }

    // Helper to read a finite r(k) or 0 if undefined/NaN
    const getR = (k) => {
        const v = controlRadialOffsetR[k];
        return Number.isFinite(v) ? v : 0.0;
    };

    // Before first control point -> clamp to its offset
    if (i <= worldControlPointIndices[0]) {
        return getR(0);
    }

    // After last control point -> clamp to its offset
    if (i >= worldControlPointIndices[nCtrl - 1]) {
        return getR(nCtrl - 1);
    }

    // Find segment [i0, i1] such that i0 <= i <= i1
    for (let k = 0; k < nCtrl - 1; ++k) {
        const i0 = worldControlPointIndices[k];
        const i1 = worldControlPointIndices[k + 1];

        if (i < i0 || i > i1) {
            continue;
        }

        const r0 = getR(k);
        const r1 = getR(k + 1);

        if (i1 === i0) {
            // Degenerate, just pick the left one
            return r0;
        }

        const dt = i1 - i0;
        const t = (i - i0) / dt; // 0..1 along the segment
        // Catmull-Rom tangents (non-uniform spacing, one-sided at boundaries)
        const m0 = k > 0
            ? (r1 - getR(k - 1)) / (i1 - worldControlPointIndices[k - 1])
            : (r1 - r0) / dt;
        const m1 = k + 2 < nCtrl
            ? (getR(k + 2) - r0) / (worldControlPointIndices[k + 2] - i0)
            : (r1 - r0) / dt;
        return catmullRomHermite(t, r0, r1, m0, m1, dt);
    }

    // Fallback (shouldn't really hit here)
    return 0.0;
}

/**
 * Interpolate world-space offset vectors for an arbitrary centerline point index.
 * Similar to getRadialOffsetForCenterlineIndex but operates on 3D vectors.
 *
 * @param {number} i - Centerline point index.
 * @param {number[]} worldControlPointIndices - Array of original control point indices.
 * @param {(number[]|null)[]} controlWorldOffsets - Array of world-space offset vectors per control point.
 * @returns {number[]} - Interpolated world offset [dx, dy, dz].
 */
function getWorldOffsetForCenterlineIndex(i, worldControlPointIndices, controlWorldOffsets) {
    const zero = [0, 0, 0];

    if (
        !worldControlPointIndices ||
        !worldControlPointIndices.length ||
        !controlWorldOffsets ||
        !controlWorldOffsets.length
    ) {
        return zero;
    }

    const nCtrl = Math.min(worldControlPointIndices.length, controlWorldOffsets.length);

    if (nCtrl === 0) {
        return zero;
    }

    const getW = (k) => {
        const v = controlWorldOffsets[k];
        return (Array.isArray(v) && v.length === 3) ? v : zero;
    };

    // Before first control point -> clamp to its offset
    if (i <= worldControlPointIndices[0]) {
        return getW(0);
    }

    // After last control point -> clamp to its offset
    if (i >= worldControlPointIndices[nCtrl - 1]) {
        return getW(nCtrl - 1);
    }

    // Find segment [i0, i1] such that i0 <= i <= i1
    for (let k = 0; k < nCtrl - 1; ++k) {
        const i0 = worldControlPointIndices[k];
        const i1 = worldControlPointIndices[k + 1];

        if (i < i0 || i > i1) {
            continue;
        }

        const w0 = getW(k);
        const w1 = getW(k + 1);

        if (i1 === i0) {
            return w0;
        }

        const dt = i1 - i0;
        const t = (i - i0) / dt;
        // Catmull-Rom tangents per component (non-uniform spacing, one-sided at boundaries)
        const wPrev = k > 0        ? getW(k - 1) : null;
        const wNext = k + 2 < nCtrl ? getW(k + 2) : null;
        const prevDt = wPrev ? i1 - worldControlPointIndices[k - 1] : dt;
        const nextDt = wNext ? worldControlPointIndices[k + 2] - i0 : dt;
        return [
            catmullRomHermite(t, w0[0], w1[0],
                wPrev ? (w1[0] - wPrev[0]) / prevDt : (w1[0] - w0[0]) / dt,
                wNext ? (wNext[0] - w0[0]) / nextDt : (w1[0] - w0[0]) / dt, dt),
            catmullRomHermite(t, w0[1], w1[1],
                wPrev ? (w1[1] - wPrev[1]) / prevDt : (w1[1] - w0[1]) / dt,
                wNext ? (wNext[1] - w0[1]) / nextDt : (w1[1] - w0[1]) / dt, dt),
            catmullRomHermite(t, w0[2], w1[2],
                wPrev ? (w1[2] - wPrev[2]) / prevDt : (w1[2] - w0[2]) / dt,
                wNext ? (wNext[2] - w0[2]) / nextDt : (w1[2] - w0[2]) / dt, dt),
        ];
    }

    return zero;
}

/**
 * Edited CPR coordinates for a centerline point i.
 *
 * - Start from the base CPR coords (computeCPRCoordsForCenterlineIndex)
 * - Apply a continuous radial offset r(i) in X via getRadialOffsetForCenterlineIndex.
 *
 * This is effectively an edited CPR-space "copy" of the oriented centerline that
 * tracks the user-edited spline, instead of only the original centerline.
 *
 * @param {number} i - Centerline point index.
 * @param {vtkPolyLine} orientedCenterline - Oriented centerline.
 * @param {Object} params - Parameters object.
 * @param {number} params.width - CPR width.
 * @param {number} params.height - CPR height.
 * @param {number[]} params.tangentDirection - World-space tangent direction vector.
 * @param {number[]} params.centerPoint - World-space center point [x, y, z].
 * @param {number[]} params.distancesToFirstPoint - Array of distances along centerline to first point.
 * @param {Array.<quat>|null} params.orientations - Array of orientation quaternions per centerline point, or null.
 * @param {number[]} worldControlPointIndices - Array of original control point indices.
 * @param {number[]} controlRadialOffsetR - Array of radial offsets at the control points.
 * @returns {number[]|null} - Edited CPR coordinates [x, y, 0] or null if invalid index.
 */
function computeEditedCPRCoordsForCenterlineIndex(
    i,
    orientedCenterline,
    params,
    worldControlPointIndices,
    controlRadialOffsetR
) {
    const base = computeCPRCoordsForCenterlineIndex(
        i,
        orientedCenterline,
        params
    );
    if (!base) {
        return null;
    }
    log('computeEditedCPRCoordsForCenterlineIndex - base:', base);

    const { width } = params || {};
    const r = getRadialOffsetForCenterlineIndex(i, worldControlPointIndices, controlRadialOffsetR);

    let x = base[0] + r;
    const y = base[1];
    const z = base[2] ?? 0.0;

    if (width != null) {
        x = clamp(x, 0, width);
    }

    return [x, y, z];
}

function setCPRCursor(style, cprCanvas) {
    if (cprCanvas && cprCanvas.style) {
        cprCanvas.style.cursor = style;
    }
}

const MEASUREMENT_LINE_DRAG_CURSOR_STYLE_ID = 'cpr-measurement-line-drag-cursor';

/**
 * Inject (once) the stylesheet for measurement-line drag cursors; `!important`
 * class rules override the vtk interactor's continuously rewritten inline cursor.
 */
function ensureMeasurementLineDragCursorStyle() {
    if (typeof document === 'undefined' || document.getElementById(MEASUREMENT_LINE_DRAG_CURSOR_STYLE_ID)) {
        return;
    }
    const styleEl = document.createElement('style');
    styleEl.id = MEASUREMENT_LINE_DRAG_CURSOR_STYLE_ID;
    styleEl.textContent =
        '.cpr-measurement-line-hover, .cpr-measurement-line-hover * { cursor: grab !important; }' +
        '.cpr-measurement-line-dragging, .cpr-measurement-line-dragging * { cursor: grabbing !important; }';
    document.head.appendChild(styleEl);
}

/**
 * Convert DOM mouse event -> VTK display coords [x, y, 0]
 * @param {MouseEvent} ev - The mouse event.
 * @param {HTMLCanvasElement} cprCanvas - The CPR canvas element.
 * @returns {number[]|null} - Display coordinates [x, y, 0] or null if invalid.
 */
function displayCoordsFromMouseEvent(ev, cprCanvas) {
    if (!cprCanvas) return null;
    const rect = cprCanvas.getBoundingClientRect();
    const xCss = ev.clientX - rect.left;
    // VTK display origin is bottom-left; DOM origin is top-left
    const yCss = rect.height - (ev.clientY - rect.top);
    const sx = cprCanvas.width / rect.width;
    const sy = cprCanvas.height / rect.height;
    return [xCss * sx, yCss * sy, 0.0];
}

/**
 * World -> CPR local coordinates (actor space)
 * @param {number[]} worldPos - World position [x, y, z].
 * @param {vtkImageSlice} actor - The CPR image slice actor.
 * @returns {number[]|null} - CPR local coordinates [x, y, z] or null if failed.
 */
function worldToCPRCoordinates(worldPos, actor) {
    if (!actor || !actor.getUserMatrix) {
        return null;
    }

    const actorMatrix = actor.getUserMatrix();
    if (!actorMatrix) {
        return null;
    }

    const invMat = mat4.create();
    if (!mat4.invert(invMat, actorMatrix)) {
        return null;
    }

    const p = vec3.fromValues(worldPos[0], worldPos[1], worldPos[2]);
    const cpr = vec3.create();
    vec3.transformMat4(cpr, p, invMat);
    return cpr;
}

/**
 * Remove an original control point index from the spline world control point data.
 * Also removes corresponding entries in the radial and Y offset arrays.
 * @param {number} pointIndex - Centerline point index to remove.
 * @param {BranchControlData} branchControlData - Branch control data containing spline world control points and offset arrays.
  */
function removeOriginalControlPointIndex(
    pointIndex,
    branchControlData,
) {
    if (
        !branchControlData.splineWorldControlPointData ||
        !branchControlData.splineWorldControlPointData.worldControlPointIndices.length
    ) {
        return;
    }

    const oldIndices = branchControlData.splineWorldControlPointData.worldControlPointIndices;

    const oldRadial =
        branchControlData.controlRadialOffsetR &&
            branchControlData.controlRadialOffsetR.length === oldIndices.length
            ? branchControlData.controlRadialOffsetR
            : new Array(oldIndices.length).fill(0.0);

    const oldY =
        branchControlData.controlOffsetYCPR &&
            branchControlData.controlOffsetYCPR.length === oldIndices.length
            ? branchControlData.controlOffsetYCPR
            : new Array(oldIndices.length).fill(0.0);

    const oldWorld =
        branchControlData.controlWorldOffsets &&
        branchControlData.controlWorldOffsets.length === oldIndices.length
            ? branchControlData.controlWorldOffsets
            : new Array(oldIndices.length).fill(null);

    const newIndices = [];
    const newRadial = [];
    const newY = [];
    const newWorld = [];

    for (let i = 0; i < oldIndices.length; i++) {
        const idx = oldIndices[i];
        if (idx === pointIndex) {
            continue; // skip removed
        }
        newIndices.push(idx);
        newRadial.push(oldRadial[i]);
        newY.push(oldY[i]);
        newWorld.push(oldWorld[i]);
    }

    branchControlData.splineWorldControlPointData.worldControlPointIndices = newIndices;
    branchControlData.controlRadialOffsetR = newRadial;
    branchControlData.controlOffsetYCPR = newY;
    branchControlData.controlWorldOffsets = newWorld;
}


/**
 * Set up the add/remove control point mouse handler for the CPR canvas.
 * @param {HTMLCanvasElement} cprCanvas - The CPR canvas element.
 * @param {React.RefObject.<Object>} currentCPRFrameRef - Ref to current CPR frame data.
 * @param {vtkPointPicker} pointPicker - The VTK point picker.
 * @param {vtkRenderer} renderer - The VTK renderer.
 * @param {vtkImageCPRMapper} mapper - The CPR image mapper.
 * @param {React.RefObject.<Map<string, BranchControlData>>} branchControlDataRef - Ref to map of branch control data.
 * @param {React.RefObject.<Union>} selectedBranchRef - Ref to the currently selected branch DU.
 * @param {vtkSplineWidget} splineWidget - The spline widget.
 * @param {vtkRenderWindowInteractor} interactor - The render window interactor.
 * @param {vtkRenderWindow} renderWindow - The render window.
 * @param {vtkPlaneManipulator} planeManipulator - The plane manipulator for the spline handles.
 * @param {vtkImageSlice} actor - The CPR image slice actor.
 * @param {React.RefObject.<function>} onCenterlinePointEditRef - Ref to callback function for centerline point edits.
 * @param {vtkPolyData} liveCenterline - The live centerline polydata used by the mapper.
 * @param {Object.<string, vtkPolyData>} centerlinesPolyData - Map of branch names to original centerline polydata.
 * @param {Object} eventNames - Event name map from component props.
 * @param {vtkResliceCursorWidget} resliceCursorWidget - The reslice cursor widget.
 * @param {Object} resliceCursorWidgetState - The reslice cursor widget state.
 * @param {vtkCPRManipulator} cprManipulator - The CPR manipulator.
 * @param {Object} cprObjects - CPR objects for state tracking.
 * @param {React.RefObject.<function>} onAngleChangedRef - Ref to angle changed callback.
 */
function setUpAddOrRemoveControlPointHandler(
    cprCanvas,
    currentCPRFrameRef,
    pointPicker,
    renderer,
    mapper,
    branchControlDataRef,
    selectedBranchRef,
    splineWidget,
    interactor,
    renderWindow,
    planeManipulator,
    actor,
    onCenterlinePointEditRef,
    liveCenterline,
    centerlinesPolyData,
    eventNames,
    resliceCursorWidget,
    resliceCursorWidgetState,
    cprManipulator,
    cprObjects,
    onAngleChangedRef,
) {
    function handleAddOrRemoveControlPoint(ev) {
        const branchControlData = branchControlDataRef.current.get(getDUName(selectedBranchRef.current))

        // Only react to left mouse button.
        if (ev.button !== 0) {
            return;
        }

        if (!currentCPRFrameRef.current) {
            return;
        }

        const displayPoint = displayCoordsFromMouseEvent(ev, cprCanvas);
        if (!displayPoint) {
            log('Centerline add-point: could not compute display coords.');
            return;
        }

        // 1) Use vtkPointPicker to get world position on the CPR view
        pointPicker.pick(displayPoint, renderer);
        const worldPos = pointPicker.getPickPosition();
        if (!worldPos || worldPos.length !== 3) {
            log('Centerline add-point: no valid pick on CPR view.');
            return;
        }
        log('Centerline add-point: world pick position =', worldPos);

        // 2) Convert pick to CPR local coordinates
        log("picked point world :", worldPos)
        const pickCPR = worldToCPRCoordinates(worldPos, actor);
        log("picked point CPR: ", pickCPR)
        if (!pickCPR) {
            log('Centerline add-point: could not transform pick to CPR space.');
            return;
        }

        // 3) Compare against oriented centerline points in CPR local space.
        //    The oriented centerline already has edits applied (via computeEditedCenterlinePositions),
        //    so computeCPRCoordsForCenterlineIndex gives positions that match the rendered image.
        const orientedCL = mapper.getOrientedCenterline();
        if (!orientedCL) {
            log('Centerline add-point: mapper.getOrientedCenterline() returned null.');
            return;
        }

        const pts = orientedCL.getPoints();
        if (!pts) {
            log('Centerline add-point: oriented centerline has no points.');
            return;
        }

        const data = pts.getData();
        const nPts = pts.getNumberOfPoints();

        const params = buildCPRParams(mapper, orientedCL, currentCPRFrameRef.current);

        let bestId = -1;
        let bestDist2 = Infinity;
        let bestWorldCL = null;
        let bestCPRCL = null;

        for (let i = 0; i < nPts; i++) {
            // Use base CPR coords directly, as the oriented centerline already includes edits.
            const cprCL = computeCPRCoordsForCenterlineIndex(
                i,
                orientedCL,
                params
            );
            if (!cprCL) continue;

            const dx = pickCPR[0] - cprCL[0];
            const dy = pickCPR[1] - cprCL[1];
            const d2 = dx * dx + dy * dy;

            if (d2 < bestDist2) {
                bestDist2 = d2;
                bestId = i;
                bestCPRCL = cprCL;
                bestWorldCL = [
                    data[3 * i],
                    data[3 * i + 1],
                    data[3 * i + 2],
                ];
            }
        }

        if (bestId < 0) {
            log('Centerline add-point: no nearest CPR-space point found.');
            return;
        }

        const bestDist = Math.sqrt(bestDist2);
        const maxDistanceMm = 2.0;

        log('picked point world:', worldPos);
        log('picked point CPR:', pickCPR);
        log('picked centerline point world:', bestWorldCL);
        log('picked centerline point CPR:', bestCPRCL);
        log(
            'Centerline add-point (CPR-space): nearest point is',
            { id: bestId, world_coord: bestWorldCL, cpr_distance_mm: bestDist }
        );

        if (bestDist > maxDistanceMm) {
            log(
                `Centerline add-point: nearest CPR-space point is ${bestDist.toFixed(
                    2
                )} mm away (> ${maxDistanceMm} mm). Click closer to the green line.`
            );
            return;
        }
        // SUCCESS: point within tolerance on CPR plane
        log('Accepted CPR-plane centerline pick within 1mm:', {
            id: bestId,
            cpr_distance_mm: bestDist,
        });

        // ADD / REMOVE logic based on proximity in *index* space
        const nearbyIdx = findNearbyControlPointIndex(bestId, 3, branchControlData.splineWorldControlPointData.worldControlPointIndices);

        if (nearbyIdx !== null) {
            // Click near an existing control point -> remove it
            removeOriginalControlPointIndex(
                nearbyIdx,
                branchControlData,
            );
            log(
                'Removed existing control point near picked id:',
                { pickedId: bestId, removedId: nearbyIdx }
            );
        } else {
            // Initialize the new control point with the current interpolated offset at
            // this index.
            const initialR = getRadialOffsetForCenterlineIndex(
                bestId,
                branchControlData.splineWorldControlPointData.worldControlPointIndices,
                branchControlData.controlRadialOffsetR
            );

            // Use the interpolated world-space offset. If no world offsets are stored yet
            // (legacy data), derive the world offset from the radial offset and the
            // orientation at this centerline point.
            let initialWorldOffset = getWorldOffsetForCenterlineIndex(
                bestId,
                branchControlData.splineWorldControlPointData.worldControlPointIndices,
                branchControlData.controlWorldOffsets
            );

            const hasWorldOffset = Array.isArray(initialWorldOffset) &&
                (initialWorldOffset[0] !== 0 || initialWorldOffset[1] !== 0 || initialWorldOffset[2] !== 0);

            if (!hasWorldOffset && initialR !== 0.0) {
                const addOrientations = orientedCL?.getOrientations?.();
                const addTangentDir = mapper.getTangentDirection();
                const addQ = addOrientations?.[bestId];
                if (addQ && addTangentDir) {
                    const addWorldDir = vec3.create();
                    vec3.transformQuat(addWorldDir, addTangentDir, addQ);
                    const addLen = vec3.length(addWorldDir);
                    if (addLen > 0) {
                        vec3.normalize(addWorldDir, addWorldDir);
                        initialWorldOffset = [
                            addWorldDir[0] * initialR,
                            addWorldDir[1] * initialR,
                            addWorldDir[2] * initialR,
                        ];
                    }
                }
            }

            addOriginalControlPointIndex(
                bestId,
                initialR,
                branchControlData,
                initialWorldOffset,
            );
            log('Added control point at centerline id', bestId);
        }

        // Restore the unedited centerline, then re-apply all edits
        // so the mapper's oriented centerline reflects the updated control data.
        const branchName = getDUName(selectedBranchRef.current);
        const originalPolyData = centerlinesPolyData[branchName];
        restoreOriginalCenterline(liveCenterline, originalPolyData);
        {
            const editedPts = computeEditedCenterlinePositions(originalPolyData, branchControlData, mapper.getTangentDirection());
            liveCenterline.getPoints().setData(editedPts, 3);
            liveCenterline.modified();
        }

        updateDistanceAndDirection(
            {
                renderWindow,
                renderer,
                interactor,
                mapper,
                actor,
                resliceCursorWidget,
                resliceCursorWidgetState,
                cprManipulator,
                planeManipulator,
                splineWidget,
                cprObjects,
            },
            onAngleChangedRef.current,
            branchControlData,
            currentCPRFrameRef,
            branchName,
            true,
        );

        notifyStraightenedView(eventNames, getDUName(selectedBranchRef.current), branchControlData);
        notifyOrthogonalViews(eventNames, getDUName(selectedBranchRef.current), liveCenterline);

        onCenterlinePointEditRef.current();

        ev.preventDefault();
    }

    return handleAddOrRemoveControlPoint;
}

/**
 * @typedef {object} EventHandler
 * @property {string} eventType - The type of the event (e.g., 'mousedown', 'mousemove').
 * @property {function} handler - The event handler function.
 * @property {HTMLElement|Window} container - The DOM container or window to which the event handler is attached.
 */

/**
 * Set up CPR spline handle picking and dragging via DOM mouse events.
 * @param {object} params
 * @param {HTMLElement} params.container - The DOM container of the CPR view.
 * @param {vtkRenderer} params.renderer - The vtkRenderer for the CPR view.
 * @param {vtkRenderWindow} params.renderWindow - The vtkRenderWindow for the CPR view.
 * @param {vtkSplineWidget} params.splineWidget - The spline widget to manipulate.
 * @param {import('vtk.js/Sources/Widgets/Core/WidgetManager/WidgetInstance')} params.widgetManager - The widget manager.
 * @param {vtkPointPicker} params.pointPicker - The VTK point picker.
 * @param {React.RefObject.<Object>} params.currentCPRFrameRef - Ref to current CPR frame data.
 * @param {React.RefObject.<Map<string, BranchControlData>>} params.branchControlDataRef - Ref to map of branch control data.
 * @param {React.RefObject.<Union>} params.selectedBranchRef - Ref to selected branch.
 * @param {vtkRenderWindowInteractor} params.interactor - The render window interactor.
 * @param {vtkPlaneManipulator} params.planeManipulator - The plane manipulator for the spline handles.
 * @param {vtkImageCPRMapper} params.mapper - The CPR image mapper.
 * @param {vtkImageSlice} params.actor - The CPR image slice actor.
 * @param {React.RefObject.<function>} params.onCenterlinePointEditRef - Ref to callback function for centerline point edits.
 * @param {vtkPolyData} params.liveCenterline - The live centerline polydata used by the mapper.
 * @param {Object.<string, vtkPolyData>} params.centerlinesPolyData - Map of branch names to original centerline polydata.
 * @return {Array<EventHandler>} Array of event handlers with their types and containers for cleanup.
 */

/**
 * Return the spline control-point handle nearest to a world position, or null when the
 * closest one is outside the pick tolerance (CONTROL_POINT_PICK_TOLERANCE_WORLD_SQ).
 * Shared by the spline handle drag (setupSplineCPRPicking) and the measurement-line drag,
 * which defers to a control point when one is under the cursor.
 *
 * @param {number[]|null|undefined} worldPos - Cursor position in world coordinates.
 * @param {Object|null|undefined} splineWidget - The spline widget whose handles to test.
 * @returns {Object|null} The nearest handle within tolerance, or null.
 */
function findClosestSplineHandle(worldPos, splineWidget) {
    if (!worldPos || worldPos.length !== 3 || !splineWidget || typeof splineWidget.getWidgetState !== 'function') {
        return null;
    }

    const widgetState = splineWidget.getWidgetState();
    const handles =
        widgetState && typeof widgetState.getHandleList === 'function'
            ? widgetState.getHandleList() || []
            : [];

    let bestHandle = null;
    let bestDist2 = Infinity;

    for (const handle of handles) {
        if (typeof handle.getOrigin !== 'function') {
            continue;
        }
        const origin = handle.getOrigin();
        const dx = origin[0] - worldPos[0];
        const dy = origin[1] - worldPos[1];
        const dz = origin[2] - worldPos[2];
        const dist2 = dx * dx + dy * dy + dz * dz;
        if (dist2 < bestDist2) {
            bestDist2 = dist2;
            bestHandle = handle;
        }
    }

    if (bestDist2 > CONTROL_POINT_PICK_TOLERANCE_WORLD_SQ) {
        return null;
    }

    return bestHandle;
}

function setupSplineCPRPicking({
    container,
    renderer,
    renderWindow,
    splineWidget,
    widgetManager,
    pointPicker,
    currentCPRFrameRef,
    branchControlDataRef,
    selectedBranchRef,
    interactor,
    planeManipulator,
    mapper,
    actor,
    onCenterlinePointEditRef,
    liveCenterline,
    centerlinesPolyData,
    eventNames,
    handleIndexList,
    isCenterlineEditableRef,
}) {
    if (!container || !renderer || !renderWindow || !splineWidget) {
        log('CPR picking (DOM) disabled: missing dependencies');
        return [];
    }

    const canvas = container.querySelector('canvas');
    if (!canvas) {
        log('CPR picking (DOM) disabled: no canvas in container');
        return [];
    }

    log('CPR picking (DOM): installing handlers');

    let isDragging = false;
    let pickedHandle = null;
    let dragStartCPRY = null;

    function worldFromMouseEvent(ev) {
        const displayPoint = displayCoordsFromMouseEvent(ev, canvas);
        pointPicker.pick(displayPoint, renderer);
        const worldPos = pointPicker.getPickPosition();

        if (!worldPos || worldPos.length !== 3) {
            return null;
        }

        return worldPos;
    }

    function onMouseDown(ev) {
        if (ev.button !== 0) {
            return;
        }

        // Handles are only draggable while centerline editing is the active mode.
        if (isCenterlineEditableRef && isCenterlineEditableRef.current === false) {
            return;
        }

        const worldPos = worldFromMouseEvent(ev);
        if (!worldPos) {
            return;
        }
        log('onMouseDown: mouse down at world pos', worldPos);

        const handle = findClosestSplineHandle(worldPos, splineWidget);
        if (!handle) {
            pickedHandle = null;
            isDragging = false;
            return;
        }

        pickedHandle = handle;
        isDragging = true;

        // Stores the along-centerline (CPR y) coordinate of the handle at drag start.
        // Used to enforce horizontal-only movement: the y component is locked.
        const handleOrigin = handle.getOrigin();
        const handleCPR = worldToCPRCoordinates(handleOrigin, actor);
        dragStartCPRY = handleCPR ? handleCPR[1] : null;

        // Disable window/level interaction while dragging handle.
        interactor.getInteractorStyle().setEnabled(false);

        log(
            'CPR (DOM): start drag handle at',
            typeof pickedHandle.getOrigin === 'function'
                ? pickedHandle.getOrigin()
                : '(no origin)'
        );

        ev.preventDefault();
    }

    function onMouseMove(ev) {
        if (!isDragging || !pickedHandle) {
            return;
        }

        const worldPos = worldFromMouseEvent(ev);
        if (!worldPos) {
            return;
        }

        // --- Project mouse hit to the CPR plane via world -> CPR -> world ---

        const cpr = worldToCPRCoordinates(worldPos, actor);
        if (!cpr) {
            return;
        }

        let x = cpr[0];
        let y = dragStartCPRY ?? cpr[1];

        if (currentCPRFrameRef.current) {
            const { width, height } = currentCPRFrameRef.current;
            if (width != null) {
                x = clamp(x, 0, width);
            }
            if (height != null) {
                y = clamp(y, 0, height);
            }
        }

        const cprPoint = [x, y, 0.0];

        const actorMatrix =
            actor && typeof actor.getUserMatrix === 'function'
                ? actor.getUserMatrix()
                : null;
        if (!actorMatrix) {
            return;
        }

        const worldOnPlane = [0, 0, 0];
        vec3.transformMat4(worldOnPlane, cprPoint, actorMatrix);

        // Live feedback: move the handle to the CPR-plane-projected point
        if (typeof pickedHandle.setOrigin === 'function') {
            log('CPR (DOM): moving dragged handle to', worldOnPlane);
            pickedHandle.setOrigin(worldOnPlane[0], worldOnPlane[1], worldOnPlane[2]);
        }

        // The following ViTAA code doesn't actually do anything. Keeping it commented out for reference until we get the updates to the PoC.
        /*
                // --- NEW: store CPR-space delta X for this control point ---
                if (
                    handleIndex !== -1 &&
                    Array.isArray(worldControlPointIndices) &&
                    handleIndex < worldControlPointIndices.length &&
                    currentCPRFrameRef.current
                ) {
                    const orientedCenterline =
                        typeof mapper.getOrientedCenterline === 'function'
                            ? mapper.getOrientedCenterline()
                            : null;

                    if (orientedCenterline) {
                        const { width, height } = currentCPRFrameRef.current;
                        const distancesToFirstPoint =
                            orientedCenterline.getDistancesToFirstPoint();
                        const orientations =
                            typeof orientedCenterline.getOrientations === 'function'
                                ? orientedCenterline.getOrientations() || null
                                : null;
                        const tangentDirection =
                            typeof mapper.getTangentDirection === 'function'
                                ? mapper.getTangentDirection()
                                : null;
                        const centerPoint =
                            typeof mapper.getCenterPoint === 'function'
                                ? mapper.getCenterPoint()
                                : null;

                        const centerlineIndex = worldControlPointIndices[handleIndex];

                        if (typeof centerlineIndex === 'number') {
                            const baseCPR = computeCPRCoordsForCenterlineIndex(
                                centerlineIndex,
                                orientedCenterline,
                                {
                                    width,
                                    height,
                                    tangentDirection,
                                    centerPoint,
                                    distancesToFirstPoint,
                                    orientations,
                                }
                            );

                            if (baseCPR) {
                                const baseX = baseCPR[0];

                                // r is now "how far from the centerline's CPR X" at this index
                                const r = x - baseX;
                            }
                        }
                    }
                }
        */

        // Recompute the stretched view from the edited centerline during drag.
        const branchControlData = branchControlDataRef.current.get(getDUName(selectedBranchRef.current));
        if (branchControlData) {
            const branchName = getDUName(selectedBranchRef.current);
            const originalPolyData = centerlinesPolyData[branchName];

            // Restore original so offset computation sees unedited positions.
            restoreOriginalCenterline(liveCenterline, originalPolyData);
            updateOffsetsFromHandles(branchControlData, currentCPRFrameRef, mapper, actor, splineWidget, pickedHandle);

            // Apply edited positions for rendering.
            {
                const editedPts = computeEditedCenterlinePositions(originalPolyData, branchControlData, mapper.getTangentDirection());
                liveCenterline.getPoints().setData(editedPts, 3);
                liveCenterline.modified();
            }
        }

        if (widgetManager && typeof widgetManager.render === 'function') {
            widgetManager.render();
        }
        renderWindow.render();

        notifyStraightenedView(eventNames, getDUName(selectedBranchRef.current), branchControlData);
        notifyOrthogonalViews(eventNames, getDUName(selectedBranchRef.current), liveCenterline);

        ev.preventDefault();
    }


    function onMouseUp(ev) {
        if (!isDragging) {
            return;
        }

        log('CPR (DOM): end drag');
        isDragging = false;
        dragStartCPRY = null;
        const draggedHandle = pickedHandle;
        pickedHandle = null;

        // Re-enable window/level interaction after dragging handle.
        interactor.getInteractorStyle().setEnabled(true);

        // Reset the interactor's button state by simulating a button release.
        // This ensures the interactor doesn't think the mouse button is still pressed.
        const rect = canvas.getBoundingClientRect();
        const x = ev.clientX - rect.left;
        const y = rect.height - (ev.clientY - rect.top);
        interactor.leftButtonReleaseEvent({
            position: { x, y, z: 0 },
            controlKey: ev.ctrlKey,
            altKey: ev.altKey,
            shiftKey: ev.shiftKey,
        });

        const branchControlData = branchControlDataRef.current.get(getDUName(selectedBranchRef.current));
        const branchName = getDUName(selectedBranchRef.current);
        const originalPolyData = centerlinesPolyData[branchName];

        // Restore original centerline so offset and spline computations see unedited positions.
        restoreOriginalCenterline(liveCenterline, originalPolyData);

        // 1) Update radial offsets based on where the handles ended up
        updateOffsetsFromHandles(
            branchControlData,
            currentCPRFrameRef,
            mapper,
            actor,
            splineWidget,
            draggedHandle,
        );

        log("controlRadialOffsetR after drag: ", branchControlData.controlRadialOffsetR);
        log("controlOffsetYCPR after drag: ", branchControlData.controlOffsetYCPR);

        // 2) Rebuild the spline from the canonical arrays
        updateSplineProjection(
            branchControlData,
            currentCPRFrameRef,
            mapper,
            splineWidget,
            interactor,
            renderWindow,
            planeManipulator,
            actor,
            handleIndexList,
        );

        // 3) Recompute the stretched view from the edited centerline.
        {
            const editedPts = computeEditedCenterlinePositions(originalPolyData, branchControlData, mapper.getTangentDirection());
            liveCenterline.getPoints().setData(editedPts, 3);
            liveCenterline.modified();
        }
        renderWindow.render();

        notifyStraightenedView(eventNames, getDUName(selectedBranchRef.current), branchControlData);
        notifyOrthogonalViews(eventNames, getDUName(selectedBranchRef.current), liveCenterline);

        onCenterlinePointEditRef.current();

        ev.preventDefault();
    }

    canvas.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    // Return event handlers and their respective container (i.e. canvas or window) to allow later removal if needed.
    return [
        { eventType: 'mousedown', handler: onMouseDown, container: canvas },
        { eventType: 'mousemove', handler: onMouseMove, container: window },
        { eventType: 'mouseup', handler: onMouseUp, container: window }
    ];
}

/**
 * Copy trunk control data from the source to the target control data.
 * @param {BranchControlData} sourceControlData - Source branch control data to copy from.
 * @param {BranchControlData} targetControlData - Target branch control data to copy to.
 */
function copyTrunkControlData(sourceControlData, targetControlData) {
    const {
        worldControlPoints: sourceWorldControlPoints,
        worldControlPointIndices: sourceWorldControlPointIndices,
        lastTrunkMasterIndex
    } = sourceControlData.splineWorldControlPointData;

    // Find the index of the last trunk master control point in worldControlPointIndices.
    let lastTrunkControlPointIndex = -1;
    for (let i = sourceWorldControlPointIndices.length - 1; i >= 0; i--) {
        const idx = sourceWorldControlPointIndices[i];
        if (idx <= lastTrunkMasterIndex) {
            lastTrunkControlPointIndex = i;
            break;
        }
    }

    // Copy the world control points and indices up to lastTrunkControlPointIndex.
    const trunkWorldControlPoints = sourceWorldControlPoints.slice(0, lastTrunkControlPointIndex + 1);
    const trunkWorldControlPointIndices = sourceWorldControlPointIndices.slice(0, lastTrunkControlPointIndex + 1);

    // Copy the control point offset arrays up to lastTrunkControlPointIndex.
    const trunkControlRadialOffsetR = sourceControlData.controlRadialOffsetR.slice(0, lastTrunkControlPointIndex + 1);
    const trunkControlOffsetYCPR = sourceControlData.controlOffsetYCPR.slice(0, lastTrunkControlPointIndex + 1);
    const trunkControlWorldOffsets = (sourceControlData.controlWorldOffsets || []).slice(0, lastTrunkControlPointIndex + 1);

    const {
        splineWorldControlPointData,
        controlRadialOffsetR,
        controlOffsetYCPR,
    } = targetControlData;
    const controlWorldOffsets = targetControlData.controlWorldOffsets || [];

    // Count number of trunk control points in the new spline data.
    let newTrunkControlPointCount = 0;
    for (let i = 0; i < splineWorldControlPointData.worldControlPointIndices.length; i++) {
        const idx = splineWorldControlPointData.worldControlPointIndices[i];
        if (idx <= splineWorldControlPointData.lastTrunkMasterIndex) {
            newTrunkControlPointCount++;
        } else {
            break;
        }
    }

    // Overwrite the beginning of the new spline data with the trunk data.
    splineWorldControlPointData.worldControlPoints.splice(
        0,
        newTrunkControlPointCount,
        ...trunkWorldControlPoints
    );
    splineWorldControlPointData.worldControlPointIndices.splice(
        0,
        newTrunkControlPointCount,
        ...trunkWorldControlPointIndices
    );
    controlRadialOffsetR.splice(
        0,
        newTrunkControlPointCount,
        ...trunkControlRadialOffsetR
    );
    controlOffsetYCPR.splice(
        0,
        newTrunkControlPointCount,
        ...trunkControlOffsetYCPR
    );
    controlWorldOffsets.splice(
        0,
        newTrunkControlPointCount,
        ...trunkControlWorldOffsets
    );
    targetControlData.controlWorldOffsets = controlWorldOffsets;
}

/**
 * Restore the live centerline to the original points from the given original polydata.
 * This must be called before any computation that depends on the mapper's oriented centerline
 * (e.g. updateOffsetsFromHandles, updateSplineProjection) to ensure they see unedited positions.
 *
 * @param {vtkPolyData} liveCenterline - The live centerline polydata used by the mapper.
 * @param {vtkPolyData} originalCenterlinePolyData - The original centerline polydata for the branch.
 */
function restoreOriginalCenterline(liveCenterline, originalCenterlinePolyData) {
    if (!liveCenterline || !originalCenterlinePolyData) {
        return;
    }
    liveCenterline.getPoints().setData(originalCenterlinePolyData.getPoints().getData(), 3);
    liveCenterline.modified();
}

/**
 * Compute edited world-space positions for all points in a branch centerline.
 * Pure computation — reads only from originalPolyData and branchControlData with no
 * side effects on any rendering objects. When world-space offsets are available they
 * are applied directly; otherwise falls back to the legacy per-point radial path using
 * the orientation tensors stored in the polydata.
 *
 * @param {vtkPolyData} originalPolyData - Original (unedited) polydata for the branch.
 * @param {BranchControlData} branchControlData - Control data with offsets to apply.
 * @param {number[]|null} tangentDirection - Mapper tangent direction (legacy radial path only).
 * @returns {Float32Array} Edited positions, length = nPoints * 3.
 */
function computeEditedCenterlinePositions(originalPolyData, branchControlData, tangentDirection) {
    const { worldControlPointIndices } = branchControlData.splineWorldControlPointData;
    const { controlRadialOffsetR, controlWorldOffsets } = branchControlData;

    const origPts = originalPolyData.getPoints();
    const nPts = origPts.getNumberOfPoints();
    const origPtsData = origPts.getData();
    const outPts = new Float32Array(3 * nPts);

    const hasWorldOffsets = Array.isArray(controlWorldOffsets) &&
        controlWorldOffsets.some(o => Array.isArray(o) && o.length === 3);

    if (hasWorldOffsets) {
        for (let i = 0; i < nPts; ++i) {
            const wo = getWorldOffsetForCenterlineIndex(i, worldControlPointIndices, controlWorldOffsets);
            outPts[3 * i]     = origPtsData[3 * i]     + wo[0];
            outPts[3 * i + 1] = origPtsData[3 * i + 1] + wo[1];
            outPts[3 * i + 2] = origPtsData[3 * i + 2] + wo[2];
        }
    } else {
        // Legacy path: per-point radial offset, direction from orientation tensors.
        const tensors = originalPolyData.getPointData().getTensors();
        const tensorData = tensors ? tensors.getData() : null;
        const tensorComponents = tensors ? tensors.getNumberOfComponents() : 0;

        const dir = [0, 0, 0];
        const localM3 = mat3.create();
        const localM4 = mat4.create();

        for (let i = 0; i < nPts; ++i) {
            let px = origPtsData[3 * i];
            let py = origPtsData[3 * i + 1];
            let pz = origPtsData[3 * i + 2];

            const r = getRadialOffsetForCenterlineIndex(i, worldControlPointIndices, controlRadialOffsetR);

            if (r !== 0.0 && tangentDirection && tensorData && tensorComponents === 16) {
                const offset = i * 16;
                for (let j = 0; j < 16; j++) {
                    localM4[j] = tensorData[offset + j];
                }
                mat3.fromMat4(localM3, localM4);
                vec3.transformMat3(dir, tangentDirection, localM3);
                const len = vec3.length(dir);
                if (len > 0) {
                    vec3.normalize(dir, dir);
                    px += dir[0] * r;
                    py += dir[1] * r;
                    pz += dir[2] * r;
                }
            }

            outPts[3 * i]     = px;
            outPts[3 * i + 1] = py;
            outPts[3 * i + 2] = pz;
        }
    }

    return outPts;
}

function buildEditedCenterlinePolyData(
    mapper,
    worldControlPointIndices,
    controlRadialOffsetR,
    controlWorldOffsets,
) {
    // Get the oriented centerline from the CPR mapper
    const orientedCenterline = mapper.getOrientedCenterline();
    const pts = orientedCenterline.getPoints();
    const nPts = pts.getNumberOfPoints();
    const ptsArray = pts.getData();

    // Per-point orientations (quaternions) from mapper
    const orientations = orientedCenterline.getOrientations() || null;

    // Global "radial" axis in the CPR local frame, provided by mapper
    const tangentDirection = mapper.getTangentDirection();

    if (!tangentDirection) {
        log('buildEditedCenterlinePolyData: no tangentDirection on mapper');
    }

    // Determine whether world-space offsets are available.
    const hasWorldOffsets = Array.isArray(controlWorldOffsets) &&
        controlWorldOffsets.some(o => Array.isArray(o) && o.length === 3);

    const outPts = new Float32Array(3 * nPts);

    const dir = [0, 0, 0];
    const delta = [0, 0, 0];
    const basePoint = [0, 0, 0];

    // 1) Compute world-space displaced points
    for (let i = 0; i < nPts; ++i) {
        basePoint[0] = ptsArray[3 * i];
        basePoint[1] = ptsArray[3 * i + 1];
        basePoint[2] = ptsArray[3 * i + 2];

        let px = basePoint[0];
        let py = basePoint[1];
        let pz = basePoint[2];

        if (hasWorldOffsets) {
            // Use angle-independent world-space offsets.
            const wo = getWorldOffsetForCenterlineIndex(i, worldControlPointIndices, controlWorldOffsets);
            px += wo[0];
            py += wo[1];
            pz += wo[2];
        } else {
            // Legacy path: radial offset rotated by current tangentDirection.
            const r = getRadialOffsetForCenterlineIndex(i, worldControlPointIndices, controlRadialOffsetR);

            if (tangentDirection && r !== 0.0) {
                const q = orientations?.[i];
                if (q) {
                    // Rotate CPR "X axis" (tangentDirection) into world at this point
                    vec3.transformQuat(dir, tangentDirection, q);
                } else {
                    vec3.copy(dir, tangentDirection);
                }

                if (vec3.length(dir) > 0) {
                    vec3.normalize(dir, dir);
                    // NOTE: no sign flip here - use same convention as CPR mapper.
                    vec3.scale(delta, dir, r);
                    px += delta[0];
                    py += delta[1];
                    pz += delta[2];
                }
            }
        }

        outPts[3 * i] = px;
        outPts[3 * i + 1] = py;
        outPts[3 * i + 2] = pz;
    }

    // 2) Local smoothing around the active control point (±2 controls)
    // BUG: _activeControlPointIndex doesn't exist, so smoothing never happens.
    if (
        Array.isArray(worldControlPointIndices) &&
        typeof _activeControlPointIndex === 'number'
    ) {
        const nCtrl = worldControlPointIndices.length;
        if (nCtrl > 0) {
            const h0 = Math.max(0, Math.min(nCtrl - 1, _activeControlPointIndex));

            const hStart = Math.max(0, h0 - 2);
            const hEnd = Math.min(nCtrl - 1, h0 + 2);

            let clStart = worldControlPointIndices[hStart];
            let clEnd = worldControlPointIndices[hEnd];

            if (clStart > clEnd) {
                const tmp = clStart;
                clStart = clEnd;
                clEnd = tmp;
            }

            clStart = Math.max(0, Math.min(nPts - 1, clStart));
            clEnd = Math.max(0, Math.min(nPts - 1, clEnd));

            smoothCenterlineSegment(outPts, clStart, clEnd);
        }
    }

    // 3) Build new polydata with a single polyline
    const poly = vtkPolyData.newInstance();
    const vtkPts = vtkPoints.newInstance();
    vtkPts.setData(outPts, 3);
    poly.setPoints(vtkPts);

    const lines = new Uint32Array(nPts + 1);
    lines[0] = nPts;
    for (let i = 0; i < nPts; ++i) {
        lines[i + 1] = i;
    }
    poly.getLines().setData(lines);

    return poly;
}

/**
 * @param {Object} props
 * @param {Union} props.cprViewMode
 * @param {Union} props.selectedBranch - UnifiedDistanceBranch
 * @param {File[]} props.imageFiles
 * @param {function} props.onTouchStart
 * @param {function} props.onTouchEnd
 * @param {Object} props.interactionModeProps
 * @param {Object} props.screenshotProps
 * @param {boolean} props.isSliceScroll
 * @param {number} angleInDegrees
 * @param {function} props.onAngleChanged - Callback to invoke when the angle changes.
 * @param {boolean} [props.isVisible]
 * @param {vtkPolyData|null} [props.wallCenterline]
 * @param {Union} props.cprControlPointMode
 * @param {string} viewportId
 * @param {Union} props.maybeActiveMeasurement
 * @param {function} props.onVisualizationReady - Callback to invoke when the visualization is ready.
 * @param {Object} [props.centerlineColors] - Optional colors for the centerline overlays.
 * @param {[number, number, number]} [props.centerlineColors.stretchedCenterline]
 * @param {[number, number, number]} [props.centerlineColors.straightenedCenterline]
 */

/**
 * Install DOM handlers for dragging horizontal measurement guide lines.
 * Maps cursor movement to a branch index and updates the measurement plane
 * through onMeasurementLineDraggedRef. Returns a cleanup function.
 *
 * @returns {() => void} Cleanup that removes the listeners and any cursor classes.
 */
function setupMeasurementLineDrag({
    container,
    context,
    currentCPRFrameRef,
    selectedBranchRef,
    onMeasurementLineDraggedRef,
}) {
    if (!container) {
        return () => {};
    }

    ensureMeasurementLineDragCursorStyle();

    let isDragging = false;
    let dragRole = null;
    let lastDispatchedIndex = null;

    const getCanvas = () => container.querySelector('canvas');

    // A single reusable world->display coordinate converter, so the hover hit-test does
    // not allocate a vtkCoordinate on every mouse move. Disposed in the cleanup below.
    const worldToDisplayCoord = vtkCoordinate.newInstance();
    worldToDisplayCoord.setCoordinateSystemToWorld();

    // Return the measurement-line descriptor whose on-screen Y is within the pick
    // tolerance of the mouse event, or null when none is close enough.
    function findLineNearEvent(ev) {
        const ctx = context.current;
        const cprObjects = ctx?.cprObjects;
        const renderer = ctx?.renderer;
        const lines = cprObjects?.stretchedMeasurementLines;
        const canvas = getCanvas();

        if (!renderer || !canvas || !Array.isArray(lines) || !lines.length) {
            return null;
        }

        const display = displayCoordsFromMouseEvent(ev, canvas);
        if (!display) {
            return null;
        }
        const mouseY = display[1];

        let closestLine = null;
        let closestDistance = Infinity;

        for (const line of lines) {
            const points = line?.polyData?.getPoints?.();
            if (!points || points.getNumberOfPoints() < 1) {
                continue;
            }
            const worldPoint = points.getPoint(0);
            worldToDisplayCoord.setValue(worldPoint[0], worldPoint[1], worldPoint[2]);
            const displayPoint = worldToDisplayCoord.getComputedDoubleDisplayValue(renderer);
            const distance = Math.abs(displayPoint[1] - mouseY);
            if (distance < closestDistance) {
                closestDistance = distance;
                closestLine = line;
            }
        }

        const dpr = window.devicePixelRatio || 1;
        if (closestDistance > MEASUREMENT_LINE_DRAG_TOLERANCE_CSS_PX * dpr) {
            return null;
        }
        return closestLine;
    }

    // Convert a mouse event to the unified branch index under the cursor.
    function unifiedIndexFromEvent(ev) {
        const ctx = context.current;
        const cprObjects = ctx?.cprObjects;
        const canvas = getCanvas();
        const frame = currentCPRFrameRef.current;

        if (!cprObjects?.mapper || !cprObjects.actor || !cprObjects.pointPicker || !canvas) {
            return null;
        }
        if (!frame || !Number.isFinite(frame.height)) {
            return null;
        }

        const displayPoint = displayCoordsFromMouseEvent(ev, canvas);
        cprObjects.pointPicker.pick(displayPoint, ctx.renderer);
        const worldPos = cprObjects.pointPicker.getPickPosition();
        if (!worldPos || worldPos.length !== 3) {
            return null;
        }

        const cpr = worldToCPRCoordinates(worldPos, cprObjects.actor);
        if (!cpr) {
            return null;
        }

        // Inverse of computeCPRCoordsForCenterlineIndex Y-mapping.
        const targetDistance = frame.height - cpr[1];

        const orientedCenterline = cprObjects.mapper.getOrientedCenterline?.();
        const distances = orientedCenterline?.getDistancesToFirstPoint?.();
        if (!distances || !distances.length) {
            return null;
        }

        let closestCenterlineIndex = 0;
        let closestDelta = Infinity;
        for (let i = 0; i < distances.length; i++) {
            const delta = Math.abs(distances[i] - targetDistance);
            if (delta < closestDelta) {
                closestDelta = delta;
                closestCenterlineIndex = i;
            }
        }

        const branchName = getDUName(selectedBranchRef.current);
        const branchIndices = cprObjects.branchOriginalIndices?.[branchName];
        if (!branchIndices || closestCenterlineIndex >= branchIndices.length) {
            return null;
        }
        const masterIndex = branchIndices[closestCenterlineIndex];

        const centerlineFieldData = cprObjects.wallCenterline?.getFieldData?.();
        if (!centerlineFieldData) {
            return null;
        }
        const branchLocal = tryConvertMasterToDistanceBranchIndex(masterIndex, centerlineFieldData);
        if (!branchLocal) {
            return null;
        }
        return branchLocal.index;
    }

    // Return true when a centerline spline control-point handle is under the cursor.
    function isControlPointNearEvent(ev) {
        const ctx = context.current;
        const cprObjects = ctx?.cprObjects;
        const renderer = ctx?.renderer;
        const canvas = getCanvas();

        if (!cprObjects?.splineWidget || !cprObjects.pointPicker || !renderer || !canvas) {
            return false;
        }

        const displayPoint = displayCoordsFromMouseEvent(ev, canvas);
        cprObjects.pointPicker.pick(displayPoint, renderer);
        const worldPos = cprObjects.pointPicker.getPickPosition();

        return findClosestSplineHandle(worldPos, cprObjects.splineWidget) !== null;
    }

    function setInteractorEnabled(enabled) {
        context.current?.interactor?.getInteractorStyle?.()?.setEnabled?.(enabled);
    }

    function onMouseDown(ev) {
        if (ev.button !== 0 || isDragging) {
            return;
        }
        if (typeof onMeasurementLineDraggedRef.current !== 'function') {
            return;
        }
        if (!container.contains(ev.target)) {
            return;
        }

        const line = findLineNearEvent(ev);
        if (!line) {
            return;
        }

        // Centerline control points win when they overlap a guide line: if a spline
        // handle is also under the cursor, defer to the spline drag (do not start a line
        // drag, do not stop propagation, so the canvas handler receives the event).
        if (isControlPointNearEvent(ev)) {
            return;
        }

        isDragging = true;
        dragRole = line.role;
        lastDispatchedIndex = Number.isFinite(line.unifiedIndex) ? line.unifiedIndex : null;

        // Suppress the vtk interactor's window/level (it acts through pointer events)
        // and block any spline handlers bound on the inner canvas.
        setInteractorEnabled(false);
        container.classList.remove('cpr-measurement-line-hover');
        container.classList.add('cpr-measurement-line-dragging');
        ev.stopPropagation();
        ev.preventDefault();
    }

    function onMouseMove(ev) {
        if (!isDragging) {
            // Hover affordance: show a "grab" cursor over a draggable line, unless a
            // centerline control point is also there (it takes priority, so no line cue).
            if (typeof onMeasurementLineDraggedRef.current !== 'function') {
                return;
            }
            const nearLine =
                container.contains(ev.target)
                && !!findLineNearEvent(ev)
                && !isControlPointNearEvent(ev);
            container.classList.toggle('cpr-measurement-line-hover', nearLine);
            return;
        }

        ev.stopPropagation();
        ev.preventDefault();

        const newIndex = unifiedIndexFromEvent(ev);
        if (newIndex == null || newIndex === lastDispatchedIndex) {
            return;
        }
        lastDispatchedIndex = newIndex;
        onMeasurementLineDraggedRef.current?.([dragRole, newIndex]);
    }

    function onMouseUp(ev) {
        if (!isDragging) {
            return;
        }
        isDragging = false;
        dragRole = null;
        lastDispatchedIndex = null;

        setInteractorEnabled(true);

        // The vtk interactor tracks its button state through pointer events,
        // independently of these mouse handlers. Because its interactor style was
        // disabled for the duration of the drag, the matching button-up was swallowed —
        // synthesize a release so it does not stay "pressed" and start adjusting
        // window/level on subsequent plain mouse moves.
        const interactor = context.current?.interactor;
        const canvas = getCanvas();
        if (interactor && canvas) {
            const rect = canvas.getBoundingClientRect();
            interactor.leftButtonReleaseEvent?.({
                position: { x: ev.clientX - rect.left, y: rect.height - (ev.clientY - rect.top), z: 0 },
                controlKey: ev.ctrlKey,
                altKey: ev.altKey,
                shiftKey: ev.shiftKey,
            });
        }

        container.classList.remove('cpr-measurement-line-dragging');
        ev.stopPropagation();
        ev.preventDefault();
    }

    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('mousemove', onMouseMove, true);
    window.addEventListener('mouseup', onMouseUp, true);

    return () => {
        window.removeEventListener('mousedown', onMouseDown, true);
        window.removeEventListener('mousemove', onMouseMove, true);
        window.removeEventListener('mouseup', onMouseUp, true);
        container.classList.remove('cpr-measurement-line-hover', 'cpr-measurement-line-dragging');
        worldToDisplayCoord.delete?.();
    };
}

const CprVisualization = (
    {
        viewportId,
        cprViewMode,
        selectedBranch,
        imageFiles: imageFiles,
        onTouchStart,
        onTouchEnd,
        interactionModeProps,
        screenshotProps,
        isSliceScroll,
        angleInDegrees,
        onAngleChanged,
        isVisible = true,
        wallCenterline = null,
        onCenterlinePointEdit,
        eventNames,
        cprControlPointMode,
        imageLoader: imageLoader,
        onCenterlineVtpExported,
        maybeActiveMeasurement,
        onMeasurementLineDragged,
        onStretchedCprControlPointsExported,
        onReady,
        onVisualizationReady,
        onLiveCenterlineChanged,
        centerlineColors = {},
        isCenterlineEditable = true,
    }) => {

    const containerRef = useRef(null);
    const context = useRef(null);
    const onAngleChangedRef = useRef(onAngleChanged);
    const addOrRemoveControlPointHandlerRef = useRef(null);
    const onCenterlineVtpExportedRef = useRef(onCenterlineVtpExported);
    const maybeActiveMeasurementRef = useRef(maybeActiveMeasurement);
    const onStretchedCprControlPointsExportedRef = useRef(onStretchedCprControlPointsExported);
    const onMeasurementLineDraggedRef = useRef(onMeasurementLineDragged);
    const onReadyRef = useRef(onReady);
    const onLiveCenterlineChangedRef = useRef(onLiveCenterlineChanged);
    const isCenterlineEditableRef = useRef(isCenterlineEditable);

    // Sync the editable state synchronously on every render.
    isCenterlineEditableRef.current = isCenterlineEditable;
    if (context.current?.cprObjects) {
        context.current.cprObjects.isCenterlineEditable = isCenterlineEditable;
    }

    /** @type {React.MutableRefObject<Map<string, BranchControlData | null>>} */
    const branchControlDataRef = useRef(
        new Map([
            [CenterlineDistanceBranchSelection.LEFT, null],
            [CenterlineDistanceBranchSelection.RIGHT, null],
            [CenterlineDistanceBranchSelection.CUSP, null],
        ])
    );

    const angleInDegreesRef = useRef(angleInDegrees);
    const selectedBranchRef = useRef(selectedBranch);
    const previousSelectedBranchRef = useRef(null);

    const currentCPRFrameRef = useRef({
        worldTangent: null,
        worldNormal: null,
        worldBitangent: null,
        worldWidgetCenter: null,
        distance: null, // current distance along centerline where CPR plane is built
        height: null, // CPR mapper height
        width: null, // CPR mapper width
        worldActorTranslation: null, // translation component of actor user matrix
    });
    const onCenterlinePointEditRef = useRef(onCenterlinePointEdit);

    /** @type {React.MutableRefObject<Map<string, CommittedChanges | null>>} */
    const committedChangesRef = useRef(
        new Map([
            [CenterlineDistanceBranchSelection.LEFT, null],
            [CenterlineDistanceBranchSelection.RIGHT, null],
            [CenterlineDistanceBranchSelection.CUSP, null],
        ])
    );

    const isStraightened = getDUName(cprViewMode) === CprViewMode.STRAIGHTENED;
    const extraZoom = isStraightened ? 1.0 : 1.1;

    const stretchedCenterlineColor   = centerlineColors.stretchedCenterline ?? STRETCHED_CENTERLINE_COLOR;
    const straightenedCenterlineColor = centerlineColors.straightenedCenterline ?? STRAIGHTENED_CENTERLINE_COLOR;

    /**
     * Composites the Konva layer onto the given canvas context for both straightened and stretched CPR views.
     * @param {CanvasRenderingContext2D} ctx - The canvas context to draw on.
     * @param {number} width - The canvas width.
     * @param {number} height - The canvas height.
     */
    function compositeKonvaLayer(ctx, width, height) {
        const konvaElements = context.current.stretchedKonvaElements;

        if (konvaElements?.stage) {
            const konvaStage = konvaElements.stage;
            const konvaCanvas = konvaStage.toCanvas();
            ctx.drawImage(konvaCanvas, 0, 0, width, height);
        }
    }

    function _captureCustomScreenshot() {
        return new Promise((resolve, reject) => {
            context.current.renderWindow.render();
            const viewportCanvas = context.current.genericRenderWindow
                .getApiSpecificRenderWindow()
                .getCanvas();
            if (!viewportCanvas) {
                reject(`Unable to find canvas for viewport ${viewportId}`);
                return;
            }

            const offscreenCanvas = document.createElement("canvas");
            // SVGs do not respect the scaling passed into CanvasContext.drawImage, so instead of doing a bunch of
            // futzing to force the SVG to scale up with the DPR, we will just scale the canvas down.
            // TODO: this approach results in a lower quality image, we should fix it later when we have more time.
            // TODO: Uncomment if we start using SVG annotations with CPR viewports.
            // const dpr = window.devicePixelRatio;
            // offscreenCanvas.width = viewportCanvas.width / dpr;
            // offscreenCanvas.height = viewportCanvas.height / dpr;

            // TODO: Remove if we start using SVG annotations with CPR viewports (see above).
            offscreenCanvas.width = viewportCanvas.width;
            offscreenCanvas.height = viewportCanvas.height;

            if (viewportCanvas.width === 0 || viewportCanvas.height === 0) {
                reject(`Trying to take a screenshot of a viewport ${viewportId}, not being shown`);
                return;
            }

            const offscreenCtx = offscreenCanvas.getContext("2d");
            offscreenCtx.drawImage(viewportCanvas, 0, 0, offscreenCanvas.width, offscreenCanvas.height);

            const finalizeScreenshot = () => {
                compositeKonvaLayer(offscreenCtx, offscreenCanvas.width, offscreenCanvas.height);
                const canvasInfo = {
                    viewportIndex: viewportId,
                    canvasData: offscreenCanvas.toDataURL()
                };
                resolve(canvasInfo);
            }

            finalizeScreenshot();

            // TODO: Uncomment if we start using SVG annotations with CPR viewports.
            // const svg = viewportCanvas.nextElementSibling;
            // if (!svg) {
            //     console.warn(`Unable to find annotations for viewport ${viewportId}, rendering without annotations`);
            //     finalizeScreenshot();
            //     return;
            // }
            //
            // const outerHTML = svg.outerHTML;
            // // VTK is evil and doesn't actually make the tag a valid SVG file.
            // const validSvg = outerHTML.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"');
            // const blob = new Blob([validSvg], {type: 'image/svg+xml'});
            // const url = URL.createObjectURL(blob);
            //
            // const svgImg = new Image();
            //
            // svgImg.onload = function() {
            //     offscreenCtx.drawImage(
            //         svgImg,
            //         0,
            //         0,
            //         offscreenCanvas.width,
            //         offscreenCanvas.height
            //     );
            //     URL.revokeObjectURL(url);
            //     finalizeScreenshot();
            // }
            //
            // const rejectHandler = function() {
            //     URL.revokeObjectURL(url);
            //     // Would it be better to fail here?
            //     console.warn(`Unable to render annotations for viewport ${viewportId}, rendering without annotations`);
            //     finalizeScreenshot();
            // }
            // svgImg.oncancel = rejectHandler;
            // svgImg.onabort = rejectHandler;
            // svgImg.onerror = rejectHandler;
            //
            // svgImg.width = offscreenCanvas.width;
            // svgImg.height = offscreenCanvas.height;
            // svgImg.src = url;
        });
    }

    function captureCustomScreenshot(ev) {
        if (ev.detail.viewportId !== viewportId) {
            return;
        }

        if (typeof ev.detail.callback !== 'function') {
            log("Invalid event detail for screenshot capture");
            return;
        }

        // Wait for a short delay to ensure all rendering is complete.
        setTimeout(async () => {
            try {
                const ret = await _captureCustomScreenshot();
                ev.detail.callback(ret);
            } catch (e) {
                log("captureCustomScreenshot failed", e);
            }
        }, 1000);
    }

    /**
     * Applies committed centerline edits to the wallCenterline polydata in-place.
     * This propagates edited centerline geometry to dependent components
     * (mesh overlay, measurement positioning, focal point calculations, etc.).
     *
     * @param {Map} committedChanges - The committed branch control data map.
     * @param {Object} centerlinesPolyData - Per-branch original centerline polydata.
     */
    function applyCommittedEditsToWallCenterline(committedChanges, centerlinesPolyData) {
        const { branchOriginalIndices, wallCenterline, mapper } =
            context.current?.cprObjects || {};

        if (!wallCenterline || !mapper || !branchOriginalIndices) return;

        const originalPoints = wallCenterline.getPoints();
        const originalArray = originalPoints?.getData();
        if (!originalArray?.length) return;

        const outCoords = new Float32Array(originalArray);
        const tangentDirection = mapper.getTangentDirection();

        committedChanges.forEach((committed, branch) => {
            if (!committed?.branchControlData) return;

            const indices = branchOriginalIndices[branch];
            if (!indices?.length) return;

            const editedPts = computeEditedCenterlinePositions(
                centerlinesPolyData[branch],
                committed.branchControlData,
                tangentDirection
            );

            if (editedPts.length !== indices.length * 3) return;

            for (let k = 0; k < indices.length; ++k) {
                const srcIdx = 3 * k;
                const dstIdx = 3 * indices[k];
                outCoords[dstIdx]     = editedPts[srcIdx];
                outCoords[dstIdx + 1] = editedPts[srcIdx + 1];
                outCoords[dstIdx + 2] = editedPts[srcIdx + 2];
            }
        });

        // Recompute normals for the updated positions.
        // VTP convention: stored normal = -forwardTangent, because CenterlineUtils.js reads them
        // back as `tangentsFromVTP = normals.map(x => -x)`.
        // PlaneIndexFilter uses the stored normal directly to orient the cross-section plane.
        // Left and Right branches both include the trunk; skip already-written master indices
        // so a future seed-direction change in one branch can't silently overwrite the other.
        const normalsArray = wallCenterline.getPointData().getArrayByName('Normals');
        let outNormals = null;
        if (normalsArray) {
            outNormals = new Float32Array(normalsArray.getData());
            const writtenNormals = new Set();
            for (const indices of Object.values(branchOriginalIndices)) {
                const n = indices.length;
                for (let i = 0; i < n; i++) {
                    const masterIdx = indices[i];
                    if (writtenNormals.has(masterIdx)) continue;
                    writtenNormals.add(masterIdx);
                    const prev = indices[Math.max(0, i - 1)];
                    const next = indices[Math.min(n - 1, i + 1)];
                    let tx = outCoords[3 * next]     - outCoords[3 * prev];
                    let ty = outCoords[3 * next + 1] - outCoords[3 * prev + 1];
                    let tz = outCoords[3 * next + 2] - outCoords[3 * prev + 2];
                    const len = Math.sqrt(tx * tx + ty * ty + tz * tz);
                    if (len > 1e-8) {
                        outNormals[3 * masterIdx]     = -tx / len;
                        outNormals[3 * masterIdx + 1] = -ty / len;
                        outNormals[3 * masterIdx + 2] = -tz / len;
                    }
                }
            }
            normalsArray.setData(outNormals, 3);
            normalsArray.modified();
        }

        // Recompute cumulative arc-length distances from the edited positions.
        // Each branch path is computed independently; trunk indices shared between Left and
        // Right branches produce the same values (same indices, same positions). Skip
        // already-written master indices to guard against any future divergence.
        let outDistances = null;
        const distancesArray = wallCenterline.getPointData().getArrayByName('Distances');
        if (distancesArray) {
            outDistances = new Float32Array(distancesArray.getData());
            const writtenDistances = new Set();
            for (const indices of Object.values(branchOriginalIndices)) {
                let cumDist = 0;
                for (let i = 0; i < indices.length; i++) {
                    if (i > 0) {
                        const curr = indices[i];
                        const prev = indices[i - 1];
                        const dx = outCoords[3 * curr]     - outCoords[3 * prev];
                        const dy = outCoords[3 * curr + 1] - outCoords[3 * prev + 1];
                        const dz = outCoords[3 * curr + 2] - outCoords[3 * prev + 2];
                        cumDist += Math.sqrt(dx * dx + dy * dy + dz * dz);
                    }
                    const masterIdx = indices[i];
                    if (writtenDistances.has(masterIdx)) continue;
                    writtenDistances.add(masterIdx);
                    outDistances[masterIdx] = cumDist;
                }
            }
            distancesArray.setData(outDistances, 1);
            distancesArray.modified();
        }

        // Recompute landmark arc-length distances from the updated Distances array.
        // Each Landmark_Lengths[i] = Distances[Landmark_IDs[i]] (cumulative arc-length to that landmark).
        let outLandmarkLengths = null;
        const landmarkIdsArray = wallCenterline.getFieldData().getArrayByName('Landmark_IDs');
        const landmarkLengthsArray = wallCenterline.getFieldData().getArrayByName('Landmark_Lengths');
        if (outDistances && landmarkIdsArray && landmarkLengthsArray) {
            const landmarkIdsData = landmarkIdsArray.getData();
            outLandmarkLengths = new Float32Array(landmarkLengthsArray.getData());
            for (let i = 0; i < landmarkIdsData.length; i++) {
                outLandmarkLengths[i] = outDistances[landmarkIdsData[i]];
            }
            landmarkLengthsArray.setData(outLandmarkLengths, 1);
            landmarkLengthsArray.modified();
        }

        // Update the local (Measurement viewport) wallCenterline in-place.
        wallCenterline.getPoints().setData(outCoords, 3);
        wallCenterline.modified();

        // Dispatch updated arrays so every wallCenterline instance (each viewport
        // has its own independent JS object loaded from the same VTP) can apply the same data.
        window.dispatchEvent(new CustomEvent('wallCenterlineUpdated', {
            detail: { outCoords, outNormals, outDistances, outLandmarkLengths }
        }));
    }

    function setUpConfirmCancelChangesHandler(
        centerlinesPolyData,
    ) {
        function cancelChanges() {
            const branchName = getDUName(selectedBranchRef.current);
            log('Cancelling changes and reverting to original control points for branch', branchName);

            const branchControlData = branchControlDataRef.current.get(branchName);
            const branchCommittedChanges = committedChangesRef.current.get(branchName);

            if (branchCommittedChanges) {
                // Revert to last committed changes (deep copy to avoid shared references).
                const { splineWorldControlPointData, controlRadialOffsetR, controlOffsetYCPR, controlWorldOffsets } = branchCommittedChanges.branchControlData;
                branchControlData.splineWorldControlPointData = structuredClone(splineWorldControlPointData);
                branchControlData.controlRadialOffsetR = structuredClone(controlRadialOffsetR);
                branchControlData.controlOffsetYCPR = structuredClone(controlOffsetYCPR);
                branchControlData.controlWorldOffsets = structuredClone(controlWorldOffsets || null);
            } else {
                // Revert to original control points from centerline.
                const originalBranchControlData = buildOriginalControlPointsFromPolyData(centerlinesPolyData[branchName]);
                branchControlDataRef.current.set(branchName, originalBranchControlData);
            }
            const { renderWindow } = context.current;
            const { mapper, cprManipulator, resliceCursorWidgetState, liveCenterline } = context.current.cprObjects;

            // Restore original centerline before recomputing.
            restoreOriginalCenterline(liveCenterline, centerlinesPolyData[branchName]);

            rotateView(angleInDegreesRef.current, mapper, cprManipulator, resliceCursorWidgetState);

            syncMapperDirectionMatrix(mapper, resliceCursorWidgetState, cprManipulator);
            {
                const editedPts = computeEditedCenterlinePositions(centerlinesPolyData[branchName], branchControlDataRef.current.get(branchName), mapper.getTangentDirection());
                liveCenterline.getPoints().setData(editedPts, 3);
                liveCenterline.modified();
            }

            const cprContext = createCPRUpdateContext(context.current);

            updateDistanceAndDirection(
                cprContext,
                onAngleChangedRef.current,
                branchControlDataRef.current.get(branchName),
                currentCPRFrameRef,
                branchName,
                true
            );

            renderWindow.render();

            notifyStraightenedView(eventNames, branchName, branchControlDataRef.current.get(branchName));
            if (context.current?.cprObjects?.liveCenterline) {
                notifyOrthogonalViews(eventNames, branchName, context.current.cprObjects.liveCenterline);
            }
        }

        window.addEventListener(eventNames.CancelChanges, cancelChanges);

        function confirmChanges(ev) {
            const branch = ev.detail;
            const branchName = getDUName(branch);
            log("Confirming changes for branch", branchName);

            const selectedBranchControlData = branchControlDataRef.current.get(branchName);

            committedChangesRef.current.forEach((committedChanges, otherBranch) => {
                if (otherBranch === branchName) {
                    // Commit the selected branch's control data.
                    committedChangesRef.current.set(
                        otherBranch,
                        {
                            branchControlData:
                            {
                                // Deep copy the data to avoid sharing references.
                                splineWorldControlPointData: structuredClone(selectedBranchControlData.splineWorldControlPointData),
                                controlRadialOffsetR: [...selectedBranchControlData.controlRadialOffsetR],
                                controlOffsetYCPR: [...selectedBranchControlData.controlOffsetYCPR],
                                    controlWorldOffsets: structuredClone(selectedBranchControlData.controlWorldOffsets || null),
                            }
                        }
                    );
                } else {
                    // For other branches, first update their committed changes with their own
                    // live branch control data, then sync trunk data from the selected branch.
                    const otherBranchControlData = branchControlDataRef.current.get(otherBranch);

                    if (otherBranchControlData) {
                        const updatedBranchControlData = {
                            splineWorldControlPointData: structuredClone(otherBranchControlData.splineWorldControlPointData),
                            controlRadialOffsetR: [...otherBranchControlData.controlRadialOffsetR],
                            controlOffsetYCPR: [...otherBranchControlData.controlOffsetYCPR],
                            controlWorldOffsets: structuredClone(otherBranchControlData.controlWorldOffsets || null),
                        };

                        // Copy trunk data from the selected branch to keep branches in sync.
                        copyTrunkControlData(selectedBranchControlData, updatedBranchControlData);

                        committedChangesRef.current.set(otherBranch, {
                            branchControlData: updatedBranchControlData
                        });
                    }
                }
            });

            // Propagate committed edits to the wallCenterline
            applyCommittedEditsToWallCenterline(committedChangesRef.current, centerlinesPolyData);
        }

        window.addEventListener(eventNames.ConfirmChanges, confirmChanges);

        function exportVtp() {
            log("Exporting centerline vtp");

            const { branchOriginalIndices, wallCenterline, mapper, centerlinesPolyData } = context.current.cprObjects;
            const src = wallCenterline;

            const originalPoints = src.getPoints();
            const originalArray = originalPoints?.getData?.();

            if (!originalArray || !originalArray.length) {
                log('saveEditedCenterlineAsVTP: empty original point data');
                return;
            }

            const outCoords = new Float32Array(originalArray);
            const tangentDirection = mapper.getTangentDirection();

            committedChangesRef.current.forEach((committedChanges, branch) => {
                if (!committedChanges || !committedChanges.branchControlData) {
                    return;
                }

                const indices = branchOriginalIndices[branch];
                if (!indices || !indices.length) {
                    return;
                }

                const editedPts = computeEditedCenterlinePositions(
                    centerlinesPolyData[branch],
                    committedChanges.branchControlData,
                    tangentDirection
                );

                if (editedPts.length !== indices.length * 3) {
                    log(
                        `Branch ${branch}: edited points length (${editedPts.length}) ` +
                        `does not match indices length (${indices.length * 3})`
                    );
                    return;
                }

                for (let k = 0; k < indices.length; ++k) {
                    const srcIdx = 3 * k;
                    const dstIdx = 3 * indices[k];

                    outCoords[dstIdx]     = editedPts[srcIdx];
                    outCoords[dstIdx + 1] = editedPts[srcIdx + 1];
                    outCoords[dstIdx + 2] = editedPts[srcIdx + 2];
                }
            });

            // Build an output polydata that is a copy of the original, but with updated point coordinates.
            const combined = vtkPolyData.newInstance();
            combined.shallowCopy(src);

            const outPoints = vtkPoints.newInstance();
            outPoints.setData(outCoords, 3);
            combined.setPoints(outPoints);

            // Extra safety: if combined is somehow invalid, don't hit the writer.
            if (!combined.getPoints()) {
                log('exportVtp: combined polydata is invalid, aborting write');
                return;
            }

            // Strip field data arrays to avoid writer.getRange() issues.
            const fieldData = combined.getFieldData();
            for (let i = fieldData.getNumberOfArrays() - 1; i >= 0; --i) {
                const arr = fieldData.getArrayByIndex(i);
                fieldData.removeArray(arr.getName());
            }

            // Write combined polydata.
            const writer = vtkXMLPolyDataWriter.newInstance();
            writer.setFormat(vtkXMLWriter.FormatTypes.BINARY);
            const fileContents = writer.write(combined);

            onCenterlineVtpExportedRef.current(fileContents);
        }

        window.addEventListener(eventNames.ExportVtp, exportVtp);

        /**
         * Export the committed stretched CPR spline control points as JSON.
         * This exports the branchControlData for all branches that have committed changes.
         */
        function exportStretchedCprControlPoints() {
            log("Exporting stretched CPR control points");

            const exportData = {
                version: "v1" // unused for now, future-proofing
            };

            committedChangesRef.current.forEach((committedChanges, branch) => {
                if (committedChanges && committedChanges.branchControlData) {
                    const { splineWorldControlPointData, controlRadialOffsetR, controlOffsetYCPR, controlWorldOffsets } =
                        committedChanges.branchControlData;

                    exportData[branch] = {
                        splineWorldControlPointData: {
                            worldControlPoints: splineWorldControlPointData.worldControlPoints,
                            worldControlPointIndices: splineWorldControlPointData.worldControlPointIndices,
                            lastTrunkMasterIndex: splineWorldControlPointData.lastTrunkMasterIndex
                        },
                        controlRadialOffsetR: controlRadialOffsetR,
                        controlOffsetYCPR: controlOffsetYCPR,
                        controlWorldOffsets: controlWorldOffsets || null,
                    };
                }
            });

            const jsonString = JSON.stringify(exportData);
            onStretchedCprControlPointsExportedRef.current(jsonString);
        }

        window.addEventListener(eventNames.ExportStretchedCprControlPoints, exportStretchedCprControlPoints);

        /**
         * Import stretched CPR spline control points from JSON.
         * This restores the branchControlData for all branches from the exported state.
         * @param {CustomEvent} ev - The event containing the JSON string in ev.detail.
         */
        function importStretchedCprControlPoints(ev) {
            try {
                const jsonString = ev.detail;
                const { renderWindow } = context.current;
                const { mapper, cprManipulator, resliceCursorWidgetState, liveCenterline, centerlinesPolyData } = context.current.cprObjects;

                /**
                 * Helper function to process a branch and update its control data.
                 * @param {string} branchName - The name of the branch to process.
                 * @param {BranchControlData} branchControlData - The control data for the branch.
                 */
                const processBranch = (branchName, branchControlData) => {
                    // Update the live branch control data.
                    branchControlDataRef.current.set(branchName, structuredClone(branchControlData));

                    // Update the committed changes.
                    committedChangesRef.current.set(branchName, {
                        branchControlData: structuredClone(branchControlData)
                    });
                };

                // If jsonString is null or empty (baseline revision case), reinitialize control point data for all branches.
                if (!jsonString || jsonString === '') {
                    Object.keys(CenterlineDistanceBranchSelection).forEach(branch => {
                        const branchName = CenterlineDistanceBranchSelection[branch];
                        const originalBranchControlData =
                            buildOriginalControlPointsFromPolyData(
                                centerlinesPolyData[branchName]
                            );

                        processBranch(branchName, originalBranchControlData);
                    });
                } else {
                    const importData = JSON.parse(jsonString);

                    Object.keys(importData).forEach((branch) => {
                        const branchData = importData[branch];

                        if (branchData && branchData.splineWorldControlPointData) {
                            const restoredBranchControlData = {
                                splineWorldControlPointData: {
                                    worldControlPoints: branchData.splineWorldControlPointData.worldControlPoints,
                                    worldControlPointIndices: branchData.splineWorldControlPointData.worldControlPointIndices,
                                    lastTrunkMasterIndex: branchData.splineWorldControlPointData.lastTrunkMasterIndex
                                },
                                controlRadialOffsetR: branchData.controlRadialOffsetR,
                                controlOffsetYCPR: branchData.controlOffsetYCPR,
                                controlWorldOffsets: branchData.controlWorldOffsets || null,
                            };

                            processBranch(branch, restoredBranchControlData);
                        }
                    });
                }

                // Restore the selected branch's centerline on the mapper.
                const selectedBranchName = getDUName(selectedBranchRef.current);
                setCenterlinePolyData(
                    mapper,
                    liveCenterline,
                    centerlinesPolyData[selectedBranchName],
                    resliceCursorWidgetState,
                    cprManipulator
                );

                rotateView(angleInDegreesRef.current, mapper, cprManipulator, resliceCursorWidgetState);

                syncMapperDirectionMatrix(mapper, resliceCursorWidgetState, cprManipulator);
                const importedBranchControlData = branchControlDataRef.current.get(selectedBranchName);
                if (importedBranchControlData) {
                    const editedPts = computeEditedCenterlinePositions(centerlinesPolyData[selectedBranchName], importedBranchControlData, mapper.getTangentDirection());
                    liveCenterline.getPoints().setData(editedPts, 3);
                    liveCenterline.modified();
                }

                const cprContext = createCPRUpdateContext(context.current);

                updateDistanceAndDirection(
                    cprContext,
                    onAngleChangedRef.current,
                    branchControlDataRef.current.get(getDUName(selectedBranchRef.current)),
                    currentCPRFrameRef,
                    selectedBranchName,
                    true
                );

                Object.keys(CenterlineDistanceBranchSelection).forEach(branch => {
                    const branchName = CenterlineDistanceBranchSelection[branch];
                    notifyStraightenedView(eventNames, branchName, branchControlDataRef.current.get(branchName));
                });
                notifyOrthogonalViews(eventNames, getDUName(selectedBranchRef.current), liveCenterline);

                // Propagate imported edits to the wallCenterline so that dependent
                // visualizations reflect the revision's centerline geometry.
                applyCommittedEditsToWallCenterline(committedChangesRef.current, centerlinesPolyData);
            } catch (error) {
                console.error('Failed to import stretched CPR control points:', error);
            }
        }

        window.addEventListener(eventNames.ImportStretchedCprControlPoints, importStretchedCprControlPoints);

        // --- Orthogonal view centerline edit handler ---
        // Receives world-offset edits that were initiated by dragging the
        // control point handler in OrthogonalCprVisualization.
        function onOrthogonalCenterlineEdited(ev) {
            const { branchName, pointIndex, worldOffset, viewportId, segmentationColor } = ev.detail;
            if (!branchName || pointIndex == null || !worldOffset) return;

            setOrthogonalHighlight(viewportId, pointIndex, segmentationColor);

            const branchControlData = branchControlDataRef.current.get(branchName);
            if (!branchControlData) return;

            const originalPolyData = centerlinesPolyData[branchName];
            if (!originalPolyData) return;

            const { liveCenterline, mapper } = context.current?.cprObjects || {};
            if (!liveCenterline || !mapper) return;

            const tangentDirection = mapper.getTangentDirection();
            const { worldControlPointIndices } = branchControlData.splineWorldControlPointData;

            // ---- Convert 3D world offset to a radial offset ----
            // The radial path displaces each point along
            //   dir = normalize(rotate(tangentDirection, pointTensor))
            // We project the orthogonal drag onto this direction so the
            // reconstruction stays on the radial path, avoiding the
            // interpolation mismatch that occurs when switching to the
            // world-offset path.
            const tensors = originalPolyData.getPointData().getTensors();
            const tensorData = tensors ? tensors.getData() : null;
            const tensorComponents = tensors ? tensors.getNumberOfComponents() : 0;

            let deltaR = 0;
            if (tangentDirection && tensorData && tensorComponents === 16) {
                const localM4 = mat4.create();
                const localM3 = mat3.create();
                const d = vec3.create();
                const off = pointIndex * 16;
                for (let j = 0; j < 16; j++) localM4[j] = tensorData[off + j];
                mat3.fromMat4(localM3, localM4);
                vec3.transformMat3(d, tangentDirection, localM3);
                const len = vec3.length(d);
                if (len > 0) {
                    vec3.normalize(d, d);
                    deltaR = d[0] * worldOffset[0] + d[1] * worldOffset[1] + d[2] * worldOffset[2];
                }
            }

            // Current interpolated radial offset at this point index.
            const currentR = getRadialOffsetForCenterlineIndex(
                pointIndex, worldControlPointIndices,
                branchControlData.controlRadialOffsetR
            );
            const newR = currentR + deltaR;

            // Always store the full 3D world offset when editing the centerline using the orthogonal CPR view.
            // Unlike the stretched CPR view (which constrains movement to the
            // radial direction), orthogonal edits allow displacement in any
            // direction perpendicular to the centerline tangent.
            const existingWO = getWorldOffsetForCenterlineIndex(
                pointIndex, worldControlPointIndices,
                branchControlData.controlWorldOffsets
            );
            const newWorldOffset = [
                existingWO[0] + worldOffset[0],
                existingWO[1] + worldOffset[1],
                existingWO[2] + worldOffset[2],
            ];

            addOriginalControlPointIndex(pointIndex, newR, branchControlData, newWorldOffset);

            // Reconstruct the centerline from offsets.
            restoreOriginalCenterline(liveCenterline, originalPolyData);
            {
                const editedPts = computeEditedCenterlinePositions(originalPolyData, branchControlData, tangentDirection);
                liveCenterline.getPoints().setData(editedPts, 3);
                liveCenterline.modified();
            }

            // Recompute actor transform, camera position (vertical centering),
            // and spline handle projection and handle highlighting in one pass.
            // This follows the same path as stretched CPR handle drag.
            const cprContext = createCPRUpdateContext(context.current);
            updateDistanceAndDirection(
                cprContext,
                onAngleChangedRef.current,
                branchControlData,
                currentCPRFrameRef,
                branchName,
                true, // editsAlreadyApplied — liveCenterline already has edits
            );
            onCenterlinePointEditRef.current();

            notifyStraightenedView(eventNames, branchName, branchControlData);
            notifyOrthogonalViews(eventNames, branchName, liveCenterline);
        }

        if (eventNames?.OrthogonalCenterlineEdited) {
            window.addEventListener(eventNames.OrthogonalCenterlineEdited, onOrthogonalCenterlineEdited);
        }

        // --- Orthogonal slice change handler ---
        // Updates per-view control point handle highlighting in the stretched
        // CPR view to reflect each orthogonal view's current slice.
        function onOrthogonalSliceChanged(ev) {
            const { branchName, pointIndex, viewportId, segmentationColor } = ev.detail;
            if (!branchName || !viewportId) return;

            setOrthogonalHighlight(viewportId, pointIndex, segmentationColor);

            const { splineWidget, handleIndexList, isCenterlineEditable } = context.current?.cprObjects || {};
            const { renderWindow, interactor } = context.current || {};
            if (!splineWidget || !renderWindow || !interactor || !handleIndexList) return;

            updateHandleHighlighting(splineWidget, interactor, renderWindow, handleIndexList, isCenterlineEditable !== false);
        }

        if (eventNames?.OrthogonalSliceChanged) {
            window.addEventListener(eventNames.OrthogonalSliceChanged, onOrthogonalSliceChanged);
        }

        return [
            { eventType: eventNames.CancelChanges, handler: cancelChanges, container: window },
            { eventType: eventNames.ConfirmChanges, handler: confirmChanges, container: window },
            { eventType: eventNames.ExportVtp, handler: exportVtp, container: window },
            { eventType: eventNames.ExportStretchedCprControlPoints, handler: exportStretchedCprControlPoints, container: window },
            { eventType: eventNames.ImportStretchedCprControlPoints, handler: importStretchedCprControlPoints, container: window },
            ...(eventNames?.OrthogonalCenterlineEdited
                ? [{ eventType: eventNames.OrthogonalCenterlineEdited, handler: onOrthogonalCenterlineEdited, container: window }]
                : []),
            ...(eventNames?.OrthogonalSliceChanged
                ? [{ eventType: eventNames.OrthogonalSliceChanged, handler: onOrthogonalSliceChanged, container: window }]
                : []),
        ];
    }

    const render = () => {
        if (context.current?.genericRenderWindow &&
            containerRef.current?.offsetWidth > 0 && containerRef.current?.offsetHeight > 0) {
            log("Rendering via resize");
            // OPTIMIZE:
            // This is a quick fix for the resolution issue when switching between single/quad view.
            // A better fix would be to depend on a visibility prop.
            context.current.genericRenderWindow.resize();
        }
    };

    const handleResize = () => {
        if (containerRef.current && context.current?.genericRenderWindow &&
            containerRef.current.offsetWidth > 0 && containerRef.current.offsetHeight > 0) {
            context.current.genericRenderWindow.resize();

            // Update horizontal measurement lines for straightened view (legacy reslice path).
            if (context.current.cprObjects?.horizontalIndexLines?.length &&
                context.current.cprObjects?.resliceFilter) {
                updateStraightenedIndexLines(
                    context.current.cprObjects.resliceFilter,
                    context.current.cprObjects.horizontalIndexLines,
                    context.current.renderer,
                    context.current.renderWindow,
                );
            }

            // Update the yellow arrow for straightened view (legacy reslice path).
            if (context.current.cprObjects?.lengthMeasurementArrow &&
                context.current.cprObjects?.resliceFilter) {
                updateStraightenedMeasurementArrow(
                    context.current.cprObjects.resliceFilter,
                    context.current.cprObjects.lengthMeasurementArrow,
                    context.current.renderer,
                    context.current.renderWindow,
                );
            }

            // Resize Konva stage to match container.
            if (context.current.konvaElements?.stage) {
                const container = containerRef.current;
                context.current.konvaElements.stage.width(container.offsetWidth);
                context.current.konvaElements.stage.height(container.offsetHeight);

                // Update the measurement label position after resize.
                updateStraightenedMeasurementLabel(context.current, maybeActiveMeasurementRef.current);
            }

            // Resize stretched/straightened measurement Konva stage.
            if (context.current.stretchedKonvaElements?.stage) {
                const container = containerRef.current;
                resizeStretchedMeasurementKonva(
                    context.current.stretchedKonvaElements,
                    container.offsetWidth,
                    container.offsetHeight
                );

                // Update stretched measurement Konva elements after resize.
                updateStretchedMeasurementKonva(
                    context.current,
                    maybeActiveMeasurementRef.current,
                    selectedBranchRef.current,
                    currentCPRFrameRef.current
                );
            }

            context.current.renderWindow.render();
        }
    };

    /**
     * Updates the reslice filter's axes based on the provided transform and values.
     * @param {import('vtk.js/Sources/Imaging/Core/ImageReslice').default} resliceFilter - The vtkImageReslice instance to update.
     * @param {import('vtk.js/Sources/Common/Core/MatrixBuilder').default} transform - The vtkMatrixBuilder instance used for transformations.
     * @param {number[]} transformValues - Array of numbers representing translation values.
     * @param {number} obliqueAngleInDegrees - The oblique angle in degrees to rotate the transform.
     */
    const updateResliceFilter = (resliceFilter, transform, transformValues, obliqueAngleInDegrees) => {
        log("Translating transform by", ...transformValues);
        transform.translate(...transformValues);
        transform.rotateX(90); // Rotate to coronal.
        transform.rotateY(obliqueAngleInDegrees); // Rotate to the desired oblique angle.
        transform.rotateZ(-90); // Rotate so it's not sideways.
        resliceFilter.setResliceAxes(transform.getMatrix());
        resliceFilter.update();
    }

    // Effect that only runs when the component mounts and unmounts.
    useEffect(() => {
        log("Mounting");
        onVisualizationReady?.([viewportId, false]);
        const container = containerRef.current;

        if (container) {
            const resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(container);

            context.current = {
                ...context.current,
                resizeObserver
            };
        }

        return () => {
            const ctx = context.current;

            if (ctx) {
                log("Unmounting");
                const container = containerRef.current;

                const {
                    genericRenderWindow,
                    istyle,
                    interactor,
                    combinedTouchStart,
                    combinedTouchEnd,
                    resizeObserver,
                    cprObjects,
                    cameraModifiedSubscription } = ctx;

                if (container) {
                    container.removeEventListener('touchstart', combinedTouchStart);
                    container.removeEventListener('touchend', combinedTouchEnd);
                    container.removeEventListener('touchmove', istyle.handleTouchMove);
                }

                if (eventNames?.CaptureCustomScreenshot) {
                    window.removeEventListener(eventNames.CaptureCustomScreenshot, captureCustomScreenshot);
                }

                if (ctx.eventHandlers) {
                    // Iterate through the list and remove each event listener.
                    ctx.eventHandlers.forEach(({ eventType, handler, container }) => {
                        if (container && handler) {
                            log('Removing event listener for', eventType, 'from', container);
                            container.removeEventListener(eventType, handler);
                        }
                    });
                }

                // Unsubscribe from camera modifications.
                if (cameraModifiedSubscription) {
                    cameraModifiedSubscription.unsubscribe();
                }

                interactor?.cancelAnimation();
                cprObjects?.widgetManager?.releaseFocus();

                resizeObserver?.disconnect();
                cprObjects?.actor?.delete();
                cprObjects?.mapper?.delete();
                if (cprObjects?.centerlineOverlay) {
                    disposeStraightenedCenterlineOverlay(ctx.renderer, cprObjects.centerlineOverlay);
                    cprObjects.centerlineOverlay = null;
                }
                if (cprObjects?.horizontalIndexLines?.length) {
                    disposeStraightenedIndexLines(ctx.renderer, cprObjects.horizontalIndexLines);
                }
                if (cprObjects?.lengthMeasurementArrow) {
                    disposeStraightenedMeasurementArrow(ctx.renderer, cprObjects.lengthMeasurementArrow);
                    cprObjects.lengthMeasurementArrow = null;
                }
                // Clean up stretched measurement indicators.
                if (cprObjects?.stretchedMeasurementLines?.length) {
                    disposeStretchedMeasurementLines(ctx.renderer, cprObjects.stretchedMeasurementLines);
                    cprObjects.stretchedMeasurementLines = [];
                }
                if (cprObjects?.stretchedMeasurementArrow) {
                    disposeStretchedMeasurementArrow(ctx.renderer, cprObjects.stretchedMeasurementArrow);
                    cprObjects.stretchedMeasurementArrow = null;
                }
                cprObjects?.widgetManager?.delete();

                // Clean up Konva stage.
                if (ctx.konvaElements?.stage) {
                    ctx.konvaElements.stage.destroy();
                }
                if (ctx.konvaElements?.konvaContainer && container) {
                    container.removeChild(ctx.konvaElements.konvaContainer);
                }
                ctx.konvaElements = null;

                // Clean up stretched Konva elements.
                if (ctx.stretchedKonvaElements) {
                    disposeStretchedMeasurementKonva(ctx.stretchedKonvaElements);
                    ctx.stretchedKonvaElements = null;
                }

                genericRenderWindow?.delete();
                context.current = null;
            }
        };
    }, []);

    // Effect to update onAngleChanged ref.
    useEffect(() => {
        onAngleChangedRef.current = onAngleChanged;
    }, [onAngleChanged]);

    // Effect to update angleInDegrees ref.
    useEffect(() => {
        angleInDegreesRef.current = angleInDegrees;
    }, [angleInDegrees]);

    // Effect to update onCenterlinePointEdit ref.
    useEffect(() => {
        onCenterlinePointEditRef.current = onCenterlinePointEdit;
    }, [onCenterlinePointEdit]);

    // Effect to update onCenterlineVtpExported ref.
    useEffect(() => {
        onCenterlineVtpExportedRef.current = onCenterlineVtpExported;
    }, [onCenterlineVtpExported]);

    // Effect to update maybeActiveMeasurement ref.
    useEffect(() => {
        maybeActiveMeasurementRef.current = maybeActiveMeasurement;
    }, [maybeActiveMeasurement]);

    // Effect to update onStretchedCprControlPointsExported ref.
    useEffect(() => {
        onStretchedCprControlPointsExportedRef.current = onStretchedCprControlPointsExported;
    }, [onStretchedCprControlPointsExported]);

    // Effect to update onMeasurementLineDragged ref.
    useEffect(() => {
        onMeasurementLineDraggedRef.current = onMeasurementLineDragged;
    }, [onMeasurementLineDragged]);

    // Effect to update onReady ref.
    useEffect(() => {
        onReadyRef.current = onReady;
    }, [onReady]);

    // Effect to update onLiveCenterlineChanged ref.
    useEffect(() => {
        onLiveCenterlineChangedRef.current = onLiveCenterlineChanged;
    }, [onLiveCenterlineChanged]);

    // Effect to set up the DICOM visualization.
    useEffect(() => {
        const container = containerRef.current;
        if (!context.current?.cprObjects?.actor && imageFiles?.length > 0 && container) {
            function importVtp(ev) {
                // This will not be implemented.
                // if (!isStraightened) {
                //     const fileContents = ev.detail;
                //     const reader = vtkXMLPolyDataReader.newInstance();
                //     reader.parseAsArrayBuffer(fileContents);
                //     const polyData = reader.getOutputData();
                //
                //     console.log("POKE", polyData);
                // } else {
                //     console.log("POKE3");
                // }
            }

            window.addEventListener(eventNames.ImportVtp, importVtp);

            context.current.viewportId = viewportId;

            if (!wallCenterline) {
                // Cannot proceed without centerline for CPR view (both stretched and straightened).
                return;
            }

            // We use the wrapper here to abstract out manual RenderWindow/Renderer/OpenGLRenderWindow setup
            const genericRenderWindow = vtkGenericRenderWindow.newInstance({
                background: [0.0, 0.0, 0.0]
            });

            //var genericRenderWindow = vtkOpenGLRenderWindow.newInstance();
            genericRenderWindow.setContainer(containerRef.current);

            // Defer initial resize if container is hidden (zero size) to avoid
            // GL_INVALID_FRAMEBUFFER_OPERATION errors.
            if (containerRef.current.offsetWidth > 0 && containerRef.current.offsetHeight > 0) {
                genericRenderWindow.resize();
            }

            const renderer = genericRenderWindow.getRenderer();
            const renderWindow = genericRenderWindow.getRenderWindow();
            debug(() => global.debug_renderer = renderer);
            debug(() => global.debug_renderWindow = renderWindow);
            renderer.getActiveCamera().setParallelProjection(true);

            // Set up interactor style
            const istyle = customImageInteractor.newInstance();

            // Interaction styles don't provide the ability to distinguish between a touch and left mouse press,
            // so we have to add that functionality ourselves
            const combinedTouchStart = (e) => { onTouchStart(e); istyle.handleTouchStart(e) };
            const combinedTouchEnd = (e) => { onTouchEnd(e); istyle.handleTouchEnd(e) };

            containerRef.current.addEventListener('touchstart', combinedTouchStart);
            containerRef.current.addEventListener('touchend', combinedTouchEnd);
            containerRef.current.addEventListener('touchmove', istyle.handleTouchMove);

            if (eventNames?.CaptureCustomScreenshot) {
                window.addEventListener(eventNames.CaptureCustomScreenshot, captureCustomScreenshot);
            }

            const interactor = renderWindow.getInteractor();
            istyle.setContainer(containerRef.current);
            istyle.setRenderWindow(renderWindow);
            istyle.setMouseWheelIsSliceScroll(isSliceScroll);
            istyle.setInteractionMode('IMAGE_SLICING');
            interactor.setInteractorStyle(istyle);

            let cprObjects;

            if (isStraightened) {
                // Straightened CPR view setup using vtkImageCPRMapper in straightened mode.
                const stretchViewType = ViewTypes.XZ_PLANE;
                const planeName = viewTypeToPlaneName[stretchViewType];
                log(`Setting up ResliceCursorWidget for straightened CPR view - plane`, planeName);

                const resliceCursorWidget = vtkResliceCursorWidget.newInstance({
                    planes: [planeName],
                    behavior: widgetBehavior,
                });

                const widgetManager = vtkWidgetManager.newInstance();
                widgetManager.setRenderer(renderer);

                const resliceCursorWidgetState = resliceCursorWidget.getWidgetState();

                // Hide widget handles, as straightened view is non-interactive for centerline editing.
                resliceCursorWidgetState
                    .getStatesWithLabel('sphere')
                    .forEach((handle) => handle.setVisible(false));
                resliceCursorWidgetState.getCenterHandle().setVisible(false);

                const actor = vtkImageSlice.newInstance();
                const mapper = vtkImageCPRMapper.newInstance();
                mapper.setBackgroundColor(0, 0, 0, 0);
                actor.setMapper(mapper);

                // Holds the active centerline polydata for the selected branch.
                const liveCenterline = vtkPolyData.newInstance();

                mapper.setInputData(liveCenterline, 1);
                mapper.setOrientationArrayName('OrientationQuat'); // sign-consistent quaternions (4-comp)
                mapper.setWidth(400);
                mapper.setProjectionSlabThickness(0.1);
                mapper.setProjectionSlabNumberOfSamples(1);
                mapper.setProjectionMode(ProjectionMode.MAX);

                const cprManipulator = vtkCPRManipulator.newInstance({
                    cprActor: actor,
                });
                const planeManipulator = vtkPlaneManipulator.newInstance();

                const {
                    centerlinesPolyData,
                    branchOriginalIndices,
                } = buildCenterlinesPolyData(wallCenterline);

                cprObjects = {
                    actor,
                    mapper,
                    resliceCursorWidget,
                    widgetManager,
                    resliceCursorWidgetState,
                    cprManipulator,
                    planeManipulator,
                    centerlinesPolyData,
                    branchOriginalIndices,
                    liveCenterline,
                    wallCenterline,
                    // Picks the world position under the cursor.
                    pointPicker: vtkPointPicker.newInstance(),
                    // Measurement indicators (using stretched measurement system).
                    stretchedMeasurementLines: [],
                    stretchedMeasurementArrow: null,
                    // Track whether we've initialized the camera parallel scale.
                    cameraScaleInitialized: false,
                    // Per-branch camera state to preserve pan and zoom.
                    branchCameraStates: new Map(),
                    // Flag to prevent saving camera state during programmatic updates.
                    preventCameraSave: false,
                    // Ordered list of centerline point indices kept in sync with the
                    // spline widget's handle list.
                    handleIndexList: [],
                };
            } else {
                // Stretched CPR view setup
                const stretchViewType = ViewTypes.XZ_PLANE;
                const planeName = viewTypeToPlaneName[stretchViewType];
                log(`Setting up ResliceCursorWidget for stretched CPR view - plane`, planeName); // Should be 'Y'

                const resliceCursorWidget = vtkResliceCursorWidget.newInstance({
                    planes: [planeName],
                    behavior: widgetBehavior,
                });

                const widgetManager = vtkWidgetManager.newInstance();
                widgetManager.setRenderer(renderer);

                const resliceCursorWidgetState = resliceCursorWidget.getWidgetState();

                const actor = vtkImageSlice.newInstance();
                const mapper = vtkImageCPRMapper.newInstance();
                mapper.setBackgroundColor(0, 0, 0, 0);
                actor.setMapper(mapper);

                // Holds the active centerline polydata for the selected branch.
                const liveCenterline = vtkPolyData.newInstance();

                mapper.setInputData(liveCenterline, 1);
                mapper.setOrientationArrayName('OrientationQuat'); // sign-consistent quaternions (4-comp)
                mapper.setWidth(400);
                mapper.setProjectionSlabThickness(0.1);
                mapper.setProjectionSlabNumberOfSamples(1);
                mapper.setProjectionMode(ProjectionMode.MAX);

                const cprManipulator = vtkCPRManipulator.newInstance({
                    cprActor: actor,
                });
                const planeManipulator = vtkPlaneManipulator.newInstance();

                const {
                    centerlinesPolyData,
                    branchOriginalIndices,
                } = buildCenterlinesPolyData(wallCenterline);

                debug(() => global.debug_centerlinesPolyData = centerlinesPolyData);

                // Centerline spline
                const splineWidget = vtkSplineWidget.newInstance();
                const splineWidgetRepresentation = widgetManager.addWidget(splineWidget);
                configureSplineWidget(splineWidget, splineWidgetRepresentation, stretchedCenterlineColor);

                const pointPicker = vtkPointPicker.newInstance();

                cprObjects = {
                    actor,
                    mapper,
                    resliceCursorWidget,
                    widgetManager,
                    resliceCursorWidgetState,
                    cprManipulator,
                    planeManipulator,
                    centerlinesPolyData,
                    branchOriginalIndices,
                    liveCenterline,
                    splineWidget,
                    splineWidgetRepresentation,
                    pointPicker,
                    // For each control point i, store radial offset in the cross-section:
                    //   offsetR[i] = scalar r such that edited point at angle θ is:
                    //   P + r * R(θ)   where R(θ) = cosθ * N + sinθ * B
                    controlRadialOffsetR: [],
                    wallCenterline,
                    // Stretched measurement indicators.
                    stretchedMeasurementLines: [],
                    stretchedMeasurementArrow: null,
                    // Track whether we've initialized the camera parallel scale.
                    cameraScaleInitialized: false,
                    // Per-branch camera state to preserve pan and zoom.
                    branchCameraStates: new Map(),
                    // Flag to prevent saving camera state during programmatic updates.
                    preventCameraSave: false,
                    // Ordered list of centerline point indices kept in sync with the
                    // spline widget's handle list.
                    handleIndexList: [],
                    // Whether centerline editing is the active mode.
                    isCenterlineEditable: isCenterlineEditableRef.current,
                };
            }

            // Initialize Konva stage for measurement labels.
            const konvaElements = isStraightened ? initializeStraightenedMeasurementLabel(container, viewportId) : null;

            // Initialize Konva stage for stretched/straightened CPR measurement display.
            const stretchedKonvaElements = initializeStretchedMeasurementKonva(container, viewportId);

            context.current = {
                ...context.current,
                genericRenderWindow,
                renderWindow,
                renderer,
                cprObjects,
                istyle,
                interactor,
                combinedTouchStart,
                combinedTouchEnd,
                konvaElements,
                stretchedKonvaElements
            };

            debug(() => global.debug_cprObjects = cprObjects);

            // Set up default window/level.
            // TODO: Can we dynamically set this based on the image data?
            cprObjects.actor.getProperty().setColorWindow(1200);
            cprObjects.actor.getProperty().setColorLevel(80);

            let imageCenter;

            // Buffer LiveCenterlineEdited events that arrive before the straightened
            // view's image has loaded (the full handler needs VTK objects that are
            // only available after imageData is set). The buffer is drained once
            // the full handler is registered.
            const pendingLiveCenterlineEdits = new Map();
            let preInitLiveCenterlineHandler;
            if (isStraightened && eventNames?.LiveCenterlineEdited) {
                preInitLiveCenterlineHandler = (ev) => {
                    const { branchName, branchControlData } = ev.detail || {};
                    if (branchName && branchControlData) {
                        pendingLiveCenterlineEdits.set(branchName, structuredClone(branchControlData));
                    }
                };
                window.addEventListener(eventNames.LiveCenterlineEdited, preInitLiveCenterlineHandler);
            }

            // TODO: VRM-4359 Can we share the vtkImageData across DICOM viewports? This could be useful for
            //  [Axial / Sagittal / Coronal] and [Centerline Plane A / Plane B].
            imageLoader.tryGetImage(imageFiles)
                .then(imageData => {
                    if (isNullOrUndefined(imageData)) return;

                    if (cprObjects.mapper && !cprObjects.mapper.isDeleted()) {
                        debug(() => global.debug_imageData = imageData);

                        imageCenter = imageData.getCenter();

                        context.current = {
                            ...context.current,
                            imageCenter
                        };

                        if (isStraightened) {
                            // Straightened CPR using vtkImageCPRMapper in straightened mode.
                            const {
                                resliceCursorWidget,
                                resliceCursorWidgetState,
                                mapper,
                                actor,
                                cprManipulator,
                                planeManipulator,
                                liveCenterline,
                                centerlinesPolyData,
                            } = cprObjects;

                            mapper.setImageData(imageData);

                            resliceCursorWidget.setImage(imageData);
                            const imageDimensions = imageData.getDimensions();
                            mapper.setWidth(imageDimensions[0]);

                            actor.setUserMatrix(resliceCursorWidget.getResliceAxes(stretchViewType));
                            resliceCursorWidget.updateCameraPoints(renderer, stretchViewType, true, true);

                            // Add image slice actor to scene.
                            renderer.addActor(actor);

                            const branchName = getDUName(selectedBranch);

                            setCenterlinePolyData(
                                mapper,
                                liveCenterline,
                                centerlinesPolyData[branchName],
                                resliceCursorWidgetState,
                                cprManipulator
                            );

                            mapper.useStraightenedMode();

                            // Initialize control point data for each branch.
                            Object.keys(CenterlineDistanceBranchSelection).forEach(branch => {
                                const originalBranchControlData =
                                    buildOriginalControlPointsFromPolyData(
                                        centerlinesPolyData[CenterlineDistanceBranchSelection[branch]]
                                    );

                                branchControlDataRef.current.set(
                                    CenterlineDistanceBranchSelection[branch],
                                    originalBranchControlData
                                );
                            });

                            // Rotate to match the initial angleInDegrees prop.
                            rotateView(angleInDegrees, mapper, cprManipulator, resliceCursorWidgetState);

                            const cprContext = createCPRUpdateContext(context.current);

                            updateDistanceAndDirection(
                                cprContext,
                                onAngleChanged,
                                branchControlDataRef.current.get(getDUName(selectedBranchRef.current)),
                                currentCPRFrameRef,
                                branchName
                            );

                            // Initialize measurement indicators (using the stretched measurement system
                            // since both modes share the same CPR mapper pipeline).
                            refreshStretchedMeasurementLines(
                                context.current,
                                maybeActiveMeasurementRef.current,
                                selectedBranchRef.current,
                                currentCPRFrameRef.current
                            );
                            refreshStretchedLengthMeasurementArrow(
                                context.current,
                                maybeActiveMeasurementRef.current,
                                selectedBranchRef.current,
                                currentCPRFrameRef.current
                            );
                            updateStretchedMeasurementKonva(
                                context.current,
                                maybeActiveMeasurementRef.current,
                                selectedBranchRef.current,
                                currentCPRFrameRef.current
                            );

                            // Create and add the non-interactive centerline overlay for the straightened view.
                            const centerlineOverlay = createStraightenedCenterlineOverlay(straightenedCenterlineColor);
                            centerlineOverlay.pointsActor.setVisibility(isCenterlineEditableRef.current);
                            renderer.addActor(centerlineOverlay.lineActor);
                            renderer.addActor(centerlineOverlay.pointsActor);
                            updateStraightenedCenterlineOverlay(
                                centerlineOverlay,
                                actor,
                                mapper,
                                branchControlDataRef.current.get(branchName),
                                currentCPRFrameRef.current
                            );
                            cprObjects.centerlineOverlay = centerlineOverlay;

                            // Listen for live centerline edits from the stretched CPR view.
                            function onLiveCenterlineEdited(ev) {
                                try {
                                    const { branchName: editedBranch, branchControlData: editedData } = ev.detail;
                                    if (!editedData || !editedBranch) return;

                                    const { mapper, liveCenterline, centerlinesPolyData } = context.current.cprObjects;
                                    const { renderWindow } = context.current;

                                    // Update this instance's branch control data with the edited data.
                                    branchControlDataRef.current.set(editedBranch, structuredClone(editedData));

                                    // Only apply if the edited branch is the currently selected branch.
                                    const currentBranch = getDUName(selectedBranchRef.current);
                                    if (editedBranch !== currentBranch) return;

                                    const originalPolyData = centerlinesPolyData[currentBranch];
                                    if (!originalPolyData) return;

                                    const { cprManipulator, resliceCursorWidgetState } = context.current.cprObjects;

                                    // Restore original centerline, then apply the edits.
                                    restoreOriginalCenterline(liveCenterline, originalPolyData);

                                    syncMapperDirectionMatrix(mapper, resliceCursorWidgetState, cprManipulator);
                                    {
                                        const editedPts = computeEditedCenterlinePositions(originalPolyData, editedData, mapper.getTangentDirection());
                                        liveCenterline.getPoints().setData(editedPts, 3);
                                        liveCenterline.modified();
                                    }

                                    // Re-frame camera and actor for the updated arc length so the
                                    // straightened view stays vertically centered.
                                    const cprContext = createCPRUpdateContext(context.current);
                                    updateDistanceAndDirection(
                                        cprContext,
                                        onAngleChanged,
                                        editedData,
                                        currentCPRFrameRef,
                                        currentBranch,
                                        true
                                    );

                                    // Refresh the non-interactive centerline overlay with the edited control points.
                                    if (context.current.cprObjects?.centerlineOverlay) {
                                        updateStraightenedCenterlineOverlay(
                                            context.current.cprObjects.centerlineOverlay,
                                            context.current.cprObjects.actor,
                                            mapper,
                                            editedData,
                                            currentCPRFrameRef.current
                                        );
                                    }

                                    renderWindow.render();
                                } catch (error) {
                                    console.error('Failed to apply live centerline edit to straightened view:', error);
                                }
                            }

                            if (eventNames?.LiveCenterlineEdited) {
                                // Replace the pre-init buffer handler with the full handler.
                                if (preInitLiveCenterlineHandler) {
                                    window.removeEventListener(eventNames.LiveCenterlineEdited, preInitLiveCenterlineHandler);
                                    preInitLiveCenterlineHandler = null;
                                }

                                window.addEventListener(eventNames.LiveCenterlineEdited, onLiveCenterlineEdited);

                                // Store event handler for cleanup.
                                context.current = {
                                    ...context.current,
                                    eventHandlers: [
                                        ...(context.current.eventHandlers || []),
                                        { eventType: eventNames.LiveCenterlineEdited, handler: onLiveCenterlineEdited, container: window }
                                    ]
                                };

                                // Drain any edits that arrived before the image loaded.
                                if (pendingLiveCenterlineEdits.size > 0) {
                                    pendingLiveCenterlineEdits.forEach((editedData, editedBranch) => {
                                        onLiveCenterlineEdited({
                                            detail: { branchName: editedBranch, branchControlData: editedData }
                                        });
                                    });
                                    pendingLiveCenterlineEdits.clear();
                                }
                            }

                        } else {
                            // Stretched CPR
                            const {
                                resliceCursorWidget,
                                resliceCursorWidgetState,
                                mapper,
                                actor,
                                cprManipulator,
                                planeManipulator,
                                liveCenterline,
                                centerlinesPolyData,
                                splineWidget,
                                widgetManager,
                                pointPicker
                            } = cprObjects;

                            mapper.setImageData(imageData);

                            resliceCursorWidget.setImage(imageData);
                            const imageDimensions = imageData.getDimensions();
                            mapper.setWidth(imageDimensions[0]);

                            actor.setUserMatrix(resliceCursorWidget.getResliceAxes(stretchViewType));
                            resliceCursorWidget.updateCameraPoints(renderer, stretchViewType, true, true);

                            // Add image slice actor to scene.
                            renderer.addActor(cprObjects.actor);

                            const branchName = getDUName(selectedBranch);

                            setCenterlinePolyData(
                                mapper,
                                liveCenterline,
                                centerlinesPolyData[branchName],
                                resliceCursorWidgetState,
                                cprManipulator
                            );

                            mapper.useStretchedMode();

                            // Initialize control point data for each branch.
                            Object.keys(CenterlineDistanceBranchSelection).forEach(branch => {
                                const originalBranchControlData =
                                    buildOriginalControlPointsFromPolyData(
                                        centerlinesPolyData[CenterlineDistanceBranchSelection[branch]]
                                    );

                                branchControlDataRef.current.set(
                                    CenterlineDistanceBranchSelection[branch],
                                    originalBranchControlData
                                );

                                committedChangesRef.current.set(
                                    CenterlineDistanceBranchSelection[branch],
                                    {
                                        branchControlData: structuredClone(originalBranchControlData)
                                    }
                                );
                            });

                            const originalBranchControlData = buildOriginalControlPointsFromPolyData(
                                centerlinesPolyData[branchName],
                            );
                            branchControlDataRef.current.set(branchName, originalBranchControlData);

                            // Default seems to be 180 degrees, so we need to rotate to match our angleInDegrees prop.
                            rotateView(angleInDegrees, mapper, cprManipulator, resliceCursorWidgetState)

                            const cprContext = createCPRUpdateContext(context.current);

                            updateDistanceAndDirection(
                                cprContext,
                                onAngleChanged,
                                branchControlDataRef.current.get(getDUName(selectedBranchRef.current)),
                                currentCPRFrameRef,
                                branchName
                            );

                            // Initialize stretched measurement indicators.
                            refreshStretchedMeasurementLines(
                                context.current,
                                maybeActiveMeasurementRef.current,
                                selectedBranchRef.current,
                                currentCPRFrameRef.current
                            );
                            refreshStretchedLengthMeasurementArrow(
                                context.current,
                                maybeActiveMeasurementRef.current,
                                selectedBranchRef.current,
                                currentCPRFrameRef.current
                            );
                            updateStretchedMeasurementKonva(
                                context.current,
                                maybeActiveMeasurementRef.current,
                                selectedBranchRef.current,
                                currentCPRFrameRef.current
                            );

                            // Enable CPR spline handle picking/dragging
                            const splineCprPickingHandlers = setupSplineCPRPicking({
                                container: genericRenderWindow.getContainer(),
                                renderer,
                                renderWindow,
                                splineWidget,
                                widgetManager,
                                pointPicker,
                                currentCPRFrameRef,
                                branchControlDataRef,
                                selectedBranchRef,
                                interactor,
                                planeManipulator,
                                mapper,
                                actor,
                                onCenterlinePointEditRef,
                                liveCenterline,
                                centerlinesPolyData,
                                eventNames,
                                handleIndexList: cprObjects.handleIndexList,
                                isCenterlineEditableRef,
                            });

                            const confirmCancelChangeHandlers = setUpConfirmCancelChangesHandler(
                                centerlinesPolyData,
                            )

                            const eventHandlers = splineCprPickingHandlers.concat(confirmCancelChangeHandlers);

                            context.current = {
                                ...context.current,
                                eventHandlers
                            }

                            // Notify that the CPR visualization is ready to receive events.
                            if (onReadyRef.current) {
                                onReadyRef.current();
                            }
                        }

                        // DEBUG - Add cone for reference
                        // const coneSource = vtkConeSource.newInstance({ height: 100.0, center: [ 5.801562499999989, -0.3984375, -124.25 ], radius: 100, direction: [0,0,1] });
                        // const coneMapper = vtkMapper.newInstance();
                        // coneMapper.setInputConnection(coneSource.getOutputPort());
                        // const coneActor = vtkActor.newInstance();
                        // coneActor.setMapper(coneMapper);
                        // renderer.addActor(coneActor);
                        // global.coneSource = coneSource;
                        // global.coneActor = coneActor;

                        // Reset camera and render the scene
                        let camera = renderer.getActiveCamera();
                        debug(() => global.debug_camera = camera);

                        if (isStraightened) {
                            // Listen for camera modifications to update konva label position during panning
                            // and save camera state per branch.
                            const cameraModifiedSubscription = camera.onModified(() => {
                                updateStraightenedMeasurementLabel(context.current, maybeActiveMeasurementRef.current);
                                updateStretchedMeasurementKonva(
                                    context.current,
                                    maybeActiveMeasurementRef.current,
                                    selectedBranchRef.current,
                                    currentCPRFrameRef.current
                                );

                                // Only save camera state if not in the middle of programmatic updates.
                                if (context.current?.cprObjects?.preventCameraSave) {
                                    return;
                                }

                                // Save the current camera state for the active branch.
                                if (context.current?.cprObjects?.branchCameraStates && selectedBranchRef.current) {
                                    const branchName = getDUName(selectedBranchRef.current);
                                    saveCameraStateForBranch(
                                        camera,
                                        context.current.cprObjects.branchCameraStates,
                                        branchName
                                    );
                                }
                            });

                            // Store the subscription for cleanup.
                            context.current = {
                                ...context.current,
                                cameraModifiedSubscription
                            };
                        } else {
                            // Listen for camera modifications to update konva label position during panning
                            // and save camera state per branch.
                            const cameraModifiedSubscription = camera.onModified(() => {
                                updateStretchedMeasurementKonva(
                                    context.current,
                                    maybeActiveMeasurementRef.current,
                                    selectedBranchRef.current,
                                    currentCPRFrameRef.current
                                );

                                // Only save camera state if not in the middle of programmatic updates.
                                if (context.current?.cprObjects?.preventCameraSave) {
                                    return;
                                }

                                // Save the current camera state for the active branch.
                                if (context.current?.cprObjects?.branchCameraStates && selectedBranchRef.current) {
                                    const branchName = getDUName(selectedBranchRef.current);
                                    saveCameraStateForBranch(
                                        camera,
                                        context.current.cprObjects.branchCameraStates,
                                        branchName
                                    );
                                }
                            });

                            // Store the subscription for cleanup.
                            context.current = {
                                ...context.current,
                                cameraModifiedSubscription
                            };
                        }

                        // Reset the camera clipping range to fit the image, otherwise we won't see anything.
                        renderer.resetCameraClippingRange();

                        renderer.setActiveCamera(camera);

                        // Guard against rendering into a zero-sized framebuffer
                        // (e.g. when the viewport container is hidden).
                        if (container.offsetWidth > 0 && container.offsetHeight > 0) {

                            fitImageVerticallyToViewport(context.current, extraZoom);

                            renderWindow.render();
                        }

                        onVisualizationReady?.([viewportId, true]);
                    } else {
                        log("Aborted rendering DICOM, image mapper was disposed");
                    }
                });

        }

        // --- Expose globals so we can play with values in the dev console ---

        //global.dicomGenericRenderWindow = genericRenderWindow;
        //global.dicomRenderWindow = renderWindow;
        //global.dicomRenderer = renderer;
        //global.dicomActor = actor;
        //global.dicomMapper = mapper;
    }, [imageFiles, wallCenterline]);


    // Effect to change the branch centerline when the selected branch changes.
    useEffect(() => {
        selectedBranchRef.current = selectedBranch;

        if (!isNullOrUndefined(selectedBranch)) {

            if (isStraightened) {
                if (context.current?.cprObjects?.centerlinesPolyData) {
                    const {
                        centerlinesPolyData,
                        mapper,
                        liveCenterline,
                        resliceCursorWidgetState,
                        cprManipulator,
                    } = context.current.cprObjects;

                    const { renderWindow } = context.current;
                    const branchName = getDUName(selectedBranch);
                    const centerlinePolyData = centerlinesPolyData[branchName];

                    if (centerlinePolyData) {
                        log(`[Straightened] Updating centerline to branch ${branchName}`);
                        setCenterlinePolyData(
                            mapper,
                            liveCenterline,
                            centerlinePolyData,
                            resliceCursorWidgetState,
                            cprManipulator
                        );

                        // Ensure mapper stays in straightened mode after centerline update.
                        mapper.useStraightenedMode();

                        const currentBranchControlData = branchControlDataRef.current.get(branchName);

                        if (currentBranchControlData) {
                            rotateView(angleInDegrees, mapper, cprManipulator, resliceCursorWidgetState);

                            syncMapperDirectionMatrix(mapper, resliceCursorWidgetState, cprManipulator);
                            {
                                const editedPts = computeEditedCenterlinePositions(centerlinePolyData, currentBranchControlData, mapper.getTangentDirection());
                                liveCenterline.getPoints().setData(editedPts, 3);
                                liveCenterline.modified();
                            }

                            const cprContext = createCPRUpdateContext(context.current);

                            updateDistanceAndDirection(
                                cprContext,
                                onAngleChanged,
                                currentBranchControlData,
                                currentCPRFrameRef,
                                branchName,
                                true
                            );

                            // Refresh the non-interactive centerline overlay for the new branch.
                            if (context.current.cprObjects?.centerlineOverlay) {
                                updateStraightenedCenterlineOverlay(
                                    context.current.cprObjects.centerlineOverlay,
                                    context.current.cprObjects.actor,
                                    mapper,
                                    currentBranchControlData,
                                    currentCPRFrameRef.current
                                );
                            }
                        }

                        // Refresh measurement indicators after branch change (using stretched measurement system).
                        refreshStretchedMeasurementLines(
                            context.current,
                            maybeActiveMeasurementRef.current,
                            selectedBranchRef.current,
                            currentCPRFrameRef.current
                        );
                        refreshStretchedLengthMeasurementArrow(
                            context.current,
                            maybeActiveMeasurementRef.current,
                            selectedBranchRef.current,
                            currentCPRFrameRef.current
                        );
                        updateStretchedMeasurementKonva(
                            context.current,
                            maybeActiveMeasurementRef.current,
                            selectedBranchRef.current,
                            currentCPRFrameRef.current
                        );

                        renderWindow.render();
                    } else {
                        log('[Straightened] No centerline polydata found for branch', branchName);
                    }
                }
            }
            else if (context.current?.cprObjects?.centerlinesPolyData) {
                // Stretched CPR
                const {
                    centerlinesPolyData,
                    mapper,
                    liveCenterline,
                    resliceCursorWidgetState,
                    cprManipulator,
                } = context.current.cprObjects;

                const { renderWindow } = context.current;
                const branchName = getDUName(selectedBranch);
                const centerlinePolyData = centerlinesPolyData[branchName];

                // Implicitly confirm any pending changes when switching branches.
                if (previousSelectedBranchRef.current) {
                    /** @type {Array<EventHandler>} */
                    if (context.current.eventHandlers) {
                        // Find the ConfirmChanges handler and invoke it.
                        const confirmChangesHandler = context.current.eventHandlers.find(
                            handlerObj => handlerObj.eventType === eventNames.ConfirmChanges
                        );

                        if (confirmChangesHandler && confirmChangesHandler.handler) {
                            confirmChangesHandler.handler({ detail: previousSelectedBranchRef.current });
                        }
                    }
                }

                if (centerlinePolyData) {
                    log(`Updating centerline to branch ${branchName}`);
                    setCenterlinePolyData(
                        mapper,
                        liveCenterline,
                        centerlinePolyData,
                        resliceCursorWidgetState,
                        cprManipulator
                    );

                    const currentBranchControlData = branchControlDataRef.current.get(getDUName(selectedBranchRef.current));

                    if (currentBranchControlData) {
                        const previousBranchControlData = (() => {
                            if (previousSelectedBranchRef.current) {
                                log('Getting previous branch control data for', getDUName(previousSelectedBranchRef.current));
                                return branchControlDataRef.current.get(getDUName(previousSelectedBranchRef.current));
                            } else {
                                log('No previous branch selected, using current branch control data for', getDUName(selectedBranchRef.current));
                                return currentBranchControlData;
                            }
                        })();

                        copyTrunkControlData(previousBranchControlData, currentBranchControlData);
                        rotateView(angleInDegrees, mapper, cprManipulator, resliceCursorWidgetState);

                        syncMapperDirectionMatrix(mapper, resliceCursorWidgetState, cprManipulator);
                        {
                            const editedPts = computeEditedCenterlinePositions(centerlinePolyData, currentBranchControlData, mapper.getTangentDirection());
                            liveCenterline.getPoints().setData(editedPts, 3);
                            liveCenterline.modified();
                        }

                        const cprContext = createCPRUpdateContext(context.current);

                        updateDistanceAndDirection(
                            cprContext,
                            onAngleChanged,
                            currentBranchControlData,
                            currentCPRFrameRef,
                            branchName,
                            true
                        );

                        notifyStraightenedView(eventNames, branchName, currentBranchControlData);
                        notifyOrthogonalViews(eventNames, branchName, liveCenterline);
                    }

                    // Refresh stretched measurement indicators after branch change.
                    refreshStretchedMeasurementLines(
                        context.current,
                        maybeActiveMeasurementRef.current,
                        selectedBranchRef.current,
                        currentCPRFrameRef.current
                    );
                    refreshStretchedLengthMeasurementArrow(
                        context.current,
                        maybeActiveMeasurementRef.current,
                        selectedBranchRef.current,
                        currentCPRFrameRef.current
                    );
                    updateStretchedMeasurementKonva(
                        context.current,
                        maybeActiveMeasurementRef.current,
                        selectedBranchRef.current,
                        currentCPRFrameRef.current
                    );

                    renderWindow.render();
                } else {
                    log('No centerline polydata found for branch', branchName);
                }
            }

            previousSelectedBranchRef.current = selectedBranch;
        }
    }, [getDUName(selectedBranch)]);

    // Effect to update the resize observer.
    useEffect(() => {
        if (context.current) {
            if (isVisible) {
                if (context.current.resizeObserver) {
                    context.current.resizeObserver.disconnect();
                }

                const resizeObserver = new ResizeObserver(handleResize);
                resizeObserver.observe(containerRef.current);
                context.current.resizeObserver = resizeObserver;
            } else {
                if (context.current.resizeObserver) {
                    context.current.resizeObserver.disconnect();
                    context.current.resizeObserver = null;
                }
            }
        }
    }, [isVisible])

    // Screenshot side effect
    const [captureScreenshot, ...screenshotInteraction] =
        useScreenshot(
            containerRef.current,
            context.current,
            null,
            screenshotProps
        );

    // Interaction side effect
    useInteraction(
        null,
        interactionModeProps,
        null,
        null,
        screenshotInteraction,
        captureScreenshot,
        null,
        null,
        null,
        null);

    // Side effect to set the mouse wheel scroll mode.
    useEffect(() => {
        if (context.current?.istyle) {
            context.current.istyle.setMouseWheelIsSliceScroll(isSliceScroll);
        }
    }, [isSliceScroll]);

    // Side effect to force rendering when the component becomes visible.
    useEffect(() => {
        if (context.current?.renderWindow && context.current?.genericRenderWindow && isVisible) {
            render();
        }
    }, [isVisible]);

    // Side effect to fit Stretched / Straightened centerline DICOM images to the viewport when it becomes visible.
    useEffect(() => {
        // Returns true when the node is laid out (no ancestor has display:none).
        function isEffectivelyShown(node) {
            if (!node) {
                return false;
            }

            let element = node;

            // Walk up to the root node.
            while (element) {
                const styleDeclaration = getComputedStyle(element);

                if (styleDeclaration.display === "none") {
                    return false;
                }

                element = element.parentElement;
            }

            // If it exists in layout, its box will be measurable.
            const rect = node.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        }

        // The parent container is the one that gets hidden/shown when switching between views.
        const parentContainer = containerRef.current?.parentElement;

        if (!parentContainer) {
            return;
        }

        const update = () => {
            if (!isEffectivelyShown(parentContainer)) {
                return;
            }

            // For both straightened and stretched CPR mapper paths, just re-render.
            if (context.current?.cprObjects?.mapper && context.current?.renderWindow) {
                requestAnimationFrame(() => {
                    context.current.renderWindow.render();
                });
            }
        }

        const observer = new MutationObserver(mutations => {
            requestAnimationFrame(update);
        });

        observer.observe(parentContainer, { attributes: true, attributeFilter: ["style", "class"] });

        return () => {
            observer.disconnect();
        }
    }, []);

    // Side effect to update the oblique angle.
    useEffect(() => {
        if (isStraightened) {
            if (!isNullOrUndefined(angleInDegrees) &&
                context.current?.interactor &&
                context.current?.renderer &&
                context.current?.cprObjects?.mapper) {
                log(`[Straightened] Setting angle to ${angleInDegrees} degrees`);

                const {
                    cprManipulator,
                    mapper,
                    resliceCursorWidgetState,
                    liveCenterline,
                    centerlinesPolyData,
                } = context.current.cprObjects;

                // Restore original centerline before angle-based recomputation.
                const currentBranchName = getDUName(selectedBranchRef.current);
                const originalPolyData = centerlinesPolyData[currentBranchName];
                if (originalPolyData && liveCenterline) {
                    restoreOriginalCenterline(liveCenterline, originalPolyData);
                }

                // Ensure mapper stays in straightened mode.
                mapper.useStraightenedMode();

                rotateView(angleInDegrees, mapper, cprManipulator, resliceCursorWidgetState);

                syncMapperDirectionMatrix(mapper, resliceCursorWidgetState, cprManipulator);

                const angleBranchControlData = branchControlDataRef.current.get(currentBranchName);
                if (angleBranchControlData) {
                    const editedPts = computeEditedCenterlinePositions(originalPolyData, angleBranchControlData, mapper.getTangentDirection());
                    liveCenterline.getPoints().setData(editedPts, 3);
                    liveCenterline.modified();
                }

                const cprContext = createCPRUpdateContext(context.current);

                updateDistanceAndDirection(
                    cprContext,
                    onAngleChangedRef.current || onAngleChanged,
                    branchControlDataRef.current.get(getDUName(selectedBranchRef.current)),
                    currentCPRFrameRef,
                    getDUName(selectedBranchRef.current),
                    true
                );

                // Refresh measurement indicators after angle change (using stretched measurement system).
                refreshStretchedMeasurementLines(
                    context.current,
                    maybeActiveMeasurementRef.current,
                    selectedBranchRef.current,
                    currentCPRFrameRef.current
                );
                refreshStretchedLengthMeasurementArrow(
                    context.current,
                    maybeActiveMeasurementRef.current,
                    selectedBranchRef.current,
                    currentCPRFrameRef.current
                );
                updateStretchedMeasurementKonva(
                    context.current,
                    maybeActiveMeasurementRef.current,
                    selectedBranchRef.current,
                    currentCPRFrameRef.current
                );

                context.current.renderWindow.render();
            }
        } else {
            // Stretched CPR
            if (!isNullOrUndefined(angleInDegrees) &&
                context.current?.interactor &&
                context.current?.renderer &&
                onAngleChangedRef.current) {
                log(`Setting stretched CPR angle to ${angleInDegrees} degrees`);
                log('mapper width and height:', context.current.cprObjects.mapper.getWidth(), context.current.cprObjects.mapper.getHeight());

                const {
                    cprManipulator,
                    mapper,
                    resliceCursorWidgetState
                } = context.current.cprObjects;

                // Restore original centerline before angle-based recomputation.
                const currentBranchName = getDUName(selectedBranchRef.current);
                const originalPolyData = context.current.cprObjects.centerlinesPolyData[currentBranchName];
                restoreOriginalCenterline(context.current.cprObjects.liveCenterline, originalPolyData);

                rotateView(angleInDegrees, mapper, cprManipulator, resliceCursorWidgetState);

                syncMapperDirectionMatrix(mapper, resliceCursorWidgetState, cprManipulator);

                const angleBranchControlData = branchControlDataRef.current.get(currentBranchName);
                if (angleBranchControlData) {
                    const editedPts = computeEditedCenterlinePositions(originalPolyData, angleBranchControlData, mapper.getTangentDirection());
                    context.current.cprObjects.liveCenterline.getPoints().setData(editedPts, 3);
                    context.current.cprObjects.liveCenterline.modified();
                }

                const cprContext = createCPRUpdateContext(context.current);

                updateDistanceAndDirection(
                    cprContext,
                    onAngleChangedRef.current,
                    branchControlDataRef.current.get(getDUName(selectedBranchRef.current)),
                    currentCPRFrameRef,
                    getDUName(selectedBranchRef.current),
                    true
                );

                // Refresh stretched measurement indicators after CPR frame update.
                refreshStretchedMeasurementLines(
                    context.current,
                    maybeActiveMeasurementRef.current,
                    selectedBranchRef.current,
                    currentCPRFrameRef.current
                );
                refreshStretchedLengthMeasurementArrow(
                    context.current,
                    maybeActiveMeasurementRef.current,
                    selectedBranchRef.current,
                    currentCPRFrameRef.current
                );
                updateStretchedMeasurementKonva(
                    context.current,
                    maybeActiveMeasurementRef.current,
                    selectedBranchRef.current,
                    currentCPRFrameRef.current
                );
            }
        }
    }, [angleInDegrees]);

    // Effect to handle changes to the CPR control point mode.
    useEffect(() => {
        if (!isStraightened && containerRef.current && context.current?.cprObjects) {
            const cprCanvas = containerRef.current.querySelector('canvas');

            if (isCenterlineEditable && getDUName(cprControlPointMode) === CprControlPointMode.ADD_OR_DELETE) {
                const mapper = context.current.cprObjects.mapper;
                const orientedCL = mapper.getOrientedCenterline();
                const pts = orientedCL ? orientedCL.getPoints() : null;
                const nPts = pts ? pts.getNumberOfPoints() : 0;

                if (!nPts) {
                    return;
                }

                setCPRCursor('crosshair', cprCanvas);
                const handler = setUpAddOrRemoveControlPointHandler(
                    cprCanvas,
                    currentCPRFrameRef,
                    context.current.cprObjects.pointPicker,
                    context.current.renderer,
                    context.current.cprObjects.mapper,
                    branchControlDataRef,
                    selectedBranchRef,
                    context.current.cprObjects.splineWidget,
                    context.current.interactor,
                    context.current.renderWindow,
                    context.current.cprObjects.planeManipulator,
                    context.current.cprObjects.actor,
                    onCenterlinePointEditRef,
                    context.current.cprObjects.liveCenterline,
                    context.current.cprObjects.centerlinesPolyData,
                    eventNames,
                    context.current.cprObjects.resliceCursorWidget,
                    context.current.cprObjects.resliceCursorWidgetState,
                    context.current.cprObjects.cprManipulator,
                    context.current.cprObjects,
                    onAngleChangedRef,
                );
                log('Adding add/remove control point handler');
                cprCanvas.addEventListener('mousedown', handler);
                addOrRemoveControlPointHandlerRef.current = handler;
            } else {
                if (addOrRemoveControlPointHandlerRef.current) {
                    log('Removing add/remove control point handler');
                    cprCanvas.removeEventListener('mousedown', addOrRemoveControlPointHandlerRef.current);
                    addOrRemoveControlPointHandlerRef.current = null;
                }

                setCPRCursor('', cprCanvas);
            }

        }
    }, [cprControlPointMode, isCenterlineEditable]);

    // Show/hide control-point handles while keeping the centerline visible.
    // Handles are scaled to zero instead of hidden so the spline continues to render.
    useEffect(() => {
        const cprObjects = context.current?.cprObjects;
        if (!cprObjects) {
            return;
        }

        if (isStraightened) {
            cprObjects.centerlineOverlay?.pointsActor?.setVisibility(isCenterlineEditable);
        } else if (cprObjects.splineWidget) {
            cprObjects.splineWidget
                .getWidgetState()
                .getHandleList()
                .forEach((handle, i) =>
                    applyHandleHighlight(handle, cprObjects.handleIndexList[i], isCenterlineEditable));
        }

        context.current.interactor?.render();
        context.current.renderWindow?.render();
    }, [isCenterlineEditable]);

    // Effect to handle viewport screenshot capture events.
    useEffect(() => {
        if (!eventNames || !eventNames.CaptureCprVisualizationScreenshot || !viewportId) {
            return;
        }

        const eventName = `${eventNames.CaptureCprVisualizationScreenshot}_${viewportId}`;

        const handleScreenshotCapture = (ev) => {
            if (!ev.detail || !context.current?.genericRenderWindow) {
                return;
            }

            const { callback } = ev.detail;

            if (typeof callback !== 'function') {
                log('Invalid callback for viewport screenshot capture');
                return;
            }

            try {
                const renderWindow = context.current.genericRenderWindow.getRenderWindow();

                const waitForCanvasReady = () => {
                    const vtkCanvas = context.current.genericRenderWindow
                        .getApiSpecificRenderWindow()
                        .getCanvas();

                    if (!vtkCanvas) {
                        log('VTK canvas not available for screenshot');
                        callback('');
                        return;
                    }

                    const canvasReady =
                        vtkCanvas.width > 0 && vtkCanvas.height > 0;

                    if (!canvasReady) {
                        renderWindow.render();
                        requestAnimationFrame(waitForCanvasReady);
                        return;
                    }

                    // Double RAF to ensure the browser has fully applied layout + paint before capturing canvas
                    requestAnimationFrame(() => {
                        renderWindow.render();

                        requestAnimationFrame(() => {
                            renderWindow.render();

                            const latestVtkCanvas = context.current.genericRenderWindow.getApiSpecificRenderWindow().getCanvas();
                            capture(latestVtkCanvas);
                        });
                    });
                }

                const capture = (vtkCanvas) => {
                    // stretchedKonvaElements holds the active measurement label for
                    // both views; always use it (see compositeKonvaLayer for details).
                    const konvaElements = context.current.stretchedKonvaElements;
                    const konvaStage = konvaElements?.stage;

                    if (konvaStage) {
                        const konvaLayer = konvaElements?.layer;
                        const konvaLabel = konvaElements?.label;

                        // Temporarily scale up the label for better screenshot quality.
                        const screenshotScale = 1.6;
                        let originalScale = null;
                        let originalPosition = null;

                        if (konvaLabel && konvaLayer) {
                            // Store original scale and position.
                            originalScale = konvaLabel.scale();
                            originalPosition = { x: konvaLabel.x(), y: konvaLabel.y() };

                            // Apply enlarged scale.
                            konvaLabel.scale({ x: screenshotScale, y: screenshotScale });

                            // Adjust position to keep label centered vertically and maintain
                            // horizontal distance from the arrow.
                            // When scaling, the label grows from its origin, so we need to offset
                            // by half the size increase to maintain the center point.
                            const labelHeight = konvaLabel.height();
                            const scaleIncrease = screenshotScale - originalScale.x;
                            const yOffset = (labelHeight * scaleIncrease) / 2;

                            konvaLabel.x(originalPosition.x);
                            konvaLabel.y(originalPosition.y - yOffset);

                            konvaLayer.draw();
                        }

                        // Create a temporary canvas to composite both layers.
                        const compositeCanvas = document.createElement('canvas');
                        const dpr = window.devicePixelRatio || 1;
                        compositeCanvas.width = vtkCanvas.width / dpr;
                        compositeCanvas.height = vtkCanvas.height / dpr;
                        const ctx = compositeCanvas.getContext('2d');

                        // Draw VTK canvas first (background).
                        ctx.drawImage(vtkCanvas, 0, 0, compositeCanvas.width, compositeCanvas.height);

                        // Draw Konva canvas on top, scaling it to match VTK canvas DPR dimensions.
                        const konvaCanvas = konvaStage.toCanvas();
                        ctx.drawImage(
                            konvaCanvas,
                            0,
                            0,
                            compositeCanvas.width,
                            compositeCanvas.height,
                        );

                        // Restore original scale and position.
                        if (konvaLabel && konvaLayer && originalScale && originalPosition) {
                            konvaLabel.scale(originalScale);
                            konvaLabel.x(originalPosition.x);
                            konvaLabel.y(originalPosition.y);
                            konvaLayer.draw();
                        }

                        const b64Data = compositeCanvas.toDataURL('image/png').split(',')[1];
                        callback(b64Data);
                    } else {
                        // No Konva canvas, just return VTK canvas.
                        const b64Data = vtkCanvas.toDataURL('image/png').split(',')[1];
                        callback(b64Data);
                    }
                }

                requestAnimationFrame(waitForCanvasReady);
            } catch (error) {
                console.error('Error capturing viewport screenshot:', error);
                callback('');
            }
        };

        window.addEventListener(eventName, handleScreenshotCapture);

        return () => {
            window.removeEventListener(eventName, handleScreenshotCapture);
        };
    }, [eventNames, viewportId]);

    // Effect to display the active measurement on straightened and stretched CPR views.
    useEffect(() => {
        if (!context.current?.cprObjects?.mapper || !currentCPRFrameRef.current?.width) {
            return;
        }

        refreshStretchedMeasurementLines(
            context.current,
            maybeActiveMeasurement,
            selectedBranchRef.current,
            currentCPRFrameRef.current
        );
        refreshStretchedLengthMeasurementArrow(
            context.current,
            maybeActiveMeasurement,
            selectedBranchRef.current,
            currentCPRFrameRef.current
        );
        updateStretchedMeasurementKonva(
            context.current,
            maybeActiveMeasurement,
            selectedBranchRef.current,
            currentCPRFrameRef.current
        );
    }, [isStraightened, maybeActiveMeasurement, getDUName(selectedBranch)]);

    // Install the measurement-line drag handlers (see setupMeasurementLineDrag).
    useEffect(
        () =>
            setupMeasurementLineDrag({
                container: containerRef.current,
                context,
                currentCPRFrameRef,
                selectedBranchRef,
                onMeasurementLineDraggedRef,
            }),
        []
    );

    return <div className="layout-view-view-container" ref={containerRef} />;
};

export default CprVisualization;